import { PipeCreditsBlock } from "@/components/PipeCreditsBlock";
import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SkillsPicker } from "@/components/SkillsUI";
import { InvoiceNumbering } from "@/components/InvoiceNumbering";
import { api } from "@/lib/api";
import { Shield, Users, BarChart3, MessageSquare, Search, Trash2, Plus, Minus, X, ChevronRight, CreditCard, Download, AlertCircle, CheckCircle2, Briefcase, Clock, Lightbulb, ThumbsUp, ThumbsDown, Loader2, Megaphone, Send, Image as ImageIcon, UserCheck, UserX, UserSearch, ExternalLink, Award, FileText, RotateCcw, Eye, ListChecks, Building2, Globe, Calendar as CalendarIcon, Ticket, Trophy } from "lucide-react";
import CertifiedBadge from "@/components/CertifiedBadge";
import LanguageFlags from "@/components/LanguageFlags";
import FunnyTitleBadge, { FUNNY_TITLE_PRESETS, COLOR_OPTIONS } from "@/components/FunnyTitleBadge";
import AnalyticsChart from "@/components/AnalyticsChart";
import MarketingAssets from "@/components/MarketingAssets";
import AddSupporterBlock from "@/components/AddSupporterBlock";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: BarChart3, adminOnly: true },
  { id: "pending",   label: "Pending Vedúcko", icon: ListChecks, adminOnly: true },
  { id: "users",     label: "Užívatelia", icon: Users },
  { id: "certs",     label: "Certifikáty", icon: Award },
  { id: "cert_inv",  label: "Faktúra SauberEko", icon: FileText, adminOnly: true },
  { id: "hof_pros",  label: "Profesionáli HoF", icon: UserCheck, adminOnly: true },
  { id: "hof_recognitions", label: "Uznania HoF", icon: Trophy, adminOnly: true },
  { id: "hof_recruiters", label: "Nábor & HR HoF", icon: UserSearch, adminOnly: true },
  { id: "companies", label: "Firmy", icon: Building2, adminOnly: true },
  { id: "broadcast", label: "Hromadná správa", icon: Megaphone, adminOnly: true },
  { id: "jobs",      label: "Inzercia", icon: Briefcase },
  { id: "payments",  label: "Platby", icon: CreditCard, adminOnly: true },
  { id: "invoices",  label: "Šablóny faktúr", icon: FileText, adminOnly: true },
  { id: "voucher",   label: "Zľavový kupón", icon: Ticket, adminOnly: true },
  { id: "feedback",  label: "Nápady", icon: Lightbulb },
  { id: "chat",      label: "Moderácia chatu", icon: MessageSquare },
  { id: "marketing", label: "Marketing & obrázky", icon: ImageIcon },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState(() => {
    // Initial tab from sessionStorage (set by Layout bell click) or URL ?tab=…
    try {
      const stored = sessionStorage.getItem("admin_tab");
      if (stored) { sessionStorage.removeItem("admin_tab"); return stored; }
    } catch (e) { /* ignore */ }
    const params = new URLSearchParams(window.location.search);
    return params.get("tab") || "dashboard";
  });
  const [unreadFb, setUnreadFb] = useState(0);
  const [pendingCerts, setPendingCerts] = useState(0);
  const [pendingMod, setPendingMod] = useState(0);

  useEffect(() => {
    if (user?.role !== "admin" && user?.role !== "moderator") return;
    let cancelled = false;
    const loadCount = () => {
      api.get("/admin/feedback/unread-count")
        .then((r) => { if (!cancelled) setUnreadFb(r.data?.count || 0); })
        .catch(() => {});
      api.get("/admin/cert/pending-count")
        .then((r) => { if (!cancelled) setPendingCerts(r.data?.count || 0); })
        .catch(() => {});
      if (user?.role === "admin") {
        api.get("/admin/pending-actions/count")
          .then((r) => { if (!cancelled) setPendingMod(r.data?.count || 0); })
          .catch(() => {});
      }
    };
    loadCount();
    const id = setInterval(loadCount, 30000); // refresh every 30s
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.id, user?.role, tab]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin" && user.role !== "moderator") {
    return (
      <div className="max-w-2xl mx-auto py-20 px-6 text-center">
        <Shield className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
        <h1 className="font-display font-black text-3xl">403 — Prístup zamietnutý</h1>
        <p className="text-zinc-500 mt-2 text-sm">Táto sekcia je vyhradená pre administrátorov.</p>
      </div>
    );
  }
  const isModerator = user.role === "moderator";
  const visibleTabs = TABS.filter((tt) => !(tt.adminOnly && isModerator));
  const visibleTabIds = visibleTabs.map((tt) => tt.id);
  // If moderator landed on an admin-only tab via URL/sessionStorage, fall back to first visible.
  const safeTab = visibleTabIds.includes(tab) ? tab : visibleTabIds[0];

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-4 md:px-6 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="wb-tag !border-gold-600 !text-gold-400 inline-flex items-center gap-1.5">
            <Shield className="w-3 h-3" /> ADMIN
          </span>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-3">Riadiace centrum</h1>
          <p className="text-zinc-500 text-sm mt-1 font-mono uppercase tracking-wider">Tigik · SauberEko®</p>
        </div>
      </div>

      <div className="border-b border-zinc-800 mb-6 flex gap-1 overflow-x-auto">
        {visibleTabs.map((tt) => (
          <button key={tt.id} data-testid={`admin-tab-${tt.id}`} onClick={() => setTab(tt.id)}
            className={`px-4 py-3 text-sm font-mono uppercase tracking-wider border-b-2 inline-flex items-center gap-2 whitespace-nowrap transition-colors relative ${safeTab === tt.id ? "border-gold-500 text-gold-400" : "border-transparent text-zinc-500 hover:text-white"}`}>
            <tt.icon className="w-4 h-4" /> {tt.label}
            {tt.id === "feedback" && unreadFb > 0 && (
              <span
                data-testid="feedback-unread-badge"
                className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-600 text-white rounded-full animate-pulse"
                title={`${unreadFb} nových nápadov čaká na vybavenie`}
              >
                {unreadFb > 99 ? "99+" : unreadFb}
              </span>
            )}
            {tt.id === "certs" && pendingCerts > 0 && (
              <span
                data-testid="certs-pending-badge"
                className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-gold-500 text-black rounded-full animate-pulse"
                title={`${pendingCerts} žiadostí o overenie certifikátu čaká`}
              >
                {pendingCerts > 99 ? "99+" : pendingCerts}
              </span>
            )}
            {tt.id === "pending" && pendingMod > 0 && (
              <span
                data-testid="pending-mod-badge"
                className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-600 text-white rounded-full animate-pulse"
                title={`${pendingMod} akcií od Vedúčka čaká na schválenie`}
              >
                {pendingMod > 99 ? "99+" : pendingMod}
              </span>
            )}
          </button>
        ))}
      </div>

      {safeTab === "dashboard" && <Dashboard />}
      {safeTab === "pending"   && <PendingActionsTab />}
      {safeTab === "users"     && <UsersTab />}
      {safeTab === "certs"     && <CertRequestsTab onCountChange={setPendingCerts} />}
      {safeTab === "cert_inv"  && <CertInvoiceTab />}
      {safeTab === "hof_pros"  && <ProfessionalsTab />}
      {safeTab === "hof_recognitions" && <RecognitionsTab />}
      {safeTab === "hof_recruiters" && <RecruitersTab />}
      {safeTab === "companies" && <CompaniesTab />}
      {safeTab === "broadcast" && <BroadcastTab />}
      {safeTab === "jobs"      && <JobsAdminTab />}
      {safeTab === "payments"  && <PaymentsTab />}
      {safeTab === "invoices"  && <InvoiceTemplatesTab />}
      {safeTab === "voucher"   && <DiscountVoucherTab />}
      {safeTab === "feedback"  && <FeedbackTab />}
      {safeTab === "chat"      && <ChatModeration />}
      {safeTab === "marketing" && <MarketingAssets />}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="border-l-2 border-gold-500 bg-[var(--wb-surface)] p-5">
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="font-display font-black text-4xl mt-1">{value}</div>
      {sub && <div className="text-xs text-zinc-600 mt-1">{sub}</div>}
    </div>
  );
}

function CertRequestsTab({ onCountChange }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/cert/list", {
        params: filter === "all" ? {} : { filter_status: filter },
      });
      setRequests(r.data || []);
      api.get("/admin/cert/pending-count")
        .then((rr) => onCountChange && onCountChange(rr.data?.count || 0))
        .catch(() => {});
    } catch {
      setRequests([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (req) => {
    if (!window.confirm(`SCHVÁLIŤ certifikát ${req.certificate_no} pre ${req.first_name} ${req.last_name}?\n\nPo schválení získa PRO predplatné NATRVALO (doživotne) ZDARMA + zlatý odznak.`)) return;
    setBusy(req.id);
    try {
      const r = await api.post(`/admin/cert/${req.id}/approve`);
      if (r.data?.queued) {
        window.alert("⏳ Tvoja žiadosť bola odoslaná Adminovi na schválenie. Zmena nastane až po jeho súhlase.");
      }
      await load();
    } catch (e) { alert(e.response?.data?.detail || "Chyba pri schválení"); }
    finally { setBusy(null); }
  };

  const DEFAULT_REJECT_REASON =
    "Žiadna alebo neúplná zhoda v databáze pod značkou SauberEko®. Zadané údaje (číslo certifikátu, meno a priezvisko) sa nepodarilo overiť v oficiálnej evidencii SauberEko®. Ak ide o pravý certifikát, skontroluj presné napísanie mena a formát čísla (vrátane lomítka a roka) a žiadosť pošli znova. V prípade otázok píš na info@saubereko.eu.";

  const reject = async (req) => {
    const reason = window.prompt(
      `Zamietnuť žiadosť pre:\n${req.first_name} ${req.last_name}\nCertifikát: ${req.certificate_no}\n\nDôvod (zobrazí sa používateľovi a pošle emailom):`,
      DEFAULT_REJECT_REASON
    );
    if (reason === null) return;
    setBusy(req.id);
    try {
      await api.post(`/admin/cert/${req.id}/reject`, null, { params: { reason: (reason || DEFAULT_REJECT_REASON).trim() } });
      await load();
    } catch (e) { alert(e.response?.data?.detail || "Chyba pri zamietnutí"); }
    finally { setBusy(null); }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("sk-SK", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  const statusBadge = (s) => {
    if (s === "pending")  return <span className="wb-tag !text-amber-400 !border-amber-700">ČAKÁ</span>;
    if (s === "approved") return <span className="wb-tag !text-emerald-400 !border-emerald-700">SCHVÁLENÉ</span>;
    if (s === "rejected") return <span className="wb-tag !text-red-400 !border-red-700">ZAMIETNUTÉ</span>;
    return <span className="wb-tag">{s}</span>;
  };

  return (
    <div data-testid="certs-tab" className="space-y-4">
      <div className="border border-gold-700/40 bg-gold-500/5 p-4 flex items-start gap-3">
        <Award className="w-5 h-5 text-gold-400 mt-0.5 flex-shrink-0" strokeWidth={1.8} />
        <div className="text-xs text-zinc-300 leading-relaxed">
          <div className="font-display font-bold text-sm text-white mb-1">Overenie certifikátu SauberEko®</div>
          <ol className="list-decimal pl-4 space-y-0.5">
            <li>Porovnaj číslo certifikátu, meno a priezvisko z žiadosti s tvojou evidenciou vydaných certifikátov.</li>
            <li>Ak sedí → klikni <strong>Schváliť</strong> → používateľ automaticky dostane <strong className="text-gold-300">PRO predplatné NATRVALO (doživotne)</strong> + zlatý odznak ⭐ a príde mu welcome email.</li>
            <li>Ak nesedí → klikni <strong>Zamietnuť</strong> a zadaj dôvod (užívateľ ho uvidí a môže opraviť údaje).</li>
          </ol>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((f) => (
          <button
            key={f}
            data-testid={`certs-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors ${filter === f ? "border-gold-500 text-gold-400 bg-gold-500/5" : "border-zinc-800 text-zinc-500 hover:text-white"}`}
          >
            {f === "pending" ? "Čakajú" : f === "approved" ? "Schválené" : f === "rejected" ? "Zamietnuté" : "Všetky"}
          </button>
        ))}
        <button onClick={load} className="ml-auto wb-btn-ghost px-3 py-1.5 text-xs" data-testid="certs-refresh">Obnoviť</button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-zinc-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Načítavam…</div>
      ) : requests.length === 0 ? (
        <div className="border border-zinc-800 bg-[var(--wb-surface)] p-10 text-center">
          <Award className="w-10 h-10 text-zinc-700 mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-display font-bold text-lg">Žiadne žiadosti</div>
          <p className="text-xs text-zinc-500 mt-1">V tejto kategórii zatiaľ nie sú žiadne žiadosti o overenie certifikátu.</p>
        </div>
      ) : (
        <div className="border border-zinc-800 bg-[var(--wb-surface)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs font-mono uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <tr>
                <th className="text-left p-3">Meno z cert.</th>
                <th className="text-left p-3">Číslo certifikátu</th>
                <th className="text-left p-3">Tigik účet</th>
                <th className="text-left p-3">Žiadosť</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Akcia</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id} data-testid={`cert-row-${r.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/30">
                  <td className="p-3">
                    <div className="text-white font-medium">{r.first_name} {r.last_name}</div>
                    {r.note && <div className="text-[11px] text-zinc-500 italic mt-0.5 max-w-xs truncate" title={r.note}>„{r.note}"</div>}
                  </td>
                  <td className="p-3">
                    <span className="font-mono text-gold-400 text-xs">{r.certificate_no}</span>
                  </td>
                  <td className="p-3">
                    <a href={`/u/${r.user_id}`} target="_blank" rel="noreferrer" className="text-zinc-300 hover:text-gold-400 inline-flex items-center gap-1">
                      {r.user_account_name || "—"} <ExternalLink className="w-3 h-3 opacity-50" />
                    </a>
                    <div className="text-[11px] text-zinc-500 font-mono">{r.user_email}</div>
                  </td>
                  <td className="p-3 text-zinc-500 text-xs font-mono">{formatDate(r.created_at)}</td>
                  <td className="p-3">{statusBadge(r.status)}</td>
                  <td className="p-3 text-right">
                    {r.status === "pending" ? (
                      <div className="inline-flex gap-2">
                        <button
                          data-testid={`cert-approve-${r.id}`}
                          disabled={busy === r.id}
                          onClick={() => approve(r)}
                          className="wb-btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-50"
                        >
                          {busy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Schváliť
                        </button>
                        <button
                          data-testid={`cert-reject-${r.id}`}
                          disabled={busy === r.id}
                          onClick={() => reject(r)}
                          className="wb-btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-50"
                        >
                          <X className="w-3 h-3" /> Zamietnuť
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-zinc-600">{r.status === "rejected" && r.rejection_reason ? <span title={r.rejection_reason}>{r.rejection_reason.slice(0,30)}…</span> : "—"}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function BroadcastTab() {
  const [text, setText] = useState("");
  const [target, setTarget] = useState("all");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [sourceLang, setSourceLang] = useState("sk");
  const [autoTr, setAutoTr] = useState(true);
  const [videoUrl, setVideoUrl] = useState("");

  const validVideo = !videoUrl.trim() || /^https?:\/\/(www\.|m\.)?(youtube\.com|youtu\.be|vimeo\.com)\//i.test(videoUrl.trim());

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !videoUrl.trim()) return;
    if (videoUrl.trim() && !validVideo) {
      alert("Video URL musí byť YouTube alebo Vimeo odkaz.");
      return;
    }
    const tgtLabel = { all: "VŠETKÝM užívateľom", subscribers: "iba PREDPLATITEĽOM", active: "AKTÍVNYM (posledných 30 dní)" }[target];
    if (!window.confirm(`Naozaj odoslať túto správu ${tgtLabel}?\n\n"${text.slice(0, 120)}${text.length > 120 ? "..." : ""}"${videoUrl ? `\n\n🎬 + video: ${videoUrl}` : ""}`)) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/admin/broadcast", {
        text: text.trim(),
        target,
        source_lang: sourceLang,
        auto_translate: autoTr,
        video_url: videoUrl.trim() || null,
      });
      const langs = r.data.languages?.length ? ` · preložené do ${r.data.languages.length} jazykov` : "";
      setResult({ kind: "ok", text: `Odoslané ${r.data.sent} správ (cieľ: ${r.data.target})${langs}.` });
      setText("");
      setVideoUrl("");
    } catch (e) {
      setResult({ kind: "err", text: e.response?.data?.detail || "Chyba pri odoslaní" });
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="broadcast-tab" className="max-w-3xl">
      <h2 className="font-display font-black text-2xl mb-2">Hromadná správa</h2>
      <p className="text-sm text-zinc-400 mb-6">
        Pošli DM správu naraz všetkým (alebo skupine) užívateľov. Zobrazí sa im v ich schránke ako od teba (admin).
      </p>

      <form onSubmit={send} className="border border-zinc-800 bg-[var(--wb-surface)] p-6">
        {/* Target */}
        <div className="mb-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Komu poslať</div>
          <div className="grid sm:grid-cols-3 gap-2">
            {[
              { id: "all",         label: "Všetkým",       desc: "Každý registrovaný" },
              { id: "active",      label: "Aktívnym",      desc: "Boli online 30 dní" },
              { id: "subscribers", label: "Predplatiteľom", desc: "ACTIVE / PRO / START" },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setTarget(opt.id)}
                data-testid={`broadcast-target-${opt.id}`}
                className={`text-left p-3 border ${target === opt.id ? "border-gold-600 bg-gold-950/40" : "border-zinc-700 hover:border-zinc-500"}`}
              >
                <div className={`text-sm font-display font-bold ${target === opt.id ? "text-gold-300" : "text-zinc-200"}`}>{opt.label}</div>
                <div className="text-[10px] text-zinc-500 mt-1">{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Translation controls */}
        <div className="mb-5 grid sm:grid-cols-2 gap-3 p-3 bg-gold-950/20 border border-gold-700/30">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 block mb-1">Jazyk, v ktorom píšeš</label>
            <select
              value={sourceLang}
              onChange={(e) => setSourceLang(e.target.value)}
              data-testid="broadcast-source-lang"
              className="w-full bg-black border border-zinc-700 px-2 py-1.5 text-sm text-white focus:border-gold-600 outline-none"
            >
              <option value="sk">🇸🇰 Slovenčina</option>
              <option value="en">🇬🇧 English</option>
              <option value="cs">🇨🇿 Čeština</option>
              <option value="de">🇩🇪 Deutsch</option>
              <option value="pl">🇵🇱 Polski</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={autoTr}
                onChange={(e) => setAutoTr(e.target.checked)}
                data-testid="broadcast-auto-translate"
                className="w-4 h-4"
              />
              <span><strong className="text-gold-300">Automatický preklad</strong> do všetkých 5 jazykov (Claude Sonnet) — každý user uvidí správu v <em>svojom</em> jazyku</span>
            </label>
          </div>
        </div>

        {/* Message */}
        <div className="mb-4">
          <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-2">Správa (max 2000 znakov)</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 2000))}
            data-testid="broadcast-textarea"
            rows={6}
            placeholder="Napríklad: Ahoj kolegovia! V tomto týždni máme novú orbitálnu kalkulačku pre Orbitec. Skúste a dajte vedieť!"
            className="w-full bg-black border border-zinc-700 px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-gold-600 outline-none resize-y font-mono"
          />
          <div className="text-[10px] text-zinc-600 mt-1">{text.length}/2000</div>
        </div>

        {/* Optional video embed */}
        <div className="mb-4">
          <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-2">
            🎬 Video (voliteľné — YouTube alebo Vimeo URL)
          </label>
          <input
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            data-testid="broadcast-video-url"
            placeholder="napr. https://youtu.be/dQw4w9WgXcQ alebo https://vimeo.com/123456789"
            className={`w-full bg-black border px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none font-mono ${
              videoUrl && !validVideo ? "border-red-600" : "border-zinc-700 focus:border-gold-600"
            }`}
          />
          {videoUrl && !validVideo && (
            <div className="text-[10px] text-red-400 mt-1">⚠️ URL musí byť z YouTube alebo Vimeo.</div>
          )}
          <div className="text-[10px] text-zinc-600 mt-1">Video sa automaticky embedne ako prehrávač pri správe. Najlepšie video najprv nahraj na YouTube (môže byť "Nelistované") a sem vlož link.</div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <button
            type="submit"
            disabled={busy || (!text.trim() && !videoUrl.trim()) || (videoUrl && !validVideo)}
            data-testid="broadcast-send-btn"
            className="wb-btn-primary px-6 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {autoTr ? "Preložiť a odoslať" : "Odoslať hromadne"}
          </button>
          {result && (
            <div className={`text-sm inline-flex items-center gap-1.5 ${result.kind === "ok" ? "text-green-400" : "text-red-400"}`}>
              {result.kind === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              {result.text}
            </div>
          )}
        </div>
      </form>

      <PushBroadcastPanel />
    </div>
  );
}

function PushBroadcastPanel() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("/");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [backupBusy, setBackupBusy] = useState(false);

  const downloadBackup = async () => {
    if (!window.confirm("Stiahnuť kompletnú zálohu DB ako ZIP súbor?")) return;
    setBackupBusy(true);
    try {
      const r = await api.get("/admin/db-backup", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/zip" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      link.download = `tigik-db-backup-${ts}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setResult({ kind: "ok", text: `💾 Záloha DB stiahnutá. Ulož ZIP na 2 miesta (disk + cloud).` });
    } catch (e) {
      setResult({ kind: "err", text: e.response?.data?.detail || "Chyba pri sťahovaní zálohy" });
    } finally { setBackupBusy(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    if (!window.confirm(`Naozaj poslať PUSH NOTIFIKÁCIU všetkým subscribed užívateľom?\n\n📲 "${title}"\n${body.slice(0, 120)}${body.length > 120 ? "..." : ""}\n\nTáto akcia vyšle skutočnú systémovú notifikáciu na všetky zariadenia.`)) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/push/broadcast", { title: title.trim(), body: body.trim(), url: url.trim() || "/" });
      setResult({ kind: "ok", text: `✅ Push doručený na ${r.data.delivered}/${r.data.total} zariadení.` });
      setTitle(""); setBody("");
    } catch (e) {
      setResult({ kind: "err", text: e.response?.data?.detail || "Chyba pri odoslaní push" });
    } finally { setBusy(false); }
  };

  const cleanup = async () => {
    if (!window.confirm("Vymazať testovacie/debug push subscriptions (curl-debug, TEST_DEBUG_*)?")) return;
    setCleaning(true);
    try {
      const r = await api.post("/push/cleanup-test-subs");
      setResult({ kind: "ok", text: `🧹 Vymazaných ${r.data.deleted} test subscriptions.` });
    } catch (e) {
      setResult({ kind: "err", text: e.response?.data?.detail || "Chyba pri vyčisťovaní" });
    } finally { setCleaning(false); }
  };

  return (
    <div className="mt-8 border-2 border-gold-700/50 bg-gradient-to-br from-gold-950/30 to-zinc-950 p-6" data-testid="push-broadcast-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <h3 className="font-display font-black text-xl flex items-center gap-2 text-gold-300">
          🔔 Push notifikácia (systémová)
        </h3>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={downloadBackup}
            disabled={backupBusy}
            data-testid="db-backup-btn"
            className="text-[10px] font-mono uppercase tracking-widest text-gold-400 hover:text-gold-300 underline disabled:opacity-50"
          >
            {backupBusy ? "Stahujem…" : "💾 Záloha DB"}
          </button>
          <button
            type="button"
            onClick={cleanup}
            disabled={cleaning}
            data-testid="push-cleanup-btn"
            className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-300 underline disabled:opacity-50"
          >
            {cleaning ? "Mažem…" : "Vyčistiť test subs"}
          </button>
        </div>
      </div>
      <p className="text-xs text-zinc-400 mb-5">
        Pošli systémovú push notifikáciu (zvuk + vibrácia + heads-up banner) všetkým zariadeniam, ktoré majú zapnuté Tigik notifikácie. Funguje aj keď je appka zatvorená.
      </p>

      <form onSubmit={send} className="space-y-3">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Titulok (max 80 znakov)</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={80}
            placeholder="🎉 Tigik 4.4 je vonku!"
            data-testid="push-broadcast-title"
            className="w-full bg-black border border-zinc-700 px-3 py-2 text-sm text-white focus:border-gold-600 outline-none"
          />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Text (max 300 znakov)</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={300}
            rows={3}
            placeholder="Push notifikácie konečne fungujú! Skús si poslať DM kamarátovi a zacvendí ti telefón. ⚒️"
            data-testid="push-broadcast-body"
            className="w-full bg-black border border-zinc-700 px-3 py-2 text-sm text-white focus:border-gold-600 outline-none resize-none"
          />
          <div className="text-[10px] text-zinc-600 text-right mt-1">{body.length}/300</div>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Cieľ kliknutia (URL v appke)</label>
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="/community"
            data-testid="push-broadcast-url"
            className="w-full bg-black border border-zinc-700 px-3 py-2 text-sm text-white focus:border-gold-600 outline-none font-mono"
          />
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
          <button
            type="submit"
            disabled={busy || !title.trim() || !body.trim()}
            data-testid="push-broadcast-send-btn"
            className="wb-btn-primary px-6 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Odoslať push všetkým
          </button>
          {result && (
            <div className={`text-sm inline-flex items-center gap-1.5 ${result.kind === "ok" ? "text-green-400" : "text-red-400"}`}>
              {result.kind === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              {result.text}
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {});
  }, []);
  if (!s) return <div className="text-zinc-500 font-mono text-sm">Načítavam štatistiky…</div>;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Užívateľov" value={s.total_users} />
        <Stat label="Predplatiteľov" value={s.active_subscribers} sub="Aktívne" />
        <Stat label="Noví (7 dní)" value={s.new_users_7d} sub="Týždenný rast" />
        <Stat label="Príjem (€)" value={s.total_revenue_eur} sub="Spolu" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label="Správ v chate" value={s.total_chat_messages} />
        <Stat label="Výpočtov uložených" value={s.total_calculations} />
        <Stat label="AI správ" value={s.total_ai_messages} sub="Claude Sonnet 4.5" />
      </div>

      {/* Graf návštevnosti — denné stats */}
      <AnalyticsChart />

      <div className="border border-zinc-800 bg-[var(--wb-surface)] p-5">
        <h3 className="font-display font-bold text-lg mb-3">Fakturačné údaje</h3>
        <div className="grid sm:grid-cols-2 gap-2 text-sm font-mono">
          <div className="sm:col-span-2"><span className="text-zinc-500">Obchodné meno:</span> <span className="text-white">SauberEko s. r. o.</span></div>
          <div className="sm:col-span-2"><span className="text-zinc-500">Sídlo:</span> <span className="text-white">Ivana Krasku 25/9, 972 71 Nováky</span></div>
          <div className="sm:col-span-2"><span className="text-zinc-500">Štatutárny orgán:</span> <span className="text-white">Martin Smrkta, konateľ</span></div>
          <div><span className="text-zinc-500">IČO:</span> <span className="text-white">57356050</span></div>
          <div><span className="text-zinc-500">DIČ:</span> <span className="text-white">2122719995</span></div>
          <div><span className="text-zinc-500">IČ DPH:</span> <span className="text-white">SK2122719995</span></div>
          <div><span className="text-zinc-500">Režim:</span> <span className="text-white">Platca DPH, sadzba 23 %</span></div>
        </div>
      </div>
    </div>
  );
}

function UsersTab() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users", { params: q ? { q } : {} });
      setUsers(r.data);
      setSelected(new Set());
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const allSelected = users.length > 0 && selected.size === users.length;
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(users.map((u) => u.id)));
  };
  const toggleOne = (id) => {
    const ns = new Set(selected);
    if (ns.has(id)) ns.delete(id); else ns.add(id);
    setSelected(ns);
  };
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Naozaj zmazať ${selected.size} užívateľov? Akcia je nezvratná.`)) return;
    await api.post("/admin/users/bulk-delete", { user_ids: Array.from(selected) });
    load();
  };

  const [showBcast, setShowBcast] = useState(false);
  const [bcastText, setBcastText] = useState("");
  const [bcastBusy, setBcastBusy] = useState(false);
  const bulkBroadcast = async () => {
    if (selected.size === 0) return;
    const text = bcastText.trim();
    if (!text) return;
    if (!confirm(`Odoslať túto správu ${selected.size} vybratým užívateľom?\n\n"${text.slice(0, 120)}${text.length > 120 ? "..." : ""}"`)) return;
    setBcastBusy(true);
    try {
      const r = await api.post("/admin/broadcast", { text, target: "selected", user_ids: Array.from(selected) });
      alert(`Odoslané ${r.data.sent} správ.`);
      setBcastText("");
      setShowBcast(false);
      setSelected(new Set());
    } catch (e) {
      alert(e.response?.data?.detail || "Chyba pri odoslaní");
    } finally { setBcastBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-users">
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Hľadať podľa mena alebo emailu…"
            className="wb-input w-full pl-9 pr-3 py-2 text-sm" />
        </div>
        <button type="submit" className="wb-btn-ghost px-4 py-2 text-sm">Hľadať</button>
        {selected.size > 0 && (
          <button type="button" onClick={() => setShowBcast(true)} data-testid="admin-bulk-broadcast"
            className="wb-btn-primary px-4 py-2 text-sm inline-flex items-center gap-1.5">
            <Megaphone className="w-3.5 h-3.5" /> Poslať správu vybratým ({selected.size})
          </button>
        )}
        {selected.size > 0 && (
          <button type="button" onClick={bulkDelete} data-testid="admin-bulk-delete"
            className="wb-btn-ghost px-4 py-2 text-sm !text-red-400 !border-red-700">
            <Trash2 className="w-3.5 h-3.5 inline mr-1" /> Zmazať vybratých ({selected.size})
          </button>
        )}
      </form>

      {/* Quick broadcast inline panel */}
      {showBcast && (
        <div className="border border-gold-700 bg-gold-950/10 p-4 mb-3" data-testid="admin-bulk-broadcast-panel">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="font-display font-bold text-sm inline-flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-gold-500" />
              Hromadná správa pre <span className="text-gold-400">{selected.size}</span> vybratých
            </div>
            <button type="button" onClick={() => { setShowBcast(false); setBcastText(""); }} className="text-zinc-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          <textarea
            value={bcastText}
            onChange={(e) => setBcastText(e.target.value.slice(0, 2000))}
            data-testid="admin-bulk-broadcast-textarea"
            rows={4}
            placeholder="Tvoj odkaz pre vybratých... (Príde im to ako DM od teba)"
            className="w-full bg-black border border-zinc-700 px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-gold-600 outline-none resize-y font-mono"
          />
          <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
            <span className="text-[10px] font-mono text-zinc-600">{bcastText.length}/2000</span>
            <button
              type="button"
              onClick={bulkBroadcast}
              disabled={bcastBusy || !bcastText.trim()}
              data-testid="admin-bulk-broadcast-send"
              className="wb-btn-primary px-5 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
            >
              {bcastBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Odoslať ({selected.size})
            </button>
          </div>
        </div>
      )}

      <div className="border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60">
            <tr className="text-left text-xs font-mono uppercase tracking-wider text-zinc-500">
              <th className="px-3 py-2 w-8">
                <input type="checkbox" checked={allSelected} onChange={toggleAll}
                  data-testid="admin-select-all" className="accent-gold-500" />
              </th>
              <th className="px-3 py-2">Meno</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Rola</th>
              <th className="px-3 py-2">Online</th>
              <th className="px-3 py-2">Čas spolu</th>
              <th className="px-3 py-2">Plán do</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-3 py-6 text-center text-zinc-500">Načítavam…</td></tr>}
            {!loading && users.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-zinc-500">Žiadni užívatelia</td></tr>}
            {users.map((u) => (
              <tr key={u.id} onClick={() => setEditing(u)} className={`border-t border-zinc-800 hover:bg-zinc-900/60 cursor-pointer transition-colors ${selected.has(u.id) ? "bg-gold-500/5" : ""}`} data-testid={`admin-user-${u.id}`}>
                <td className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleOne(u.id)}
                    data-testid={`admin-user-${u.id}-checkbox`} className="accent-gold-500" />
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <div className="relative w-7 h-7 bg-zinc-900 border border-zinc-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-[10px] font-display font-bold text-gold-500 uppercase">{(u.name || u.email || "?")[0]}</span>
                      )}
                      {u.is_online && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-black" />}
                    </div>
                    <span className="inline-flex items-center gap-1.5">
                      {u.name}
                      {u.plan_tier === "gold" && (
                        <span
                          data-testid={`admin-user-${u.id}-gold-crown`}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 border border-amber-500/80 bg-amber-950/40 text-amber-300 text-[10px] font-bold uppercase tracking-wider rounded-sm shadow-[0_0_10px_-3px_rgba(212,175,55,0.9)]"
                          title="GOLD predplatné · exkluzívny status"
                        >
                          👑 GOLD
                        </span>
                      )}
                      {u.preferred_languages?.length > 0 && (
                        <LanguageFlags languages={u.preferred_languages} size="sm" />
                      )}
                      {u.certified && <CertifiedBadge size="sm" certificateNo={u.certificate_no} />}
                      {u.banned && <span className="wb-tag !text-red-400 !border-red-700">BAN</span>}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-3 font-mono text-xs text-zinc-400">{u.email}</td>
                <td className="px-3 py-3">
                  <RoleBadge role={u.role} />
                </td>
                <td className="px-3 py-3 text-xs font-mono">
                  {u.is_online
                    ? <span className="text-green-400">● live</span>
                    : u.last_seen
                      ? <span className="text-zinc-500" title={new Date(u.last_seen).toLocaleString()}>{new Date(u.last_seen).toLocaleDateString()}</span>
                      : <span className="text-zinc-700">—</span>}
                  <div className="text-[10px] text-zinc-600">{Math.round(u.minutes_today || 0)} min dnes</div>
                </td>
                <td className="px-3 py-3 text-xs font-mono text-zinc-400">
                  {(() => {
                    const m = Math.round(u.total_minutes || 0);
                    if (m < 60) return `${m} min`;
                    const h = Math.floor(m / 60);
                    const mm = m % 60;
                    return h >= 24 ? `${Math.floor(h/24)}d ${h%24}h` : `${h}h ${mm}m`;
                  })()}
                </td>
                <td className="px-3 py-3 text-xs font-mono text-zinc-500">
                  {(() => {
                    if (!u.subscription_until) return <span className="text-zinc-700">—</span>;
                    const until = new Date(u.subscription_until);
                    const now = new Date();
                    const diffDays = Math.floor((until - now) / (1000 * 60 * 60 * 24));
                    const isExpired = until < now;
                    const isExpiringSoon = !isExpired && diffDays <= 7;
                    const hadPaidPlan = u.plan_tier && !["free", "admin"].includes(u.plan_tier);
                    // Admin role & role-based lifetime access shouldn't be flagged as expired.
                    const isAdminOrPerma = u.role === "admin" || (until.getFullYear() >= 2099);
                    return (
                      <div className="flex flex-col gap-1">
                        <span className={isExpired && hadPaidPlan && !isAdminOrPerma ? "text-red-400" : ""}>
                          {until.toLocaleDateString("sk-SK")}
                        </span>
                        {isExpired && hadPaidPlan && !isAdminOrPerma && (
                          <span
                            data-testid={`admin-user-${u.id}-expired-badge`}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-red-700 bg-red-950/40 text-red-300 text-[9px] font-bold uppercase tracking-wider w-fit"
                            title={`Predplatné (${u.plan_tier?.toUpperCase()}) vypršalo pred ${Math.abs(diffDays)} dňami — používateľ má prístup ako FREE.`}
                          >
                            ⚠ VYPRŠALO · {Math.abs(diffDays)}d
                          </span>
                        )}
                        {isExpiringSoon && hadPaidPlan && !isAdminOrPerma && (
                          <span
                            data-testid={`admin-user-${u.id}-expiring-badge`}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-amber-700 bg-amber-950/30 text-amber-300 text-[9px] font-bold uppercase tracking-wider w-fit"
                            title={`Predplatné (${u.plan_tier?.toUpperCase()}) vyprší o ${diffDays} dní.`}
                          >
                            KONČÍ · {diffDays}d
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <button onClick={(e) => { e.stopPropagation(); setEditing(u); }} data-testid={`admin-user-${u.id}-edit`}
                    className="wb-btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1">
                    Upraviť <ChevronRight className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <EditUserModal user={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [days, setDays] = useState(30);
  const [role, setRole] = useState(user.role);
  const [planTier, setPlanTier] = useState(user.plan_tier || (user.is_subscribed ? "pro" : "free"));
  const [banned, setBanned] = useState(!!user.banned);
  const [certified, setCertified] = useState(!!user.certified);
  const [certNo, setCertNo] = useState(user.certificate_no || "");
  const [certHolderName, setCertHolderName] = useState(user.cert_holder_name || "");
  const [certRole, setCertRole] = useState(user.cert_role || "professional");
  const [customTitle, setCustomTitle] = useState(user.custom_title || "");
  const [customTitleColor, setCustomTitleColor] = useState(user.custom_title_color || "gold");
  const [busy, setBusy] = useState(false);

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const payload = {
        role,
        banned,
        plan_tier: planTier,
        certified,
        custom_title: customTitle.trim() || "",
        custom_title_color: customTitleColor,
        ...(certified && certNo ? { certificate_no: certNo } : {}),
        ...(certified ? { cert_holder_name: certHolderName.trim() } : {}),
        ...(certified ? { cert_role: certRole } : {}),
        ...extra,
      };
      await api.patch(`/admin/users/${user.id}`, payload);
      onSaved(); onClose();
    } finally { setBusy(false); }
  };

  // Extend or subtract days from subscription — doesn't touch plan_tier/role/banned (so admin can adjust
  // without accidentally downgrading a Pro user back to Free).
  // direction: +1 = add days, -1 = subtract days
  const adjustDays = async (direction = 1) => {
    const n = Math.abs(parseInt(days, 10) || 0);
    if (n < 1) return;
    setBusy(true);
    try {
      // Default to "active" tier if currently free, otherwise keep current
      const targetTier = (planTier === "free") ? "active" : planTier;
      if (direction > 0) {
        // Add days — use backend's safe extend_days flow (anchors to now if expired)
        await api.patch(`/admin/users/${user.id}`, { extend_days: n, plan_tier: targetTier });
      } else {
        // Subtract days — compute new ISO date locally and use set_subscription_until
        // (backend's extend_days uses max(cur, now), so negative would only shrink from "now",
        // not from current subscription_until — we want to shrink from actual expiry).
        const cur = user.subscription_until ? new Date(user.subscription_until) : new Date();
        const base = cur > new Date() ? cur : new Date();
        const newDate = new Date(base.getTime() - n * 86400000);
        await api.patch(`/admin/users/${user.id}`, {
          set_subscription_until: newDate.toISOString(),
          plan_tier: targetTier,
        });
      }
      setPlanTier(targetTier);
      onSaved(); onClose();
    } finally { setBusy(false); }
  };

  // Calendar picker — set subscription to expire exactly on the chosen date (23:59 UTC)
  const setExactDate = async (isoDate) => {
    if (!isoDate) return;
    setBusy(true);
    try {
      const targetTier = (planTier === "free") ? "active" : planTier;
      // Append end-of-day so it counts as a full day
      const fullIso = `${isoDate}T23:59:59+00:00`;
      await api.patch(`/admin/users/${user.id}`, {
        set_subscription_until: fullIso,
        plan_tier: targetTier,
      });
      setPlanTier(targetTier);
      onSaved(); onClose();
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Naozaj zmazať ${user.email}?`)) return;
    setBusy(true);
    try { await api.delete(`/admin/users/${user.id}`); onSaved(); onClose(); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="admin-user-edit-modal">
      <div className="bg-[var(--wb-surface)] border border-gold-700/40 max-w-lg w-full p-6 relative my-auto max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        <h3 className="font-display font-bold text-xl mb-1">{user.name}</h3>
        <p className="text-xs font-mono text-zinc-500 mb-1">{user.email}</p>
        {user.created_at && (
          <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 mb-5" data-testid="admin-user-registered-since">
            Registrovaný od: <span className="text-gold-400/80">{new Date(user.created_at).toLocaleDateString("sk-SK", { day: "numeric", month: "long", year: "numeric" })}</span>
            <span className="text-zinc-700"> · {(() => {
              const days = Math.floor((Date.now() - new Date(user.created_at).getTime()) / 86400000);
              if (days < 1) return "dnes";
              if (days === 1) return "pred 1 dňom";
              if (days < 5) return `pred ${days} dňami`;
              if (days < 30) return `pred ${days} dňami`;
              if (days < 365) return `pred ${Math.floor(days / 30)} mes.`;
              return `pred ${Math.floor(days / 365)} r. ${Math.floor((days % 365) / 30)} mes.`;
            })()}</span>
          </p>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Rola</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" data-testid="admin-role-select">
              <option value="user">Užívateľ</option>
              <option value="apprentice">Učeň</option>
              <option value="master">Majster</option>
              <option value="lead_engineer">Vedúci inžinier</option>
              <option value="moderator">Moderátor</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Plán predplatného</label>
            <select value={planTier} onChange={(e) => setPlanTier(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" data-testid="admin-plan-tier">
              <option value="free">Free</option>
              <option value="start">Start (€1.99/2dni)</option>
              <option value="active">Active (€3.99/týž.)</option>
              <option value="pro">Pro (€6.99/mes.)</option>
              <option value="gold">👑 Gold (€29.99/365d)</option>
              <option value="excellent">★ Excellent — Neobmedzený prístup (admin only)</option>
            </select>
            {planTier === "gold" && (
              <p className="text-[10px] text-amber-300 mt-1.5 leading-relaxed">
                Prémiový GOLD status na 365 dní. Klient dostane push + ďakovný email
                a v komunite mu vedľa mena pribudne 👑 zlatá korunka.
              </p>
            )}
            {planTier === "excellent" && (
              <p className="text-[10px] text-gold-400 mt-1.5 leading-relaxed">
                Doživotný prístup do roku 2099. Tichý režim — žiadne emaily o obnove,
                vypršaní ani upomienky. Vhodné pre certifikovaných profesionálov,
                ambasádorov a VIP.
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Upraviť dni predplatného</label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="number"
                min="1"
                value={days}
                onChange={(e) => setDays(Math.max(1, parseInt(e.target.value || "0", 10)))}
                className="wb-input w-20 px-3 py-2 text-sm"
                data-testid="admin-extend-days-input"
              />
              <span className="text-sm text-zinc-400">dní</span>
              <button
                onClick={() => adjustDays(-1)}
                disabled={busy}
                data-testid="admin-subtract-sub"
                className="wb-btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1 border border-red-700/40 text-red-300 hover:bg-red-950/30"
                title="Odobrať dni z aktuálneho predplatného"
              >
                <Minus className="w-3 h-3" /> Odobrať
              </button>
              <button
                onClick={() => adjustDays(1)}
                disabled={busy}
                data-testid="admin-extend-sub"
                className="wb-btn-primary px-3 py-2 text-xs inline-flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Pridať
              </button>
            </div>
            <div className="text-xs text-zinc-600 mt-2">
              Aktuálne platí do: <span className="text-zinc-300 font-mono">{user.subscription_until ? new Date(user.subscription_until).toLocaleDateString("sk-SK") : "—"}</span>
            </div>
            <PipeCreditsBlock userId={user.id} />

            {/* Calendar picker — pick exact expiry date */}
            <div className="mt-3 pt-3 border-t border-zinc-800/60">
              <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5 inline-flex items-center gap-1.5">
                <CalendarIcon className="w-3 h-3" /> Alebo nastav presný dátum konca:
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="date"
                  defaultValue={user.subscription_until ? new Date(user.subscription_until).toISOString().slice(0, 10) : ""}
                  min={new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 10)}
                  className="wb-input px-3 py-2 text-sm flex-1 min-w-[140px]"
                  data-testid="admin-exact-date-input"
                  id={`admin-exact-date-${user.id}`}
                />
                <button
                  onClick={() => {
                    const el = document.getElementById(`admin-exact-date-${user.id}`);
                    if (el && el.value) setExactDate(el.value);
                  }}
                  disabled={busy}
                  data-testid="admin-set-exact-date"
                  className="wb-btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1 border border-gold-700/40 text-gold-300 hover:bg-gold-950/30"
                >
                  <CalendarIcon className="w-3 h-3" /> Nastaviť
                </button>
              </div>
              <p className="text-[10px] text-zinc-600 mt-1.5">Klikni na dátum → predplatné skončí presne v ten deň o 23:59.</p>
            </div>
          </div>

          <div className="border-t border-yellow-700/30 pt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                id="certified"
                checked={certified}
                onChange={(e) => setCertified(e.target.checked)}
                data-testid="admin-certified-toggle"
              />
              <span className="inline-flex items-center gap-1.5 text-sm font-display font-bold">
                <CertifiedBadge size="sm" />
                Pripnúť SauberEko® Certificate
              </span>
            </label>
            {certified && (
              <div className="mt-3 ml-6 space-y-3">
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
                    Meno na certifikáte (rodné meno a priezvisko)
                  </label>
                  <input
                    type="text"
                    value={certHolderName}
                    onChange={(e) => setCertHolderName(e.target.value)}
                    placeholder="napr. Martin Smrkta"
                    className="wb-input w-full px-3 py-2 text-sm"
                    data-testid="admin-cert-holder-name"
                  />
                  <p className="text-[10px] text-zinc-600 mt-1 italic">
                    Zobrazí sa v sieni slávy. Ak je prázdne, použije sa meno z profilu.
                  </p>
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
                    Číslo certifikátu (voliteľné — nechaj prázdne pre auto-generovanie)
                  </label>
                  <input
                    type="text"
                    value={certNo}
                    onChange={(e) => setCertNo(e.target.value)}
                    placeholder="Sa.Eko.Prs-2026-0001"
                    className="wb-input w-full px-3 py-2 text-xs font-mono"
                    data-testid="admin-cert-no"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">
                    Kategória v Sieni slávy
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      data-testid="admin-cert-role-professional"
                      onClick={() => setCertRole("professional")}
                      className={`px-3 py-2 text-xs border text-left transition-colors ${
                        certRole === "professional"
                          ? "border-gold-500 bg-gold-950/30 text-gold-300"
                          : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-500"
                      }`}
                    >
                      <div className="font-semibold">Profesionál</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">welder / pipefitter</div>
                    </button>
                    <button
                      type="button"
                      data-testid="admin-cert-role-recruiter"
                      onClick={() => setCertRole("recruiter")}
                      className={`px-3 py-2 text-xs border text-left transition-colors ${
                        certRole === "recruiter"
                          ? "border-gold-500 bg-gold-950/30 text-gold-300"
                          : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-500"
                      }`}
                    >
                      <div className="font-semibold">Nábor &amp; HR</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">recruiter / agency</div>
                    </button>
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-1.5 italic">
                    Použije sa pri zobrazení v Sieni slávy. Po zmene treba kliknúť „Uložiť“.
                  </p>
                </div>
              </div>
            )}
            {certified && user.id && (
              <CertPhotoUploader userId={user.id} initialPhoto={user.certificate_photo} />
            )}
          </div>

          <div className="border-t border-purple-700/20 pt-4">
            <label className="block text-xs font-mono uppercase tracking-wider text-purple-300 mb-2">
              🎭 Srandovný status / titul (zobrazí sa v chate)
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value.slice(0, 24))}
                placeholder="napr. somroš, pirát, vymyseľník..."
                maxLength={24}
                className="wb-input flex-1 px-3 py-2 text-sm"
                data-testid="admin-custom-title-input"
              />
              <select
                value={customTitleColor}
                onChange={(e) => setCustomTitleColor(e.target.value)}
                className="wb-input px-2 py-2 text-xs"
                data-testid="admin-custom-title-color"
              >
                {COLOR_OPTIONS.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            {customTitle && (
              <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
                <span>Náhľad:</span>
                <FunnyTitleBadge title={customTitle} color={customTitleColor} />
              </div>
            )}
            <div className="flex flex-wrap gap-1.5">
              {FUNNY_TITLE_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setCustomTitle(preset)}
                  className="text-[10px] px-2 py-1 border border-zinc-700 hover:border-purple-500 hover:text-purple-300 transition-colors font-mono"
                  data-testid={`preset-${preset}`}
                >
                  {preset}
                </button>
              ))}
              {customTitle && (
                <button
                  type="button"
                  onClick={() => setCustomTitle("")}
                  className="text-[10px] px-2 py-1 border border-red-800 text-red-400 hover:bg-red-900/20 font-mono"
                  data-testid="clear-funny-title"
                >
                  ✕ Vymazať
                </button>
              )}
            </div>
            <p className="text-[10px] text-zinc-600 mt-2 font-mono leading-relaxed">
              Status sa zobrazí pri každej správe používateľa v chate. Max 24 znakov. Emoji povolené.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" id="banned" checked={banned} onChange={(e) => setBanned(e.target.checked)} />
            <label htmlFor="banned" className="text-sm">Zablokovať účet</label>
          </div>

          <div className="flex gap-2 pt-2 border-t border-zinc-800">
            <button onClick={() => save()} disabled={busy} data-testid="admin-save-user"
              className="wb-btn-primary px-4 py-2 text-sm">Uložiť zmeny</button>
            <button onClick={remove} disabled={busy} data-testid="admin-delete-user"
              className="wb-btn-ghost px-3 py-2 text-sm text-red-400 inline-flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" /> Zmazať účet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const ROLE_META = {
  user: { label: "Užívateľ", cls: "!text-zinc-400" },
  apprentice: { label: "Učeň", cls: "!text-blue-400 !border-blue-700" },
  master: { label: "Majster", cls: "!text-orange-400 !border-orange-700" },
  lead_engineer: { label: "Vedúci inžinier", cls: "!text-purple-400 !border-purple-700" },
  moderator: { label: "Moderátor", cls: "!text-emerald-400 !border-emerald-700" },
  admin: { label: "Admin", cls: "!text-gold-400 !border-gold-600" },
};

function RoleBadge({ role }) {
  const m = ROLE_META[role] || ROLE_META.user;
  return <span className={`wb-tag ${m.cls}`} data-testid={`role-${role}`}>{m.label}</span>;
}

function JobsAdminTab() {
  const [jobs, setJobs] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(null);
  const [rejecting, setRejecting] = useState(null); // ad obj when rejecting
  const [statusFilter, setStatusFilter] = useState("all"); // all | pending_review | pending_payment | active | expired
  const load = () => api.get("/admin/jobs").then((r) => setJobs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Naozaj zmazať inzerát? Akcia je nezvratná.")) return;
    setBusy(id);
    await api.delete(`/admin/jobs/${id}`).finally(() => setBusy(null));
    load();
  };
  const extend = async (id, days) => {
    setBusy(id);
    await api.patch(`/admin/jobs/${id}`, { extend_days: days }).finally(() => setBusy(null));
    load();
  };
  const setStatus = async (id, status) => {
    setBusy(id);
    await api.patch(`/admin/jobs/${id}`, { status }).finally(() => setBusy(null));
    load();
  };
  const approve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/jobs/${id}/approve`, {});
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Chyba pri schvaľovaní");
    } finally { setBusy(null); }
  };

  const counts = {
    all: jobs.length,
    pending_review: jobs.filter((j) => j.status === "pending_review").length,
    pending_payment: jobs.filter((j) => j.status === "pending_payment").length,
    active: jobs.filter((j) => j.status === "active").length,
    expired: jobs.filter((j) => j.status === "expired").length,
  };

  const filtered = jobs.filter((j) => {
    if (statusFilter !== "all" && j.status !== statusFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (j.title || "").toLowerCase().includes(s) ||
           (j.company || "").toLowerCase().includes(s) ||
           (j.user_email || "").toLowerCase().includes(s);
  });

  const FILTERS = [
    { id: "all", label: "Všetky", cls: "!text-zinc-300 !border-zinc-700" },
    { id: "pending_review", label: "Na schválenie", cls: "!text-amber-400 !border-amber-700" },
    { id: "pending_payment", label: "Čaká platbu", cls: "!text-green-400 !border-green-700" },
    { id: "active", label: "Aktívne", cls: "!text-blue-400 !border-blue-700" },
    { id: "expired", label: "Vypršané", cls: "!text-zinc-500 !border-zinc-700" },
  ];

  const statusBadge = (s) => {
    if (s === "active") return { cls: "!text-blue-400 !border-blue-700", label: "Aktívny" };
    if (s === "expired") return { cls: "!text-zinc-600 !border-zinc-700", label: "Vypršaný" };
    if (s === "pending_payment") return { cls: "!text-green-400 !border-green-700", label: "Čaká platbu" };
    if (s === "pending_review") return { cls: "!text-amber-400 !border-amber-700", label: "Na schválenie" };
    return { cls: "!text-zinc-400 !border-zinc-700", label: s };
  };

  return (
    <div data-testid="admin-jobs-tab">
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="admin-jobs-search" type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Hľadať podľa názvu, firmy alebo emailu…"
            className="wb-input w-full pl-10 pr-3 py-2 text-sm" />
        </div>
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{filtered.length} inzerátov</div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-jobs-filters">
        {FILTERS.map((f) => (
          <button key={f.id} onClick={() => setStatusFilter(f.id)}
            data-testid={`admin-jobs-filter-${f.id}`}
            className={`wb-tag inline-flex items-center gap-1.5 transition-all ${f.cls} ${statusFilter === f.id ? "ring-1 ring-gold-500" : "opacity-70 hover:opacity-100"}`}>
            {f.label}
            <span className="font-mono">({counts[f.id] || 0})</span>
            {f.id === "pending_review" && counts.pending_review > 0 && (
              <span className="ml-1 w-1.5 h-1.5 bg-amber-400 rounded-full wb-pulse" />
            )}
          </button>
        ))}
      </div>

      <div className="border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/40">
            <tr className="text-zinc-500 text-xs uppercase tracking-wider">
              <th className="px-3 py-2 text-left">Názov</th>
              <th className="px-3 py-2 text-left">Firma</th>
              <th className="px-3 py-2 text-left">Užívateľ</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Platnosť</th>
              <th className="px-3 py-2 text-right">Akcie</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((j) => {
              const expires = j.expires_at ? new Date(j.expires_at) : null;
              const expired = expires && expires < new Date();
              const sb = statusBadge(j.status);
              const isPendingReview = j.status === "pending_review";
              return (
                <tr key={j.id} className="border-t border-zinc-800" data-testid={`admin-job-${j.id}`}>
                  <td className="px-3 py-3">
                    <div className="font-display font-bold">{j.title}</div>
                    <div className="text-xs text-zinc-500 truncate max-w-xs">{j.description?.slice(0, 80)}{(j.description || "").length > 80 ? "…" : ""}</div>
                    {/* Prominent Approve/Reject buttons directly on the row — visible without
                        horizontally scrolling the table on mobile. Duplicates the buttons in the
                        Actions column so admin has a one-tap approve on any screen size. */}
                    {isPendingReview && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => approve(j.id)}
                          disabled={busy === j.id}
                          data-testid={`admin-job-${j.id}-approve-inline`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-wider bg-green-950/40 border border-green-700 text-green-300 hover:bg-green-900/50 disabled:opacity-50"
                          title="Schváliť tento inzerát"
                        >
                          {busy === j.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsUp className="w-3.5 h-3.5" />}
                          Schváliť
                        </button>
                        <button
                          onClick={() => setRejecting(j)}
                          disabled={busy === j.id}
                          data-testid={`admin-job-${j.id}-reject-inline`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-wider bg-red-950/30 border border-red-800 text-red-300 hover:bg-red-900/40 disabled:opacity-50"
                          title="Zamietnuť s dôvodom"
                        >
                          <ThumbsDown className="w-3.5 h-3.5" />
                          Zamietnuť
                        </button>
                        <span className="text-[10px] text-amber-400 font-mono">← Ťukni pre schválenie</span>
                      </div>
                    )}
                    {/* Category badge — jobs/equipment/other */}
                    {j.category && (
                      <div className="mt-1.5">
                        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${
                          j.category === "jobs" ? "border-blue-800 text-blue-300 bg-blue-950/30" :
                          j.category === "equipment" ? "border-purple-800 text-purple-300 bg-purple-950/30" :
                          "border-zinc-700 text-zinc-400 bg-zinc-900/30"
                        }`}>
                          {j.category === "jobs" ? "Ponuka práce" :
                           j.category === "equipment" ? "Zváracia technika" : "Ďalšie"}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-zinc-300">{j.company}</td>
                  <td className="px-3 py-3 text-xs font-mono text-zinc-400">{j.user_email}</td>
                  <td className="px-3 py-3">
                    <span className={`wb-tag ${sb.cls}`}>{sb.label}</span>
                  </td>
                  <td className="px-3 py-3 text-xs font-mono text-zinc-500">
                    {expires ? expires.toLocaleDateString() : "—"}
                    {expired && <div className="text-red-400 text-[10px]">vypršané</div>}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                      {isPendingReview && (
                        <>
                          <button onClick={() => approve(j.id)} disabled={busy === j.id}
                            data-testid={`admin-job-${j.id}-approve`}
                            className="wb-btn-ghost px-2 py-1 text-[10px] !text-green-400 inline-flex items-center gap-1" title="Schváliť">
                            <ThumbsUp className="w-3 h-3" /> Schváliť
                          </button>
                          <button onClick={() => setRejecting(j)} disabled={busy === j.id}
                            data-testid={`admin-job-${j.id}-reject`}
                            className="wb-btn-ghost px-2 py-1 text-[10px] !text-red-400 inline-flex items-center gap-1" title="Zamietnuť">
                            <ThumbsDown className="w-3 h-3" /> Zamietnuť
                          </button>
                        </>
                      )}
                      <button onClick={() => setEditing(j)} disabled={busy === j.id} data-testid={`admin-job-${j.id}-edit`}
                        className="wb-btn-ghost px-2 py-1 text-[10px]" title="Upraviť inzerát">
                        Upraviť
                      </button>
                      <button onClick={() => extend(j.id, 7)} disabled={busy === j.id} data-testid={`admin-job-${j.id}-extend7`}
                        className="wb-btn-ghost px-2 py-1 text-[10px]" title="Predĺžiť o 7 dní">
                        +7d
                      </button>
                      <button onClick={() => extend(j.id, 30)} disabled={busy === j.id} data-testid={`admin-job-${j.id}-extend30`}
                        className="wb-btn-ghost px-2 py-1 text-[10px]" title="Predĺžiť o 30 dní">
                        +30d
                      </button>
                      {j.status !== "active" && !isPendingReview && (
                        <button onClick={() => setStatus(j.id, "active")} disabled={busy === j.id} data-testid={`admin-job-${j.id}-activate`}
                          className="wb-btn-ghost px-2 py-1 text-[10px] !text-green-400" title="Zaktivovať">
                          Aktiv.
                        </button>
                      )}
                      <button onClick={() => remove(j.id)} disabled={busy === j.id} data-testid={`admin-job-${j.id}-delete`}
                        className="wb-btn-ghost px-2 py-1 text-[10px] !text-red-400" title="Zmazať">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan="6" className="px-3 py-10 text-center text-sm text-zinc-600">Žiadne inzeráty</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {editing && <EditJobModal job={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {rejecting && <RejectJobModal job={rejecting} onClose={() => setRejecting(null)} onDone={() => { setRejecting(null); load(); }} />}
    </div>
  );
}

function RejectJobModal({ job, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) {
      setErr("Dôvod musí mať aspoň 5 znakov.");
      return;
    }
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/jobs/${job.id}/reject`, { reason: reason.trim() });
      onDone();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Chyba");
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="admin-job-reject-modal">
      <div className="bg-[var(--wb-surface)] border border-red-700/40 max-w-lg w-full p-6 relative my-auto max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        <h3 className="font-display font-bold text-xl mb-1 text-red-400">Zamietnuť inzerát</h3>
        <p className="text-xs text-zinc-500 mb-4">
          „{job.title}" od {job.user_email}. Po odoslaní bude inzerát zmazaný a užívateľ dostane email s dôvodom.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <textarea data-testid="admin-job-reject-reason"
            value={reason} onChange={(e) => setReason(e.target.value)}
            rows={4} required minLength={5} maxLength={500}
            placeholder={'Napíš jasný dôvod, aby user vedel, čo opraviť. Napr. „Inzerát neuvádza konkrétnu firmu" alebo „Spam / nesúvisí so zváraním".'}
            className="wb-input w-full px-3 py-2 text-sm font-mono" />
          {err && (
            <div className="flex items-center gap-2 p-2 border border-red-900 bg-red-950/30 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" /> {err}
            </div>
          )}
          <div className="flex items-center gap-3 pt-3 border-t border-zinc-800">
            <button type="submit" disabled={busy} data-testid="admin-job-reject-confirm"
              className="wb-btn-primary !bg-red-600 hover:!bg-red-500 px-5 py-3 text-sm flex-1 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsDown className="w-4 h-4" />}
              {busy ? "Posielam…" : "Zamietnuť a poslať email"}
            </button>
            <button type="button" onClick={onClose} className="wb-btn-ghost px-4 py-3 text-sm">Zrušiť</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditJobModal({ job, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: job.title || "",
    company: job.company || "",
    location: job.location || "",
    description: job.description || "",
    contact_email: job.contact_email || "",
    salary_range: job.salary_range || "",
    job_type: job.job_type || "Full-time",
    status: job.status || "active",
  });
  const [busy, setBusy] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/jobs/${job.id}`, form);
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="admin-job-edit-modal">
      <div className="bg-[var(--wb-surface)] border border-gold-700/40 max-w-2xl w-full p-6 relative my-auto max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        <h3 className="font-display font-bold text-xl mb-1">Upraviť inzerát</h3>
        <p className="text-xs font-mono text-zinc-500 mb-5">{job.user_email}</p>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Názov</label>
            <input value={form.title} onChange={change("title")} className="wb-input w-full px-3 py-2 text-sm" data-testid="job-edit-title" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Firma</label>
            <input value={form.company} onChange={change("company")} className="wb-input w-full px-3 py-2 text-sm" data-testid="job-edit-company" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Lokalita</label>
            <input value={form.location} onChange={change("location")} className="wb-input w-full px-3 py-2 text-sm" data-testid="job-edit-location" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Kontaktný email</label>
            <input type="email" value={form.contact_email} onChange={change("contact_email")} className="wb-input w-full px-3 py-2 text-sm" data-testid="job-edit-email" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Mzda</label>
            <input value={form.salary_range} onChange={change("salary_range")} placeholder="napr. 1500-2200 €" className="wb-input w-full px-3 py-2 text-sm" data-testid="job-edit-salary" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Typ úväzku</label>
            <select value={form.job_type} onChange={change("job_type")} className="wb-input w-full px-3 py-2 text-sm" data-testid="job-edit-jobtype">
              <option>Full-time</option>
              <option>Part-time</option>
              <option>Contract</option>
              <option>Project</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Status</label>
            <select value={form.status} onChange={change("status")} className="wb-input w-full px-3 py-2 text-sm" data-testid="job-edit-status">
              <option value="active">Aktívny</option>
              <option value="pending_payment">Čaká na platbu</option>
              <option value="expired">Vypršaný</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Popis</label>
            <textarea value={form.description} onChange={change("description")} rows={6} className="wb-input w-full px-3 py-2 text-sm font-mono" data-testid="job-edit-description" />
          </div>
        </div>

        <div className="flex gap-2 pt-4 mt-4 border-t border-zinc-800">
          <button onClick={save} disabled={busy} data-testid="job-edit-save" className="wb-btn-primary px-4 py-2 text-sm">Uložiť zmeny</button>
          <button onClick={onClose} disabled={busy} className="wb-btn-ghost px-4 py-2 text-sm">Zrušiť</button>
        </div>
      </div>
    </div>
  );
}

function PaymentsTab() {
  const [summary, setSummary] = useState(null);
  const [mode, setMode] = useState(null);
  const [txs, setTxs] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [resendTx, setResendTx] = useState(null); // tx selected for resend modal

  const load = async () => {
    const [s, m, t] = await Promise.all([
      api.get("/admin/payments/summary"),
      api.get("/stripe/mode"),
      api.get("/admin/payments", { params: filter ? { status_filter: filter } : {} }),
    ]);
    setSummary(s.data); setMode(m.data.mode); setTxs(t.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  // Client-side search filter over email / session / plan
  const visibleTxs = txs.filter((t) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (t.user_email || "").toLowerCase().includes(q) ||
      (t.session_id || "").toLowerCase().includes(q) ||
      (t.plan || "").toLowerCase().includes(q) ||
      (t.user_name || "").toLowerCase().includes(q)
    );
  });

  const downloadCsv = async () => {
    const r = await api.get("/admin/payments/export.csv", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `tigik-payments-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const [zipMonth, setZipMonth] = useState(() => new Date().toISOString().slice(0,7));
  const [zipBusy, setZipBusy] = useState(false);
  const [rangeFrom, setRangeFrom] = useState(() => {
    const d = new Date(); d.setDate(1); return d.toISOString().slice(0,10);
  });
  const [rangeTo, setRangeTo] = useState(() => new Date().toISOString().slice(0,10));
  const [rangeOnlySent, setRangeOnlySent] = useState(true);
  const [rangeBusy, setRangeBusy] = useState(false);
  const [rangeEmail, setRangeEmail] = useState("sauber.eko@gmail.com");
  const [rangeEmailBusy, setRangeEmailBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [reconcileBusy, setReconcileBusy] = useState(false);

  const reconcileDonations = async () => {
    const hours = window.prompt(
      "Načítať uviaznuté platby (status: initiated) za posledných koľko HODÍN?\n\n" +
      "• Overí VŠETKY platby (donations, subscriptions, job ads, gold…) v Stripe.\n" +
      "• Ak Stripe potvrdí 'paid' → v DB označíme 'paid', udelíme bonus dni na predplatné, u darov pošleme ďakovný email + faktúru.\n" +
      "• Idempotentné — neduplicuje emaily ani bonusy.\n\n" +
      "Bežná hodnota: 720 hodín (= 30 dní). Pre dlhšie zadaj 8760 = 1 rok.",
      "720"
    );
    if (!hours) return;
    const h = parseInt(hours, 10);
    if (!h || h < 1 || h > 8760) { alert("Zadaj číslo hodín 1–8760."); return; }
    // Ask whether to send emails (skip if admin already contacted customers manually)
    const sendEmails = window.confirm(
      "Poslať klientom (donations) ďakovný email + faktúru?\n\n" +
      "• OK = pošli emaily (bežná voľba)\n" +
      "• Cancel = NEodosielať (ak si už klienta kontaktoval ručne)"
    );
    setReconcileBusy(true);
    try {
      // 1) Reconcile donations first (with emails when requested).
      const rDon = await api.post(`/admin/donations/reconcile-stuck?hours=${h}&skip_emails=${!sendEmails}`);
      const donResults = rDon.data.results || [];
      const donFixed = donResults.filter(x => x.ok && x.detail?.action === "reconciled OK").length;
      const donSkipped = donResults.filter(x => x.ok && x.detail?.action?.includes("skipped")).length;
      const donErrored = donResults.filter(x => !x.ok);

      // 2) Reconcile ALL other plans (subscriptions, job ads, gold, one-time). Days = ceil(hours/24).
      const days = Math.max(1, Math.ceil(h / 24));
      const rAll = await api.post(`/admin/payments/reconcile-all-stuck?days=${days}`);
      const allFixed = rAll.data.reconciled || 0;
      const allSkipped = rAll.data.skipped_not_paid_in_stripe || 0;
      const allFailed = rAll.data.failed || 0;
      const allErrors = (rAll.data.results || []).filter(x => !x.ok);

      const allErrored = [...donErrored, ...allErrors];
      const failed = allErrored.length + allFailed - allErrors.length; // avoid double count
      let detail = "";
      if (allErrored.length > 0) {
        detail = "\n\nDetail chýb:\n" + allErrored.slice(0, 3).map(x => {
          const errStr = (x.error || "?").slice(0, 200);
          const origin = x.trace_origin ? `\n   ↳ ${x.trace_origin.slice(0, 160)}` : "";
          return `• …${(x.session_id || "?").slice(-12)}: ${errStr}${origin}`;
        }).join("\n");
        if (allErrored.length > 3) detail += `\n• …a ďalších ${allErrored.length - 3} chýb (pozri Console F12)`;
      }
      alert(
        `Hotovo!\n\n` +
        `── Donations (za ${h} h) ──\n` +
        `• Skontrolovaných: ${rDon.data.checked}\n` +
        `• Dotiahnutých (paid${sendEmails ? " + email" : ", BEZ emailov"}): ${donFixed}\n` +
        `• Preskočených: ${donSkipped}\n\n` +
        `── Predplatné / Inzeráty / Ostatné (za ${days} d) ──\n` +
        `• Skontrolovaných: ${rAll.data.checked}\n` +
        `• Dotiahnutých (paid, bonus dni udelené): ${allFixed}\n` +
        `• Preskočených (Stripe ich nepotvrdil): ${allSkipped}\n` +
        `• Chyby: ${failed}` + detail
      );
      console.log("[Reconcile donations]", rDon.data);
      console.log("[Reconcile all]", rAll.data);
      load();
    } catch (e) {
      alert("Chyba: " + (e.response?.data?.detail || e.message));
    } finally { setReconcileBusy(false); }
  };
  const downloadAccountingCsv = async () => {
    if (!/^\d{4}-\d{2}$/.test(zipMonth)) {
      alert("Mesiac musí byť vo formáte YYYY-MM (napr. 2026-06).");
      return;
    }
    try {
      const r = await api.get(`/admin/payments/export-accounting?month=${zipMonth}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tigik-uctovnictvo-${zipMonth}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("CSV export zlyhal: " + (e.response?.data?.detail || e.message));
    }
  };

  const markPaidManually = async (tx) => {
    const method = window.prompt(
      `Ako bola platba ${tx.user_email} (${tx.amount} €, ${tx.plan}) prijatá?\n\n` +
      `1 = bankový prevod (SEPA / IBAN)\n` +
      `2 = hotovosť\n` +
      `3 = iný spôsob\n\n` +
      `Napíš číslo 1 / 2 / 3:`,
      "1"
    );
    if (!method) return;
    const methodMap = { "1": "bank_transfer", "2": "cash", "3": "other" };
    const payment_method = methodMap[method.trim()];
    if (!payment_method) { alert("Neplatná voľba — zadaj 1, 2 alebo 3."); return; }
    const note = window.prompt(
      `Poznámka pre účtovníčku (voliteľné):\n` +
      `Napr. „Prijaté 1.6.2026 na účet SK00..., VS 123" alebo prázdne.`,
      ""
    );
    const sendEmails = window.confirm(
      "Poslať klientovi ďakovný email + faktúru?\n\n" +
      "• OK = pošli faktúru cez Tigik\n" +
      "• Cancel = NEodosielať (ak si ho už kontaktoval ručne)"
    );
    try {
      const r = await api.post(`/admin/payments/${tx.session_id}/mark-paid`, {
        payment_method,
        note: note || null,
        send_emails: sendEmails,
      });
      alert(
        `✅ Označené ako zaplatené\n\n` +
        `• Suma: ${r.data.amount} €\n` +
        `• Spôsob: ${r.data.payment_method}\n` +
        `• Nový plán: ${r.data.new_tier || "—"}\n` +
        `• Platnosť do: ${r.data.new_subscription_until ? new Date(r.data.new_subscription_until).toLocaleDateString("sk-SK") : "—"}\n` +
        `• Emaily: ${r.data.emails}`
      );
      load();
    } catch (e) {
      alert("Chyba: " + (e.response?.data?.detail || e.message));
    }
  };

  const downloadZip = async () => {
    if (!/^\d{4}-\d{2}$/.test(zipMonth)) {
      alert("Mesiac musí byť vo formáte YYYY-MM (napr. 2026-03).");
      return;
    }
    if (!window.confirm(`Pripraviť ZIP archív všetkých zaplatených faktúr za ${zipMonth}?\nMôže to trvať niekoľko sekúnd až minútu podľa počtu transakcií.`)) return;
    setZipBusy(true);
    try {
      const r = await api.get("/admin/invoices/export-zip", {
        params: { month: zipMonth },
        responseType: "blob",
      });
      const count = r.headers["x-invoice-count"] || "?";
      const failed = r.headers["x-invoice-failed"] || "0";
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = `tigik-invoices-${zipMonth}.zip`;
      a.click(); URL.revokeObjectURL(url);
      alert(`Hotovo — pribalených ${count} PDF faktúr${Number(failed) > 0 ? `, ${failed} preskočených pre chybu` : ""}.`);
    } catch (e) {
      alert("Chyba pri exporte: " + (e.response?.data?.detail || e.message));
    } finally { setZipBusy(false); }
  };

  const downloadInvoicesRange = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(rangeFrom) || !/^\d{4}-\d{2}-\d{2}$/.test(rangeTo)) {
      alert("Dátumy musia byť vo formáte YYYY-MM-DD.");
      return;
    }
    if (rangeFrom > rangeTo) {
      alert("Dátum OD nemôže byť neskorší ako DO.");
      return;
    }
    if (!window.confirm(`Pripraviť ZIP so ${rangeOnlySent ? "odoslanými" : "všetkými zaplatenými"} faktúrami od ${rangeFrom} do ${rangeTo}?`)) return;
    setRangeBusy(true);
    try {
      const r = await api.get("/admin/invoices/export-range", {
        params: { date_from: rangeFrom, date_to: rangeTo, only_sent: rangeOnlySent },
        responseType: "blob",
      });
      const count = r.headers["x-invoice-count"] || "?";
      const failed = r.headers["x-invoice-failed"] || "0";
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = `tigik-invoices-${rangeFrom}_to_${rangeTo}.zip`;
      a.click(); URL.revokeObjectURL(url);
      alert(`Hotovo — pribalených ${count} PDF faktúr${Number(failed) > 0 ? `, ${failed} preskočených pre chybu` : ""}.`);
    } catch (e) {
      alert("Chyba pri exporte: " + (e.response?.data?.detail || e.message));
    } finally { setRangeBusy(false); }
  };

  const emailInvoicesRange = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(rangeFrom) || !/^\d{4}-\d{2}-\d{2}$/.test(rangeTo)) {
      alert("Dátumy musia byť vo formáte YYYY-MM-DD.");
      return;
    }
    if (rangeFrom > rangeTo) {
      alert("Dátum OD nemôže byť neskorší ako DO.");
      return;
    }
    if (!rangeEmail || !/^\S+@\S+\.\S+$/.test(rangeEmail)) {
      alert("Zadaj platnú emailovú adresu (napr. tvoj-email@gmail.com alebo účtovnícka@firma.sk).");
      return;
    }
    if (!window.confirm(`Poslať ZIP so ${rangeOnlySent ? "odoslanými" : "všetkými zaplatenými"} faktúrami od ${rangeFrom} do ${rangeTo} na email ${rangeEmail}?`)) return;
    setRangeEmailBusy(true);
    try {
      const r = await api.post("/admin/invoices/email-range", {
        date_from: rangeFrom,
        date_to: rangeTo,
        only_sent: rangeOnlySent,
        to_email: rangeEmail,
      });
      alert(r.data?.message || `ZIP sa pripravuje a bude odoslaný na ${rangeEmail} v priebehu 1–3 minút.`);
    } catch (e) {
      alert("Chyba: " + (e.response?.data?.detail || e.message));
    } finally { setRangeEmailBusy(false); }
  };

  const sendMonthlyReport = async () => {
    if (!/^\d{4}-\d{2}$/.test(zipMonth)) {
      alert("Mesiac musí byť vo formáte YYYY-MM.");
      return;
    }
    if (!window.confirm(`Naozaj poslať ZIP report za ${zipMonth} na sauber.eko@gmail.com?`)) return;
    setSendBusy(true);
    try {
      const r = await api.post("/admin/invoices/send-monthly-report", null, { params: { month: zipMonth } });
      if (r.data?.ok) {
        alert(
          `✅ Odoslané úspešne\n\n` +
          `• Mesiac: ${r.data.month}\n` +
          `• Pribalené PDF faktúry: ${r.data.count_ok}\n` +
          `• Resend ID: ${r.data.resend_id || "n/a"}\n\n` +
          `Skontroluj inbox sauber.eko@gmail.com.`
        );
      } else {
        // backend returned ok:false (shouldn't happen with raise_on_failure, but handle it anyway)
        alert(
          `⚠️ Email NEBOL odoslaný\n\n` +
          `Dôvod: ${r.data?.error || "neznáma chyba (skontrolovať backend log)"}\n\n` +
          `Skús znovu alebo skontaktuj support.`
        );
      }
    } catch (e) {
      alert(
        `❌ Chyba pri odosielaní\n\n` +
        `${e.response?.data?.detail || e.message}\n\n` +
        `Tip: ak chýbajú PDF faktúry za daný mesiac, skontroluj cez "Stiahnuť ZIP" či sú v ňom faktúry.`
      );
    } finally { setSendBusy(false); }
  };

  const deletePayment = async (tx) => {
    if (tx.payment_status === "paid") {
      alert("Zaplatené platby nemožno vymazať (účtovnícky zákon — 10-ročné uchovávanie).");
      return;
    }
    const label = `${tx.user_email || "?"} · ${tx.plan || "?"} · €${tx.amount ?? "?"} · ${tx.payment_status}`;
    if (!window.confirm(
      `Naozaj vymazať túto rozpracovanú platbu?\n\n${label}\n\n` +
      `Toto sa hodí pre Stripe checkout ktorý sa nikdy nedokončil. ` +
      `Akcia je nezvratná, ale platba sa môže neskôr znovu vytvoriť ak klient klikne na link.`
    )) return;
    try {
      await api.delete(`/admin/payments/${encodeURIComponent(tx.session_id)}`);
      load();
    } catch (e) {
      alert("Chyba pri mazaní: " + (e.response?.data?.detail || e.message));
    }
  };

  const regenerateInvoicePdf = async (tx) => {
    // Retroactively re-render a paid invoice PDF with correct VAT treatment
    // (SK 23 % / EU B2B reverse-charge / non-EU export) and a proper 1-YYYY-NNNN number.
    if (!window.confirm(
      `Prerobiť faktúru pre "${tx.user_name || tx.user_email}" (${tx.amount} EUR)?\n\n` +
      `• Priradí sa nové číslo v tvare 1-YYYY-NNNN (ak ešte nemá)\n` +
      `• DPH sa prepočíta podľa krajiny klienta:\n` +
      `   – SK → 23 %\n` +
      `   – EU + platné IČ DPH → 0 % + Reverse charge\n` +
      `   – Mimo EÚ → 0 % + Export\n` +
      `• PDF sa stiahne priamo do tvojho počítača.`
    )) return;
    try {
      const resp = await api.post(
        `/admin/payments/${encodeURIComponent(tx.session_id)}/regenerate-pdf`,
        null,
        { responseType: "blob" }
      );
      const invNo = resp.headers?.["x-invoice-no"] || "faktura";
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Tigik-Faktura-${invNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      load(); // refresh table so invoice_no shows up
    } catch (e) {
      // Blob axios error → parse the JSON detail out of the blob
      let msg = e.message;
      const data = e.response?.data;
      if (data instanceof Blob) {
        try { msg = JSON.parse(await data.text())?.detail || msg; } catch (_e) { /* keep default msg */ }
      } else if (data?.detail) {
        msg = data.detail;
      }
      alert("Chyba pri prerábaní PDF: " + msg);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-payments">
      <div className={`border-l-2 p-4 flex items-start gap-3 ${mode === "live" ? "border-green-500 bg-green-500/5" : "border-amber-500 bg-amber-500/5"}`}>
        {mode === "live" ? <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                         : <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />}
        <div className="text-sm">
          <div className="font-display font-bold">
            Stripe režim: <span className={mode === "live" ? "text-green-400" : "text-amber-400"}>{(mode || "…").toUpperCase()}</span>
          </div>
          <div className="text-zinc-400 text-xs mt-1">
            {mode === "live"
              ? "Reálne platby sú aktívne — peniaze idú na váš bankový účet (Stripe → Payouts)."
              : "TESTOVACÍ kľúč — používa sa karta 4242 4242 4242 4242 a žiadne reálne peniaze sa neprenášajú. Po pridaní LIVE kľúča (sk_live_…) do .env reštartujte backend."}
          </div>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Celkový príjem" value={`€${summary.total_revenue_eur}`} />
          <Stat label="Tento mesiac" value={`€${summary.this_month_eur}`} />
          <Stat label="Týždenných" value={summary.paid_weekly_count} sub="€3.99 · 7 dní" />
          <Stat label="Mesačných" value={summary.paid_monthly_count} sub="€6.99 · 30 dní" />
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1 text-xs font-mono uppercase">
          {[["", "Všetky"], ["paid", "Zaplatené"], ["initiated", "Začaté"], ["expired", "Expirované"]].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)}
              data-testid={`payments-filter-${v || "all"}`}
              className={`px-3 py-1.5 border ${filter === v ? "border-gold-500 text-gold-400" : "border-zinc-800 text-zinc-500"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[260px] max-w-md ml-auto">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
            <input
              data-testid="payments-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Hľadať podľa emailu, mena, session ID, plánu…"
              className="w-full bg-zinc-900 border border-zinc-700 pl-9 pr-3 py-1.5 text-xs focus:border-gold-500 outline-none"
            />
          </div>
          <button onClick={downloadCsv} data-testid="payments-csv-export"
            className="wb-btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {/* Monthly PDF invoices ZIP — for accountant handoff */}
      <div className="border border-gold-700/30 bg-gold-950/20 p-3 flex flex-wrap items-center gap-3" data-testid="invoices-zip-export-block">
        <div className="flex items-center gap-2 text-xs text-zinc-300">
          <FileText className="w-4 h-4 text-gold-400" />
          <span>Export pre účtovníčku — ZIP všetkých PDF faktúr za zvolený mesiac:</span>
        </div>
        <input
          type="month"
          value={zipMonth}
          onChange={(e) => setZipMonth(e.target.value)}
          data-testid="invoices-zip-month"
          className="bg-zinc-900 border border-zinc-700 px-2 py-1 text-xs focus:border-gold-500 outline-none"
        />
        <button
          onClick={downloadZip}
          disabled={zipBusy}
          data-testid="invoices-zip-export-btn"
          className="wb-btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-50"
        >
          {zipBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          {zipBusy ? "Pripravujem ZIP…" : "Stiahnuť ZIP"}
        </button>
        <button
          onClick={sendMonthlyReport}
          disabled={sendBusy}
          data-testid="invoices-zip-send-accountant-btn"
          className="wb-btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-50"
          title="Manuálne odoslanie ZIP na sauber.eko@gmail.com (cron beží automaticky 2. v mesiaci)"
        >
          {sendBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {sendBusy ? "Posielam…" : "Poslať účtovníčke"}
        </button>
        <button
          onClick={downloadAccountingCsv}
          data-testid="invoices-csv-export-btn"
          className="wb-btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1 border border-green-700/40 text-green-300 hover:bg-green-950/30"
          title="CSV súbor so všetkými zaplatenými platbami (Stripe + manuálne) — otvoriť v Exceli a poslať účtovníčke"
        >
          <Download className="w-3.5 h-3.5" /> CSV pre účtovníčku
        </button>
        <span className="text-[10px] text-zinc-500 font-mono">
          📅 Cron: každý 2. deň v mesiaci → sauber.eko@gmail.com
        </span>
      </div>

      {/* Range-based invoice export — od-do */}
      <div className="border border-emerald-700/40 bg-emerald-950/10 p-3 flex flex-wrap items-center gap-3" data-testid="invoices-range-export-block">
        <div className="flex items-center gap-2 text-xs text-zinc-300">
          <FileText className="w-4 h-4 text-emerald-400" />
          <span>Stiahnuť odoslané faktúry <strong className="text-emerald-300">od–do</strong> (PDF ako klient dostal):</span>
        </div>
        <label className="flex items-center gap-1 text-xs text-zinc-400">
          Od
          <input
            type="date"
            value={rangeFrom}
            onChange={(e) => setRangeFrom(e.target.value)}
            data-testid="invoices-range-from"
            className="bg-zinc-900 border border-zinc-700 px-2 py-1 text-xs focus:border-emerald-500 outline-none"
          />
        </label>
        <label className="flex items-center gap-1 text-xs text-zinc-400">
          Do
          <input
            type="date"
            value={rangeTo}
            onChange={(e) => setRangeTo(e.target.value)}
            data-testid="invoices-range-to"
            className="bg-zinc-900 border border-zinc-700 px-2 py-1 text-xs focus:border-emerald-500 outline-none"
          />
        </label>
        <label className="flex items-center gap-1.5 text-[11px] text-zinc-400 cursor-pointer" title="Ak zapnuté: iba faktúry, ktoré reálne odišli klientovi mailom. Ak vypnuté: všetky zaplatené v období.">
          <input
            type="checkbox"
            checked={rangeOnlySent}
            onChange={(e) => setRangeOnlySent(e.target.checked)}
            data-testid="invoices-range-only-sent"
            className="accent-emerald-500"
          />
          iba odoslané
        </label>
        <button
          onClick={downloadInvoicesRange}
          disabled={rangeBusy}
          data-testid="invoices-range-export-btn"
          className="wb-btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-50 border border-emerald-500/40 hover:bg-emerald-900/30"
        >
          {rangeBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          {rangeBusy ? "Pripravujem ZIP…" : "Stiahnuť ZIP"}
        </button>
        <span className="text-zinc-600">|</span>
        <input
          type="email"
          value={rangeEmail}
          onChange={(e) => setRangeEmail(e.target.value)}
          placeholder="tvoj-email@gmail.com"
          data-testid="invoices-range-email"
          className="bg-zinc-900 border border-zinc-700 px-2 py-1 text-xs focus:border-emerald-500 outline-none min-w-[200px]"
        />
        <button
          onClick={emailInvoicesRange}
          disabled={rangeEmailBusy}
          data-testid="invoices-range-email-btn"
          className="wb-btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-50 border border-emerald-500/40 hover:bg-emerald-900/30"
          title="Odošle ZIP v pozadí (rieši Cloudflare timeout pri veľkých obdobiach)"
        >
          {rangeEmailBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {rangeEmailBusy ? "Odosielam…" : "Poslať na email"}
        </button>
        <span className="text-[10px] text-zinc-500 font-mono">
          Vhodné pre kontrolu / archiváciu / audit
        </span>
      </div>

      {/* Reconcile stuck donations — Stripe ↔ DB synchronization */}
      <div className="border border-amber-700/40 bg-amber-950/10 p-3 flex flex-wrap items-center gap-3" data-testid="reconcile-block">
        <div className="flex items-center gap-2 text-xs text-zinc-300 flex-1 min-w-[260px]">
          <RotateCcw className="w-4 h-4 text-amber-400" />
          <div>
            <div className="font-display font-bold text-amber-300">Uviaznuté platby — Stripe overenie</div>
            <div className="text-[10px] text-zinc-500 mt-0.5">Overí ALL platby (dary, predplatné, inzeráty) v Stripe. Ak zaplatili, appka ich dotiahne, udelí bonus dni a u darov pošle faktúru.</div>
            <div className="text-[10px] text-emerald-400/80 mt-1 font-mono">🤖 Auto-cron beží denne o 03:00 UTC</div>
          </div>
        </div>
        <button
          onClick={reconcileDonations}
          disabled={reconcileBusy}
          data-testid="reconcile-stuck-btn"
          className="wb-btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-50"
        >
          {reconcileBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
          {reconcileBusy ? "Overujem v Stripe…" : "Skontrolovať uviaznuté"}
        </button>
      </div>

      <AddSupporterBlock />

      <div className="border border-zinc-800 overflow-hidden">
        <div className="overflow-x-auto overflow-y-auto max-h-[560px]">
        <table className="w-full text-sm min-w-[920px]">
          <thead className="bg-zinc-900/90 backdrop-blur-sm text-left text-xs font-mono uppercase tracking-wider text-zinc-500 sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2">Dátum</th>
              <th className="px-3 py-2">Krajina</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Plán</th>
              <th className="px-3 py-2 text-right">Suma</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Faktúra</th>
              <th className="px-3 py-2 text-right">Akcia</th>
            </tr>
          </thead>
          <tbody>
            {visibleTxs.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-zinc-500">{search ? `Žiadne zhody pre „${search}"` : "Žiadne transakcie"}</td></tr>}
            {visibleTxs.map((tx) => (
              <tr key={tx.session_id}
                  onClick={() => setResendTx(tx)}
                  data-testid={`payment-row-${tx.session_id}`}
                  title="Klikni pre detail platby / faktúry / preposlanie"
                  className="border-t border-zinc-800 hover:bg-zinc-900/60 cursor-pointer transition-colors">
                <td className="px-3 py-2 font-mono text-xs text-zinc-500 whitespace-nowrap">{tx.created_at ? new Date(tx.created_at).toLocaleString() : "—"}</td>
                <td className="px-3 py-2 font-mono text-xs text-zinc-300">{tx.country || <span className="text-zinc-600">—</span>}</td>
                <td className="px-3 py-2">{tx.user_email}</td>
                <td className="px-3 py-2 font-mono text-xs">{tx.plan}</td>
                <td className="px-3 py-2 text-right font-display font-bold">€{tx.amount}</td>
                <td className="px-3 py-2">
                  <span className={`wb-tag ${
                    tx.payment_status === "paid" ? "!text-green-400 !border-green-700"
                    : tx.payment_status === "expired" ? "!text-red-400 !border-red-800"
                    : "!text-amber-400 !border-amber-800"
                  }`}>{tx.payment_status}</span>
                </td>
                <td className="px-3 py-2 text-[10px] text-zinc-500">
                  {tx.invoice_email_sent ? (
                    <div className="inline-flex flex-col">
                      <span className="text-green-400 inline-flex items-center gap-1 font-mono">
                        <CheckCircle2 className="w-3 h-3" /> Odoslaná
                      </span>
                      {tx.invoice_last_sent_to && tx.invoice_last_sent_to !== tx.user_email && (
                        <span className="text-zinc-500 truncate max-w-[140px]" title={tx.invoice_last_sent_to}>
                          → {tx.invoice_last_sent_to}
                        </span>
                      )}
                      {tx.invoice_last_sent_at && (
                        <span className="text-zinc-600 font-mono">{new Date(tx.invoice_last_sent_at).toLocaleDateString("sk-SK")}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-zinc-600 font-mono">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-flex flex-col items-end gap-1">
                    <button
                      data-testid={`resend-${tx.session_id}`}
                      onClick={() => setResendTx(tx)}
                      className={`px-2 py-1 text-[10px] inline-flex items-center gap-1 ${tx.invoice_email_sent ? "wb-btn-ghost" : "wb-btn-primary"}`}
                      title={tx.plan === "donation" ? "Zobraziť detail a poslať poďakovanie + faktúru" : "Zobraziť detail a poslať faktúru"}
                    >
                      <Send className="w-3 h-3" />
                      {tx.invoice_email_sent ? "Zobraziť / Preposlať" : (tx.plan === "donation" ? "Poslať ďakovné" : "Poslať faktúru")}
                    </button>
                    {tx.payment_status !== "paid" && (
                      <>
                        <button
                          data-testid={`mark-paid-${tx.session_id}`}
                          onClick={() => markPaidManually(tx)}
                          className="px-2 py-1 text-[10px] inline-flex items-center gap-1 border border-green-700/40 text-green-300 hover:bg-green-950/30"
                          title="Manuálne označiť ako zaplatené (bankový prevod, hotovosť, mimo Stripe)"
                        >
                          <CheckCircle2 className="w-3 h-3" /> Zaplatené (manuálne)
                        </button>
                        <button
                          data-testid={`delete-${tx.session_id}`}
                          onClick={() => deletePayment(tx)}
                          className="px-2 py-1 text-[10px] inline-flex items-center gap-1 border border-red-800/40 text-red-400 hover:bg-red-950/30"
                          title="Vymazať túto rozpracovanú/expirovanú platbu (čistenie zoznamu)"
                        >
                          <X className="w-3 h-3" /> Vymazať
                        </button>
                      </>
                    )}
                    {tx.payment_status === "paid" && (
                      <button
                        data-testid={`regen-pdf-${tx.session_id}`}
                        onClick={() => regenerateInvoicePdf(tx)}
                        className="px-2 py-1 text-[10px] inline-flex items-center gap-1 border border-gold-700/40 text-gold-300 hover:bg-gold-950/30"
                        title="Prerobiť + stiahnuť PDF s korektnou DPH (Reverse charge pre EU B2B) a novým číslom 1-YYYY-NNNN"
                      >
                        <Download className="w-3 h-3" /> Prerobiť PDF
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <div className="text-xs text-zinc-500 leading-relaxed border-t border-zinc-800 pt-4">
        <p><span className="font-mono uppercase text-zinc-400">DPH poznámka:</span> SauberEko s. r. o. je platca DPH so sadzbou 23 %. Ceny v Stripe sú definované ako <strong>vrátane DPH</strong> — z každej platby €X sa účtovne rozpisuje základ = X/1,23 a DPH = X − základ. Pre B2B odberateľov v EÚ s platným IČ DPH sa uplatňuje prenesenie daňovej povinnosti (reverse-charge, čl. 44 smernice 2006/112/ES) — odporúčame doplniť IČ DPH zákazníka do metadata pri checkoute. Pre B2C cezhraničné digitálne služby v EÚ platí OSS schéma (One-Stop-Shop) so sadzbou DPH štátu spotreby.</p>
      </div>

      {resendTx && (
        <ResendInvoiceModal
          tx={resendTx}
          onClose={() => setResendTx(null)}
          onSent={() => { setResendTx(null); load(); }}
        />
      )}
    </div>
  );
}

function ResendInvoiceModal({ tx, onClose, onSent }) {
  const isDonation = tx.plan === "donation";
  const [overrideEmail, setOverrideEmail] = useState(tx.invoice_last_sent_to || tx.user_email || "");
  const [sending, setSending] = useState(false);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const isInitiated = tx.payment_status !== "paid" && tx.payment_status !== "expired";

  const fmt = (iso) => iso ? new Date(iso).toLocaleString("sk-SK") : "—";

  const markPaid = async () => {
    if (!window.confirm(
      `Označiť túto platbu ako ZAPLATENÚ manuálne?\n\n` +
      `• Suma €${tx.amount} sa pripočíta do "Celkový príjem".\n` +
      `• Zarátané v "Tento mesiac" + Stripe ZIP exporte pre účtovníčku.\n` +
      `• Použiť keď Stripe potvrdil platbu ale aplikácia ju nestihla overiť, alebo pri offline / hotovostných platbách.\n\n` +
      `Pôvodný status: ${tx.payment_status}`
    )) return;
    setError(""); setSuccess(""); setMarking(true);
    try {
      const r = await api.post(`/admin/payments/${tx.session_id}/mark-paid`);
      if (r.data.already_paid) setSuccess("Už bolo označené ako zaplatené.");
      else setSuccess("✅ Označené ako zaplatené — pripočítané do prehľadu.");
      setTimeout(() => onSent(), 1500);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setMarking(false); }
  };

  const sendActivation = async () => {
    if (!window.confirm(
      `Poslať klientovi "PRO/GOLD aktivované" welcome email?\n\n` +
      `• Použi ak platbu si dodatočne dotiahol cez "Skontrolovať uviaznuté" ` +
      `a klient nedostal welcome email (webhook nezafungoval).\n` +
      `• NEposiela faktúru — iba luxusné vítacie oznámenie s dátumom platnosti.`
    )) return;
    setError(""); setSuccess(""); setSending(true);
    try {
      const r = await api.post(`/admin/payments/${tx.session_id}/send-activation-email`);
      setSuccess(`✅ Welcome email zaradený → ${r.data.sent_to} (tier: ${r.data.plan_tier}, do: ${r.data.valid_until?.slice(0, 10)})`);
      setTimeout(() => onSent(), 2000);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSending(false); }
  };

  const send = async () => {
    setError(""); setSuccess(""); setSending(true);
    const targetEmail = overrideEmail.trim();
    if (!targetEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(targetEmail)) {
      setError("Zadaj platný email."); setSending(false); return;
    }
    const url = isDonation
      ? `/admin/donations/resend-emails/${tx.session_id}`
      : `/admin/payments/${tx.session_id}/resend-invoice`;
    const params = targetEmail.toLowerCase() !== (tx.user_email || "").toLowerCase()
      ? { override_email: targetEmail } : {};
    try {
      const r = await api.post(url, null, { params });
      const taskId = r.data?.task_id;
      // For non-donation resend: show "preparing" then poll to confirm.
      if (!isDonation && taskId) {
        setSuccess(`⏳ Faktúra sa pripravuje a bude odoslaná na ${r.data?.sent_to || targetEmail}…`);
        let tries = 0;
        const poll = async () => {
          tries += 1;
          try {
            const s = await api.get(`/admin/email-status/${taskId}`);
            const st = s.data?.status;
            if (st === "sent") {
              setSuccess(`✅ Faktúra úspešne odoslaná na ${s.data?.to || targetEmail}`);
              setSending(false);
              setTimeout(() => onSent(), 2000);
              return;
            }
            if (st === "failed") {
              setError(`❌ Odoslanie zlyhalo: ${s.data?.error || "neznáma chyba"}`);
              setSuccess("");
              setSending(false);
              return;
            }
          } catch (_) { /* ignore transient poll errors */ }
          if (tries < 40) setTimeout(poll, 3000);
          else {
            setError("⏱️ Odosielanie trvá dlhšie ako 2 min. Skontroluj tabuľku transakcií pre finálny stav.");
            setSending(false);
          }
        };
        setTimeout(poll, 3000);
        return; // keep `sending` true until polling ends
      }
      // Donation flow (or missing task_id) — legacy behavior.
      setSuccess(`Odoslané na: ${r.data.sent_to || targetEmail}`);
      setTimeout(() => onSent(), 1200);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Neznáma chyba");
    } finally {
      if (isDonation) setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="resend-modal"
    >
      <div
        className="w-full max-w-lg bg-zinc-950 border border-zinc-800 shadow-2xl my-auto max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Send className="w-4 h-4 text-gold-400" />
            <h3 className="font-display font-bold text-sm">
              {isDonation ? "Poďakovanie + faktúra" : "Faktúra"} — {tx.invoice_email_sent ? "Preposlať" : "Odoslať"}
            </h3>
          </div>
          <button onClick={onClose} data-testid="resend-modal-close" className="text-zinc-500 hover:text-white p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Invoice detail panel */}
          <div className="border border-zinc-800 bg-zinc-900/40 p-3 text-xs space-y-1.5 font-mono">
            <div className="flex justify-between"><span className="text-zinc-500">Session ID</span><span className="text-zinc-300 truncate ml-2 max-w-[260px]" title={tx.session_id}>{tx.session_id}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Dátum platby</span><span className="text-zinc-300">{fmt(tx.created_at)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Pôvodný email</span><span className="text-zinc-300">{tx.user_email || "—"}</span></div>
            {tx.user_name && <div className="flex justify-between"><span className="text-zinc-500">Meno</span><span className="text-zinc-300">{tx.user_name}</span></div>}
            <div className="flex justify-between"><span className="text-zinc-500">Plán</span><span className="text-zinc-300">{tx.plan}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Suma</span><span className="font-display font-bold text-base text-gold-400">€{tx.amount}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Status platby</span>
              <span className={tx.payment_status === "paid" ? "text-green-400" : tx.payment_status === "expired" ? "text-red-400" : "text-amber-400"}>{tx.payment_status}</span>
            </div>
            {tx.invoice_email_sent && (
              <>
                <div className="border-t border-zinc-800 my-2" />
                <div className="flex justify-between"><span className="text-zinc-500">Posledné odoslanie</span><span className="text-green-400">{fmt(tx.invoice_last_sent_at)}</span></div>
                {tx.invoice_last_sent_to && (
                  <div className="flex justify-between"><span className="text-zinc-500">Odoslané na</span><span className="text-zinc-300">{tx.invoice_last_sent_to}</span></div>
                )}
              </>
            )}
          </div>

          {/* Override email input */}
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">
              Cieľový email
            </label>
            <input
              data-testid="resend-modal-email"
              type="email"
              value={overrideEmail}
              onChange={(e) => setOverrideEmail(e.target.value)}
              className="wb-input w-full px-3 py-2 text-sm font-mono"
              placeholder="napr. klient@example.com"
              disabled={sending}
            />
            <p className="text-[10px] text-zinc-500 mt-1 font-mono">
              Predvolený je pôvodný email klienta. Zmeň ho ak klient potrebuje faktúru poslať inam.
            </p>
          </div>

          {error && (
            <div className="border-l-2 border-red-500 bg-red-500/5 px-3 py-2 text-xs text-red-300 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="border-l-2 border-green-500 bg-green-500/5 px-3 py-2 text-xs text-green-300 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-800 flex-wrap">
            {isInitiated && (
              <button
                data-testid="resend-modal-mark-paid"
                onClick={markPaid}
                disabled={marking || sending || !!success}
                className="px-4 py-2 text-xs inline-flex items-center gap-1.5 border border-green-700/60 bg-green-950/30 text-green-300 hover:bg-green-900/40 disabled:opacity-50"
                title="Označiť platbu ako uhradenú v účtovníctve (manuálne / mimo Stripe)"
              >
                {marking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                {marking ? "Označujem…" : "Označiť ako zaplatené"}
              </button>
            )}
            <button onClick={onClose} disabled={sending} className="wb-btn-ghost px-4 py-2 text-xs">Zrušiť</button>
            {!isDonation && !isInitiated && tx.plan !== "job_ad" && (
              <button
                data-testid="resend-modal-send-activation"
                onClick={sendActivation}
                disabled={sending || !!success}
                className="px-4 py-2 text-xs inline-flex items-center gap-1.5 border border-amber-600/60 bg-amber-950/30 text-amber-200 hover:bg-amber-900/40 disabled:opacity-50"
                title="Pošle luxusné 'PRO aktivované' vítacie oznámenie (nie faktúru)"
              >
                <Send className="w-3.5 h-3.5" />
                Poslať PRO welcome
              </button>
            )}
            <button
              data-testid="resend-modal-send"
              onClick={send}
              disabled={sending || !!success}
              className="wb-btn-primary px-4 py-2 text-xs inline-flex items-center gap-1.5"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {sending ? "Odosielam…" : (tx.invoice_email_sent ? "Preposlať" : "Odoslať")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============= INVOICE TEMPLATES TAB =============
function InvoiceTemplatesTab() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [draft, setDraft] = useState({ subject: "", salutation: "", intro: "" });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testRecipient, setTestRecipient] = useState("");
  const [testSending, setTestSending] = useState(false);

  // REAL send (production) — to actual customers
  const [realOpen, setRealOpen] = useState(false);
  const [realForm, setRealForm] = useState({
    to: "", customer_name: "", amount: "",
    plan_label: "", description: "", bonus_days: "",
    valid_until_iso: "", certified_since_iso: "", certificate_no: "", lang: "sk",
  });
  const [realSending, setRealSending] = useState(false);
  const [realPreviewing, setRealPreviewing] = useState(false);
  const [realPreviewHtml, setRealPreviewHtml] = useState(null);   // inline modal (Iteration 72)

  const [toast, setToast] = useState(null); // {type, msg}

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const load = async () => {
    const r = await api.get("/admin/invoice-templates");
    setTemplates(r.data);
    if (!activeKey && r.data.length) {
      setActiveKey(r.data[0].template_key);
      setDraft(r.data[0].current);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const active = templates.find((t) => t.template_key === activeKey);
  const isHoF = activeKey === "hof_welcome";
  const isCertInvoice = activeKey === "cert_service";
  const docNoun = isHoF ? "gratuláciu" : "faktúru";
  const docNounNom = isHoF ? "gratulácia" : "faktúra";
  const ctaLabel = isHoF ? "Odoslať gratuláciu klientovi" : "Odoslať klientovi";

  useEffect(() => {
    if (active) {
      setDraft({ ...active.current });
      setPreviewHtml(null);
    }
    // eslint-disable-next-line
  }, [activeKey]);

  useEffect(() => {
    if (user?.email && !testRecipient) setTestRecipient(user.email);
  }, [user?.email, testRecipient]);

  const isDirty = active && (
    draft.subject !== active.current.subject ||
    draft.salutation !== active.current.salutation ||
    draft.intro !== active.current.intro
  );

  const save = async () => {
    if (!activeKey) return;
    setSaving(true);
    try {
      await api.put(`/admin/invoice-templates/${activeKey}`, draft);
      await load();
      showToast("success", "Šablóna uložená — všetky budúce faktúry použijú nový text.");
    } catch (e) {
      showToast("error", e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!activeKey || !window.confirm("Naozaj resetovať šablónu na pôvodný text?")) return;
    setResetting(true);
    try {
      await api.post(`/admin/invoice-templates/${activeKey}/reset`);
      await load();
      showToast("success", "Šablóna resetnutá na pôvodný text.");
    } catch (e) {
      showToast("error", e.response?.data?.detail || e.message);
    } finally {
      setResetting(false);
    }
  };

  const showPreview = async () => {
    if (!activeKey) return;
    setPreviewLoading(true);
    try {
      const r = await api.post(`/admin/invoice-templates/${activeKey}/preview`, draft);
      setPreviewHtml(r.data.html);
    } catch (e) {
      showToast("error", e.response?.data?.detail || e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendTest = async () => {
    if (!activeKey) return;
    const to = (testRecipient || "").trim();
    if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      showToast("error", "Zadaj platný email pre testovacie odoslanie.");
      return;
    }
    if (!window.confirm(`Skutočne odoslať testovaciu faktúru na ${to}?`)) return;
    setTestSending(true);
    try {
      const r = await api.post(`/admin/invoice-templates/${activeKey}/send-test`, {
        to,
        sample_name: user?.name || "Martin",
        ...draft,
      });
      showToast("success", `Testovacia faktúra odoslaná na ${r.data.sent_to}.`);
    } catch (e) {
      showToast("error", e.response?.data?.detail || e.message);
    } finally {
      setTestSending(false);
    }
  };

  // ---- REAL SEND (production) ----
  const buildRealPayload = () => ({
    to: realForm.to.trim(),
    customer_name: realForm.customer_name.trim(),
    amount: realForm.amount === "" ? null : Number(realForm.amount),
    plan_label: realForm.plan_label.trim() || null,
    description: realForm.description.trim() || null,
    bonus_days: realForm.bonus_days === "" ? null : Number(realForm.bonus_days),
    valid_until_iso: realForm.valid_until_iso || null,
    certified_since_iso: realForm.certified_since_iso || null,
    certificate_no: realForm.certificate_no.trim() || null,
    lang: realForm.lang || "sk",
    ...draft,
  });

  const previewReal = async () => {
    if (!activeKey) return;
    if (!realForm.customer_name.trim()) {
      showToast("error", "Vyplň meno klienta pre náhľad.");
      return;
    }
    setRealPreviewing(true);
    try {
      const r = await api.post(`/admin/invoice-templates/${activeKey}/preview-real`, buildRealPayload());
      setRealPreviewHtml(r.data.html);   // inline modal — user can close it easily
    } catch (e) {
      showToast("error", e.response?.data?.detail || e.message);
    } finally {
      setRealPreviewing(false);
    }
  };

  const sendReal = async () => {
    if (!activeKey) return;
    const to = realForm.to.trim();
    const name = realForm.customer_name.trim();
    if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      showToast("error", "Zadaj platný email klienta.");
      return;
    }
    if (!name || name.length < 2) {
      showToast("error", "Zadaj meno klienta.");
      return;
    }
    if (!window.confirm(`POZOR — naozaj odoslať REÁLNY dokument klientovi ${name} <${to}>?\n\nTáto operácia nie je revokovateľná.`)) return;
    setRealSending(true);
    try {
      const r = await api.post(`/admin/invoice-templates/${activeKey}/send-real`, buildRealPayload());
      showToast("success", `✅ Reálny dokument odoslaný klientovi ${r.data.sent_to}`);
      // Reset only customer-specific fields
      setRealForm({ to: "", customer_name: "", amount: "", plan_label: "", description: "", bonus_days: "", valid_until_iso: "", certified_since_iso: "", certificate_no: "" });
      setRealOpen(false);
    } catch (e) {
      showToast("error", e.response?.data?.detail || e.message);
    } finally {
      setRealSending(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-invoices">
      {/* Header */}
      <div className="border border-zinc-800 bg-zinc-900/40 p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-gold-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="font-display font-bold text-lg">Šablóny faktúr a potvrdení</h2>
            <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
              Tu vidíš presne, ako vyzerajú emaily / faktúry, ktoré tvoji zákazníci dostávajú. Môžeš upraviť
              <strong className="text-gold-400"> predmet emailu</strong>, <strong className="text-gold-400">oslovenie</strong> a
              <strong className="text-gold-400"> úvodný text</strong>. Údaje SauberEko (IČO, IČ DPH, adresa) a rozpis DPH 23 % zostávajú
              automaticky predvyplnené v päte. <span className="text-zinc-600">Zmeny sa použijú na všetky budúce odoslania.</span>
            </p>
            <p className="text-[10px] text-zinc-600 mt-2 font-mono">
              PLACEHOLDERY: <code className="text-gold-400">{"{name}"}</code> · <code className="text-gold-400">{"{amount}"}</code> · <code className="text-gold-400">{"{plan_label}"}</code> · <code className="text-gold-400">{"{valid_until}"}</code>
            </p>
          </div>
        </div>
      </div>

      {/* Template chips */}
      <div className="flex gap-1.5 flex-wrap" data-testid="invoice-template-list">
        {templates.map((t) => (
          <button
            key={t.template_key}
            data-testid={`invoice-tpl-${t.template_key}`}
            onClick={() => setActiveKey(t.template_key)}
            className={`px-3 py-2 text-xs font-mono uppercase tracking-wider border inline-flex items-center gap-2 transition-colors ${
              activeKey === t.template_key
                ? "border-gold-500 text-gold-400 bg-gold-500/5"
                : "border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-700"
            }`}
          >
            {t.name}
            {t.has_override && (
              <span className="w-1.5 h-1.5 rounded-full bg-gold-400" title="Upravené" />
            )}
          </button>
        ))}
      </div>

      {active && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* LEFT — editor */}
          <div className="lg:col-span-2 space-y-3">
            <div className="border border-zinc-800 bg-zinc-900/30 p-4 space-y-3">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Predmet emailu</label>
                <input
                  data-testid="invoice-tpl-subject"
                  type="text"
                  value={draft.subject}
                  onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  className="wb-input w-full px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Oslovenie / Nadpis</label>
                <input
                  data-testid="invoice-tpl-salutation"
                  type="text"
                  value={draft.salutation}
                  onChange={(e) => setDraft({ ...draft, salutation: e.target.value })}
                  className="wb-input w-full px-3 py-2 text-sm"
                />
                <p className="text-[10px] text-zinc-600 mt-1 font-mono">Tip: použi <code className="text-gold-400">{"{name}"}</code> pre meno klienta</p>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Úvodný text (HTML povolený)</label>
                <textarea
                  data-testid="invoice-tpl-intro"
                  value={draft.intro}
                  onChange={(e) => setDraft({ ...draft, intro: e.target.value })}
                  rows={9}
                  className="wb-input w-full px-3 py-2 text-sm font-mono leading-relaxed"
                />
              </div>

              <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-800">
                <button
                  data-testid="invoice-tpl-preview"
                  onClick={showPreview}
                  disabled={previewLoading}
                  className="wb-btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1.5"
                >
                  {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                  Náhľad
                </button>
                <button
                  data-testid="invoice-tpl-save"
                  onClick={save}
                  disabled={saving || !isDirty}
                  className="wb-btn-primary px-3 py-2 text-xs inline-flex items-center gap-1.5"
                  title={!isDirty ? "Žiadne zmeny" : "Uložiť pre budúce odoslania"}
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  Uložiť šablónu
                </button>
                {active.has_override && (
                  <button
                    data-testid="invoice-tpl-reset"
                    onClick={resetToDefault}
                    disabled={resetting}
                    className="wb-btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1.5 !border-red-900 !text-red-400 hover:!bg-red-900/20"
                  >
                    {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                    Reset na default
                  </button>
                )}
              </div>
            </div>

            {/* Test send */}
            <div className="border border-zinc-800 bg-zinc-900/30 p-4 space-y-2">
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Otestuj — pošli si vzorovú {docNounNom}</div>
              <div className="flex gap-2">
                <input
                  data-testid="invoice-tpl-test-email"
                  type="email"
                  value={testRecipient}
                  onChange={(e) => setTestRecipient(e.target.value)}
                  placeholder="napr. tvoj@email.com"
                  className="wb-input flex-1 px-3 py-2 text-sm font-mono"
                />
                <button
                  data-testid="invoice-tpl-test-send"
                  onClick={sendTest}
                  disabled={testSending}
                  className="wb-btn-primary px-4 py-2 text-xs inline-flex items-center gap-1.5 whitespace-nowrap"
                >
                  {testSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Poslať test
                </button>
              </div>
              <p className="text-[10px] text-zinc-600 leading-relaxed">
                {isHoF
                  ? "Test pošle vzorovú gratuláciu do Siene Slávy s PDF diplomom — len s ukážkovým menom. Predmet bude mať prefix "
                  : "Test používa rovnaké údaje ako reálna faktúra (suma, plán, IČO, DPH 23 %) — len s ukážkovým menom a sumou. Predmet bude mať prefix "}
                <code className="text-gold-400">[TEST]</code>.
              </p>
            </div>

            {/* REAL send — production */}
            <div className="border-2 border-gold-700/50 bg-gold-950/10 p-4 space-y-3">
              <button
                type="button"
                onClick={() => setRealOpen(!realOpen)}
                className="w-full text-left flex items-center justify-between gap-2"
                data-testid="invoice-tpl-real-toggle"
              >
                <div>
                  <div className="text-xs font-mono uppercase tracking-wider text-gold-300 inline-flex items-center gap-2">
                    <Send className="w-3 h-3" /> {isHoF ? "Pošli gratuláciu klientovi (REÁLNY email + PDF diplom)" : "Pošli REÁLNY dokument klientovi"}
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-0.5">
                    {isHoF
                      ? "Uvedenie do Siene Slávy — bielo-zlatý email + PDF diplom v zvolenom jazyku."
                      : "Použiť na: ďakovný certifikát darcovi, faktúra mimo Stripe, ručné odoslanie potvrdenia."}
                  </p>
                </div>
                <ChevronRight className={`w-4 h-4 text-gold-400 transition-transform ${realOpen ? "rotate-90" : ""}`} />
              </button>

              {realOpen && (
                <div className="space-y-3 pt-2 border-t border-gold-700/30">
                  <div className="border border-orange-700/40 bg-orange-950/20 p-2 text-[10px] text-orange-300 font-mono">
                    ⚠️ Toto NIE je test — email pôjde priamo klientovi BEZ prefixu [TEST]. Použi „Náhľad" pred odoslaním.
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">E-mail klienta *</label>
                      <input
                        type="email"
                        value={realForm.to}
                        onChange={(e) => setRealForm({ ...realForm, to: e.target.value })}
                        placeholder="napr. jiri@email.cz"
                        className="wb-input w-full px-2 py-1.5 text-xs font-mono"
                        data-testid="invoice-tpl-real-to"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Meno klienta *</label>
                      <input
                        value={realForm.customer_name}
                        onChange={(e) => setRealForm({ ...realForm, customer_name: e.target.value })}
                        placeholder="napr. Jiří Dalekorej"
                        className="wb-input w-full px-2 py-1.5 text-xs"
                        data-testid="invoice-tpl-real-name"
                      />
                    </div>
                  </div>
                  {!isHoF && (
                  <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Suma (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={realForm.amount}
                        onChange={(e) => setRealForm({ ...realForm, amount: e.target.value })}
                        placeholder="napr. 15.00"
                        className="wb-input w-full px-2 py-1.5 text-xs font-mono"
                        data-testid="invoice-tpl-real-amount"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Bonus dni (voliteľné)</label>
                      <input
                        type="number"
                        min="0"
                        value={realForm.bonus_days}
                        onChange={(e) => setRealForm({ ...realForm, bonus_days: e.target.value })}
                        placeholder="napr. 30"
                        className="wb-input w-full px-2 py-1.5 text-xs font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Plán / názov (voliteľné)</label>
                    <input
                      value={realForm.plan_label}
                      onChange={(e) => setRealForm({ ...realForm, plan_label: e.target.value })}
                      placeholder="napr. Podpora Tigik"
                      className="wb-input w-full px-2 py-1.5 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Popis / popisok (voliteľné)</label>
                    <textarea
                      value={realForm.description}
                      onChange={(e) => setRealForm({ ...realForm, description: e.target.value })}
                      rows={2}
                      placeholder="Napr.: Ďakujeme za podporu rozvoja Tigik vo výške 15 EUR."
                      className="wb-input w-full px-2 py-1.5 text-xs"
                    />
                  </div>
                  </>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Č. certifikátu (voliteľné)</label>
                      <input
                        value={realForm.certificate_no}
                        onChange={(e) => setRealForm({ ...realForm, certificate_no: e.target.value })}
                      placeholder={isHoF ? "napr. Sa.Eko.Pre.2026-0011" : "napr. Sa.Eko.Don.2026-0001"}
                        className="wb-input w-full px-2 py-1.5 text-xs font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">{isHoF ? "Platnosť do (voliteľné)" : "Platnosť do (voliteľné)"}</label>
                      <input
                        type="date"
                        value={realForm.valid_until_iso ? realForm.valid_until_iso.split("T")[0] : ""}
                        onChange={(e) => setRealForm({ ...realForm, valid_until_iso: e.target.value })}
                        className="wb-input w-full px-2 py-1.5 text-xs font-mono"
                      />
                    </div>
                  </div>
                  {isHoF && (
                    <div>
                      <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
                        Certifikovaný od <span className="text-gold-500">(manuálne)</span>
                      </label>
                      <input
                        type="date"
                        value={realForm.certified_since_iso ? realForm.certified_since_iso.split("T")[0] : ""}
                        onChange={(e) => setRealForm({ ...realForm, certified_since_iso: e.target.value })}
                        className="wb-input w-full px-2 py-1.5 text-xs font-mono"
                        data-testid="invoice-tpl-real-certified-since"
                      />
                      <p className="text-[10px] text-zinc-600 mt-1">
                        Dátum uvedenia do Siene slávy (zobrazí sa v gratulácii/diplome). Ak nevyplníš, použije sa dnešný.
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
                      Jazyk emailu + PDF <span className="text-amber-400">★</span>
                    </label>
                    <select
                      value={realForm.lang || "sk"}
                      onChange={(e) => setRealForm({ ...realForm, lang: e.target.value })}
                      className="wb-input w-full px-2 py-1.5 text-xs"
                      data-testid="invoice-tpl-real-lang"
                    >
                      <option value="sk">🇸🇰 Slovenčina</option>
                      <option value="cs">🇨🇿 Čeština</option>
                      <option value="en">🇬🇧 English</option>
                      <option value="de">🇩🇪 Deutsch</option>
                      <option value="pl">🇵🇱 Polski</option>
                    </select>
                    <p className="text-[10px] text-zinc-600 mt-1">
                      Email aj PDF príloha budú v zvolenom jazyku. Tip: vyber jazyk podľa krajiny klienta.
                    </p>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button
                      type="button"
                      onClick={previewReal}
                      disabled={realPreviewing}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-3 py-2 border border-zinc-700 text-zinc-300 hover:border-zinc-500 disabled:opacity-50"
                      data-testid="invoice-tpl-real-preview"
                    >
                      {realPreviewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                      Náhľad
                    </button>
                    <button
                      type="button"
                      onClick={sendReal}
                      disabled={realSending}
                      className="flex-1 wb-btn-primary px-3 py-2 text-[11px] font-mono uppercase tracking-wider inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                      data-testid="invoice-tpl-real-send"
                    >
                      {realSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      {ctaLabel}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — preview */}
          <div className="lg:col-span-3">
            <div className="border border-zinc-800 bg-black sticky top-4">
              <div className="px-4 py-2 border-b border-zinc-800 flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 inline-flex items-center gap-2">
                  <Eye className="w-3.5 h-3.5" /> Fyzický náhľad — presne ako to klient uvidí v Gmaile / Outlooku
                </div>
                {!previewHtml && (
                  <button
                    onClick={showPreview}
                    disabled={previewLoading}
                    className="text-[10px] text-gold-400 hover:underline font-mono uppercase"
                  >
                    Načítať
                  </button>
                )}
              </div>
              {previewHtml ? (
                <iframe
                  data-testid="invoice-tpl-iframe"
                  title="Náhľad faktúry"
                  srcDoc={previewHtml}
                  sandbox=""
                  className="w-full h-[720px] bg-black"
                />
              ) : (
                <div className="h-[300px] flex items-center justify-center text-zinc-600 text-xs font-mono">
                  {previewLoading ? "Renderujem..." : `Klikni „Náhľad" pre zobrazenie`}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Real-preview modal — inline (no popup), always closable via X or Esc */}
      {realPreviewHtml && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setRealPreviewHtml(null); }}
          data-testid="invoice-tpl-real-preview-modal"
        >
          <div className="w-full max-w-4xl h-[90vh] bg-black border border-gold-700/40 flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800">
              <div className="text-[11px] font-mono uppercase tracking-wider text-gold-400 inline-flex items-center gap-2">
                <Eye className="w-3 h-3" /> Náhľad emailu (klient nedostane)
              </div>
              <button
                onClick={() => setRealPreviewHtml(null)}
                className="inline-flex items-center gap-1.5 px-3 py-1 border border-zinc-700 text-zinc-300 hover:border-gold-500 hover:text-gold-300 text-[11px] font-mono uppercase tracking-wider"
                data-testid="invoice-tpl-real-preview-close"
              >
                <X className="w-3 h-3" /> Zavrieť náhľad
              </button>
            </div>
            <iframe title="Real preview" srcDoc={realPreviewHtml} sandbox="" className="flex-1 w-full bg-white" />
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          data-testid="invoice-tpl-toast"
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 border-l-2 text-xs font-mono max-w-md shadow-2xl ${
            toast.type === "success"
              ? "border-green-500 bg-green-500/10 text-green-300"
              : "border-red-500 bg-red-500/10 text-red-300"
          }`}
        >
          <div className="flex items-start gap-2">
            {toast.type === "success" ? <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
            <span>{toast.msg}</span>
          </div>
        </div>
      )}
    </div>
  );
}



function ChatModeration() {
  const [channel, setChannel] = useState("");
  const [msgs, setMsgs] = useState([]);

  const load = async () => {
    const r = await api.get("/admin/chat/recent", { params: channel ? { channel } : {} });
    setMsgs(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [channel]);

  const del = async (id) => {
    if (!window.confirm("Zmazať túto správu?")) return;
    await api.delete(`/chat/messages/${id}`); load();
  };

  // Real channels from backend (CHANNELS in server.py)
  const channels = useMemo(() => ([
    { id: "",            label: "Všetky",          color: "border-gold-500 text-gold-400" },
    { id: "tig",         label: "Ručný TIG",       color: "border-orange-700 text-orange-400" },
    { id: "orbital",     label: "Orbital",         color: "border-blue-700 text-blue-400" },
    { id: "pipefitters", label: "Potrubári",       color: "border-cyan-700 text-cyan-400" },
    { id: "prep",        label: "Prípravári",      color: "border-green-700 text-green-400" },
    { id: "others",      label: "Ostatné profesie", color: "border-purple-700 text-purple-400" },
  ]), []);

  const CHANNEL_META = {
    tig:         { label: "Ručný TIG",        color: "!border-orange-700 !text-orange-400" },
    orbital:     { label: "Orbital",          color: "!border-blue-700 !text-blue-400" },
    pipefitters: { label: "Potrubári",        color: "!border-cyan-700 !text-cyan-400" },
    prep:        { label: "Prípravári",       color: "!border-green-700 !text-green-400" },
    others:      { label: "Ostatné profesie", color: "!border-purple-700 !text-purple-400" },
  };

  // Group messages by channel when viewing "All"
  const grouped = useMemo(() => {
    if (channel) return null; // not grouping when single channel selected
    const map = {};
    for (const m of msgs) {
      const ch = m.channel || "unknown";
      if (!map[ch]) map[ch] = [];
      map[ch].push(m);
    }
    // Sort channels by order in CHANNEL_META
    const order = ["tig", "orbital", "pipefitters", "prep", "others"];
    return order.filter(ch => map[ch]).map(ch => ({ channel: ch, msgs: map[ch] }))
      .concat(Object.keys(map).filter(ch => !order.includes(ch)).map(ch => ({ channel: ch, msgs: map[ch] })));
  }, [msgs, channel]);

  const renderMessage = (m) => (
    <div key={m.id} className="p-3 flex items-start gap-3 hover:bg-zinc-900/40 group" data-testid={`admin-msg-${m.id}`}>
      <div className="text-[10px] font-mono text-zinc-600 w-24 shrink-0 leading-tight">
        {new Date(m.ts).toLocaleString("sk-SK", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs flex items-center gap-1.5 flex-wrap">
          {channel === "" && (
            <span className={`wb-tag ${CHANNEL_META[m.channel]?.color || ""}`}>
              {CHANNEL_META[m.channel]?.label || m.channel}
            </span>
          )}
          <span className="font-display font-bold">{m.user_name}</span>
        </div>
        <div className="text-sm text-zinc-200 mt-1 break-words whitespace-pre-wrap">{m.text}</div>
      </div>
      <button onClick={() => del(m.id)} data-testid={`admin-msg-${m.id}-delete`}
        className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="admin-chat">
      {/* Channel filter chips */}
      <div className="flex gap-1.5 text-xs font-mono uppercase flex-wrap">
        {channels.map((c) => {
          const count = c.id === "" ? msgs.length : msgs.filter(m => m.channel === c.id).length;
          const active = channel === c.id;
          return (
            <button key={c.id || "all"} onClick={() => setChannel(c.id)}
              data-testid={`mod-channel-${c.id || "all"}`}
              className={`px-3 py-1.5 border inline-flex items-center gap-2 ${active ? c.color : "border-zinc-800 text-zinc-500 hover:text-white"}`}>
              {c.label}
              <span className={`px-1.5 py-0 text-[9px] rounded ${active ? "bg-zinc-900" : "bg-zinc-800/60"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      <p className="text-[10px] font-mono text-zinc-600 uppercase tracking-wider">
        Zobrazené je posledných 200 správ. Klikni na kanál pre filter alebo zostávaj na &quot;Všetky&quot; pre prehľad zoskupený podľa kanálu.
      </p>

      {/* Messages — single channel view */}
      {channel !== "" && (
        <div className="border border-zinc-800 divide-y divide-zinc-800">
          {msgs.length === 0 && <div className="p-6 text-center text-zinc-500 text-sm">Žiadne správy v tomto kanáli</div>}
          {msgs.map(renderMessage)}
        </div>
      )}

      {/* Messages — grouped view */}
      {channel === "" && grouped && grouped.length === 0 && (
        <div className="border border-zinc-800 p-6 text-center text-zinc-500 text-sm">Žiadne správy</div>
      )}
      {channel === "" && grouped && grouped.map(({ channel: ch, msgs: chMsgs }) => (
        <div key={ch} className="border border-zinc-800" data-testid={`mod-group-${ch}`}>
          <div className={`px-3 py-2 bg-zinc-900/50 border-b border-zinc-800 flex items-center justify-between`}>
            <div className="flex items-center gap-2">
              <span className={`wb-tag ${CHANNEL_META[ch]?.color || ""}`}>{CHANNEL_META[ch]?.label || ch}</span>
              <span className="text-xs font-mono text-zinc-500">{chMsgs.length} správ</span>
            </div>
            <button
              onClick={() => setChannel(ch)}
              className="text-[10px] font-mono uppercase text-zinc-500 hover:text-white"
              data-testid={`mod-jump-${ch}`}
            >
              Otvoriť kanál →
            </button>
          </div>
          <div className="divide-y divide-zinc-800">
            {chMsgs.map(renderMessage)}
          </div>
        </div>
      ))}
    </div>
  );
}

const FEEDBACK_STATUS = {
  new:         { label: "Nový",       color: "!text-amber-400 !border-amber-700" },
  in_progress: { label: "Riešim",     color: "!text-blue-400 !border-blue-700" },
  done:        { label: "Hotovo",     color: "!text-green-400 !border-green-700" },
  wontfix:     { label: "Zamietnuté", color: "!text-zinc-400 !border-zinc-700" },
};

const FEEDBACK_CATEGORY = {
  idea: "💡 Nápad", bug: "🐞 Bug", praise: "❤️ Pochvala", other: "✉️ Iné",
};

function FeedbackTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);

  const load = () => {
    const q = filter === "all" ? "" : `?status_filter=${filter}`;
    api.get(`/admin/feedback${q}`).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status) => {
    setBusy(id);
    try {
      await api.patch(`/admin/feedback/${id}`, { status });
      load();
    } finally { setBusy(null); }
  };

  const delItem = async (id) => {
    if (!confirm("Naozaj zmazať tento záznam?")) return;
    setBusy(id);
    try {
      await api.delete(`/admin/feedback/${id}`);
      load();
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="feedback-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display font-bold text-lg flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-yellow-500" /> Schránka nápadov ({items.length})
        </h2>
        <div className="flex gap-2 flex-wrap text-xs">
          {["all", "new", "in_progress", "done", "wontfix"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`feedback-filter-${s}`}
              className={`px-3 py-1.5 border font-mono uppercase tracking-wider ${
                filter === s ? "border-yellow-500 text-yellow-400" : "border-zinc-700 text-zinc-500 hover:text-white"
              }`}
            >
              {s === "all" ? "Všetko" : FEEDBACK_STATUS[s].label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {items.length === 0 && (
          <div className="text-center text-sm text-zinc-500 py-12 border border-dashed border-zinc-800">
            Žiadne nápady v tejto kategórii
          </div>
        )}
        {items.map((fb) => (
          <div key={fb.id} className="border border-zinc-800 bg-[var(--wb-surface)] p-4" data-testid={`feedback-${fb.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                  {FEEDBACK_CATEGORY[fb.category] || fb.category}
                </span>
                <span className={`wb-tag ${FEEDBACK_STATUS[fb.status]?.color || ""}`}>
                  {FEEDBACK_STATUS[fb.status]?.label || fb.status}
                </span>
              </div>
              <span className="text-[10px] font-mono text-zinc-600 whitespace-nowrap">
                {new Date(fb.created_at).toLocaleString()}
              </span>
            </div>

            <p className="text-sm text-zinc-200 whitespace-pre-wrap mb-3 leading-relaxed">{fb.message}</p>

            <div className="flex items-center justify-between gap-3 text-xs flex-wrap pt-2 border-t border-zinc-800/60">
              <div className="text-zinc-500 font-mono">
                <span className="text-zinc-400">{fb.user_name}</span> · {fb.user_email}
                {fb.page && <span className="text-zinc-600"> · {fb.page}</span>}
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {["new", "in_progress", "done", "wontfix"].map((s) => (
                  <button
                    key={s}
                    disabled={busy === fb.id || fb.status === s}
                    onClick={() => updateStatus(fb.id, s)}
                    data-testid={`feedback-${fb.id}-set-${s}`}
                    className={`text-[10px] px-2 py-1 border font-mono ${
                      fb.status === s
                        ? `${FEEDBACK_STATUS[s].color} cursor-default`
                        : "border-zinc-700 hover:border-yellow-500 text-zinc-400 hover:text-yellow-400"
                    }`}
                  >
                    {FEEDBACK_STATUS[s].label}
                  </button>
                ))}
                <button
                  disabled={busy === fb.id}
                  onClick={() => delItem(fb.id)}
                  data-testid={`feedback-${fb.id}-delete`}
                  className="text-[10px] px-2 py-1 border border-red-800 text-red-400 hover:bg-red-900/30 font-mono"
                >
                  Zmazať
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function PendingActionsTab() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null); // pid being processed
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/admin/pending-actions");
      setItems(r.data.items || []);
    } catch { /* */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, []);

  const decide = async (pid, ok) => {
    setBusy(pid);
    try {
      if (ok) {
        await api.post(`/admin/pending-actions/${pid}/approve`);
      } else {
        const reason = window.prompt("Dôvod zamietnutia (voliteľne):", "") || "";
        await api.post(`/admin/pending-actions/${pid}/reject`, { reason });
      }
      await load();
    } catch (e) {
      window.alert(e.response?.data?.detail || "Chyba pri spracovaní");
    } finally { setBusy(null); }
  };

  const actionLabels = {
    cert_approve: "Schválenie certifikátu (PRO doživotne)",
    cert_reject: "Zamietnutie certifikátu",
    delete_chat_message: "Vymazanie chat správy",
    ban_user: "Zablokovanie užívateľa",
    delete_user: "Vymazanie užívateľa",
    bulk_delete_users: "Hromadné vymazanie užívateľov",
    update_user: "Úprava užívateľa (role/plan/extend/...)",
  };

  if (loading) return <div className="text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline" /> Načítavam...</div>;

  return (
    <div data-testid="admin-pending-tab">
      <div className="mb-5">
        <h2 className="font-display font-black text-2xl">Pending akcie od Vedúcka</h2>
        <p className="text-xs text-zinc-500 mt-1">Vedúcko môže klikať v Admin panele, ale jeho zmeny sa najprv ukážu tu na schválenie.</p>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-zinc-600 italic">Žiadne čakajúce akcie. 👌</div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} className="border border-gold-700/40 bg-zinc-950 p-4" data-testid={`pending-item-${it.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="wb-tag text-gold-400 border-gold-600">{actionLabels[it.action] || it.action}</span>
                    <span className="text-[10px] font-mono text-zinc-500">{new Date(it.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-xs text-zinc-400 mt-1">
                    Žiada: <strong className="text-white">{it.actor_name}</strong>
                  </div>
                  <div className="bg-black border border-zinc-800 p-2 mt-2 text-[11px] font-mono text-zinc-300 whitespace-pre-wrap break-words">
                    {JSON.stringify(it.params, null, 2)}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => decide(it.id, true)} disabled={busy === it.id}
                  data-testid={`pending-approve-${it.id}`}
                  className="wb-btn-primary px-4 py-2 text-xs inline-flex items-center gap-1 disabled:opacity-50">
                  {busy === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                  Schváliť & vykonať
                </button>
                <button onClick={() => decide(it.id, false)} disabled={busy === it.id}
                  data-testid={`pending-reject-${it.id}`}
                  className="border border-red-700 text-red-400 hover:bg-red-950/40 px-4 py-2 text-xs inline-flex items-center gap-1 disabled:opacity-50">
                  <X className="w-3 h-3" /> Zamietnuť
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



function CertPhotoUploader({ userId, initialPhoto }) {
  const [photo, setPhoto] = useState(initialPhoto || null);
  const [busy, setBusy] = useState(false);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4_500_000) { alert("Príliš veľký obrázok (max 4.5 MB)."); return; }
    const r = new FileReader();
    r.onload = async () => {
      const dataUrl = r.result;
      setBusy(true);
      try {
        await api.post(`/admin/users/${userId}/certificate-photo`, { photo_base64: dataUrl });
        setPhoto(dataUrl);
      } catch (err) {
        alert("Nepodarilo sa nahrať: " + (err?.response?.data?.detail || err.message));
      } finally { setBusy(false); }
    };
    r.readAsDataURL(file);
  };

  const removePhoto = async () => {
    if (!window.confirm("Zmazať fotku certifikátu zo siene slávy?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${userId}/certificate-photo`);
      setPhoto(null);
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 ml-6 border-t border-zinc-800/60 pt-3">
      <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">
        Fotka fyzického certifikátu (Sieň slávy)
      </label>
      {photo ? (
        <div className="space-y-2">
          <img src={photo} alt="Certificate" className="w-full max-h-48 object-contain border border-gold-700/30 bg-black" data-testid="admin-cert-photo-preview" />
          <div className="flex gap-2">
            <label className="cursor-pointer text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-zinc-500">
              Vymeniť
              <input type="file" accept="image/*" onChange={handleFile} className="hidden" disabled={busy} data-testid="admin-cert-photo-replace" />
            </label>
            <button onClick={removePhoto} disabled={busy} className="text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 border border-red-700/50 text-red-400 hover:bg-red-950/30" data-testid="admin-cert-photo-delete">
              Zmazať
            </button>
          </div>
        </div>
      ) : (
        <label className="cursor-pointer inline-block">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-3 py-2 border border-gold-700/40 text-gold-400 hover:bg-gold-900/20">
            {busy ? "Nahrávam…" : "Nahrať fotku certifikátu"}
          </span>
          <input type="file" accept="image/*" onChange={handleFile} className="hidden" disabled={busy} data-testid="admin-cert-photo-upload" />
        </label>
      )}
    </div>
  );
}

// ============= CERTIFIED COMPANIES (admin) =============
function CompaniesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, "new" = new, object = edit
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/certified-companies");
      setItems(r.data?.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`Naozaj zmazať firmu „${c.name}“ zo siene slávy?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/certified-companies/${c.id}`);
      load();
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-companies-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold text-2xl text-white">Certifikované firmy</h2>
          <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-1">
            Sieň slávy → sekcia „Firmy“
          </p>
        </div>
        <button
          onClick={() => setEditing("new")}
          className="wb-btn-primary inline-flex items-center gap-2 px-4 py-2 text-xs"
          data-testid="admin-company-add"
        >
          <Plus className="w-3 h-3" /> Pridať firmu
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Načítavam…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border border-zinc-800 bg-zinc-950">
          <Building2 className="w-12 h-12 mx-auto text-zinc-700 mb-3" />
          <div className="text-zinc-400 text-sm">Zatiaľ žiadne certifikované firmy</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <div key={c.id} className="border border-zinc-800 bg-zinc-950 p-4" data-testid={`admin-company-${c.id}`}>
              <div className="flex items-start gap-3 mb-3">
                {c.logo_base64 ? (
                  <img src={c.logo_base64} alt={c.name} className="w-12 h-12 object-contain bg-white/5 border border-zinc-800" />
                ) : (
                  <div className="w-12 h-12 bg-gold-950/30 border border-gold-700/40 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-gold-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm text-white truncate">{c.name}</div>
                  <div className="text-[10px] font-mono text-zinc-500">{c.certificate_no || "—"}</div>
                </div>
              </div>
              {(c.city || c.country) && (
                <div className="text-xs text-zinc-400 mb-3">{[c.city, c.country].filter(Boolean).join(", ")}</div>
              )}
              <div className="flex gap-2">
                <button onClick={() => setEditing(c)} className="flex-1 text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-gold-500" data-testid={`admin-company-edit-${c.id}`}>
                  Upraviť
                </button>
                <button onClick={() => remove(c)} disabled={busy} className="text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 border border-red-700/50 text-red-400 hover:bg-red-950/30" data-testid={`admin-company-delete-${c.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <CompanyEditModal
          company={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function CompanyEditModal({ company, onClose, onSaved }) {
  const [name, setName] = useState(company?.name || "");
  const [certNo, setCertNo] = useState(company?.certificate_no || "");
  const [ico, setIco] = useState(company?.ico || "");
  const [country, setCountry] = useState(company?.country || "");
  const [city, setCity] = useState(company?.city || "");
  const [website, setWebsite] = useState(company?.website || "");
  const [description, setDescription] = useState(company?.description || "");
  const [logo, setLogo] = useState(company?.logo_base64 || "");
  const [certPhoto, setCertPhoto] = useState(company?.certificate_photo || "");
  const [certifiedSince, setCertifiedSince] = useState(
    company?.certified_since ? String(company.certified_since).split("T")[0] : ""
  );
  const [sector, setSector] = useState(company?.sector || "");   // "" | "industry" | "services"
  const [busy, setBusy] = useState(false);

  const handleFile = (setter, maxMB = 4.5) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxMB * 1_000_000) { alert(`Príliš veľký súbor (max ${maxMB} MB).`); return; }
    const r = new FileReader();
    r.onload = () => setter(r.result);
    r.readAsDataURL(file);
  };

  const save = async () => {
    if (!name.trim() || name.trim().length < 2) { alert("Názov firmy je povinný."); return; }
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        ico: ico.trim(),
        certificate_no: certNo.trim(),
        country: country.trim(),
        city: city.trim(),
        website: website.trim(),
        description: description.trim(),
        logo_base64: logo || null,
        certificate_photo: certPhoto || null,
        certified_since: certifiedSince ? new Date(certifiedSince + "T12:00:00Z").toISOString() : null,
        sector: sector || null,   // Iteration 73 — industry vs services
      };
      if (company?.id) {
        await api.patch(`/admin/certified-companies/${company.id}`, payload);
      } else {
        await api.post("/admin/certified-companies", payload);
      }
      onSaved();
    } catch (err) {
      alert("Chyba: " + (err?.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="admin-company-edit-modal">
      <div className="bg-[var(--wb-surface)] border border-gold-700/40 max-w-2xl w-full p-6 relative my-auto max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        <h3 className="font-display font-bold text-xl mb-1 inline-flex items-center gap-2">
          <Building2 className="w-5 h-5 text-gold-400" />
          {company ? "Upraviť firmu" : "Pridať certifikovanú firmu"}
        </h3>
        <p className="text-xs font-mono text-zinc-500 mb-5">Sieň slávy SauberEko®</p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Názov firmy *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" placeholder="napr. ABC Welding s. r. o." data-testid="admin-company-name" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">IČO</label>
            <input value={ico} onChange={(e) => setIco(e.target.value)} className="wb-input w-full px-3 py-2 text-sm font-mono" placeholder="12345678" data-testid="admin-company-ico" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Číslo certifikátu</label>
              <input value={certNo} onChange={(e) => setCertNo(e.target.value)} className="wb-input w-full px-3 py-2 text-sm font-mono" placeholder="Sa.Eko.Comp-2026-0001" data-testid="admin-company-cert-no" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Web</label>
              <input value={website} onChange={(e) => setWebsite(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" placeholder="abc-welding.sk" data-testid="admin-company-website" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Mesto</label>
              <input value={city} onChange={(e) => setCity(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" data-testid="admin-company-city" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Krajina</label>
              <input value={country} onChange={(e) => setCountry(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" placeholder="Slovensko" data-testid="admin-company-country" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">
              Dátum udelenia certifikátu <span className="text-gold-500">(certifikovaný od)</span>
            </label>
            <input
              type="date"
              value={certifiedSince}
              onChange={(e) => setCertifiedSince(e.target.value)}
              className="wb-input w-full px-3 py-2 text-sm font-mono"
              data-testid="admin-company-certified-since"
            />
            <p className="text-[10px] text-zinc-600 mt-1">
              Ak vynecháš, použije sa dnešný. Zadaj skutočný dátum udelenia certifikátu (napr. keď dopĺňaš firmu, ktorá bola certifikovaná v apríli).
            </p>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">
              Sektor <span className="text-gold-500">(priemysel vs služby)</span>
            </label>
            <div className="grid grid-cols-3 gap-2" data-testid="admin-company-sector">
              {[
                { v: "", label: "Nezaradené", emoji: "—" },
                { v: "industry", label: "Priemysel / Výroba", emoji: "🏭" },
                { v: "services", label: "Služby", emoji: "🛠️" },
              ].map((opt) => (
                <button
                  key={opt.v || "none"}
                  type="button"
                  onClick={() => setSector(opt.v)}
                  className={`px-3 py-2 text-xs font-mono uppercase tracking-wider border transition-colors ${
                    sector === opt.v
                      ? "border-gold-500 bg-gold-900/20 text-gold-300"
                      : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                  }`}
                  data-testid={`admin-company-sector-${opt.v || "none"}`}
                >
                  <span className="mr-1">{opt.emoji}</span>{opt.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-zinc-600 mt-1">
              Použije sa na triedenie v Sieni slávy aj v napojení na saubereko-intro.
            </p>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Popis (krátky)</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value.slice(0, 500))} rows={2} className="wb-input w-full px-3 py-2 text-sm" placeholder="Krátky popis činnosti firmy…" data-testid="admin-company-desc" />
            <div className="text-[10px] text-zinc-600 text-right mt-1">{description.length}/500</div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Logo firmy</label>
              {logo ? (
                <div className="space-y-2">
                  <img src={logo} alt="Logo" className="w-full h-24 object-contain bg-white/5 border border-zinc-800" />
                  <div className="flex gap-2">
                    <label className="cursor-pointer text-[10px] font-mono uppercase px-2 py-1 border border-zinc-700 hover:border-zinc-500">
                      Vymeniť
                      <input type="file" accept="image/*" onChange={handleFile(setLogo, 2)} className="hidden" />
                    </label>
                    <button onClick={() => setLogo("")} className="text-[10px] font-mono uppercase px-2 py-1 border border-red-700/50 text-red-400">Zmazať</button>
                  </div>
                </div>
              ) : (
                <label className="cursor-pointer inline-block">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase px-3 py-2 border border-gold-700/40 text-gold-400 hover:bg-gold-900/20">
                    Nahrať logo
                  </span>
                  <input type="file" accept="image/*" onChange={handleFile(setLogo, 2)} className="hidden" data-testid="admin-company-logo-upload" />
                </label>
              )}
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Foto certifikátu</label>
              {certPhoto ? (
                <div className="space-y-2">
                  <img src={certPhoto} alt="Cert" className="w-full h-24 object-contain bg-white/5 border border-gold-700/30" />
                  <div className="flex gap-2">
                    <label className="cursor-pointer text-[10px] font-mono uppercase px-2 py-1 border border-zinc-700 hover:border-zinc-500">
                      Vymeniť
                      <input type="file" accept="image/*" onChange={handleFile(setCertPhoto, 4.5)} className="hidden" />
                    </label>
                    <button onClick={() => setCertPhoto("")} className="text-[10px] font-mono uppercase px-2 py-1 border border-red-700/50 text-red-400">Zmazať</button>
                  </div>
                </div>
              ) : (
                <label className="cursor-pointer inline-block">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase px-3 py-2 border border-gold-700/40 text-gold-400 hover:bg-gold-900/20">
                    Nahrať foto
                  </span>
                  <input type="file" accept="image/*" onChange={handleFile(setCertPhoto, 4.5)} className="hidden" data-testid="admin-company-cert-photo-upload" />
                </label>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-xs font-mono uppercase tracking-wider border border-zinc-700 text-zinc-300 hover:border-zinc-500">
            Zrušiť
          </button>
          <button onClick={save} disabled={busy} className="wb-btn-primary px-4 py-2 text-xs inline-flex items-center gap-2" data-testid="admin-company-save">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            {company ? "Uložiť zmeny" : "Pridať firmu"}
          </button>
        </div>
      </div>
    </div>
  );
}



// ============= CERTIFIED PROFESSIONALS — HALL OF FAME (admin-managed) =============
// Lets the admin add Sa.Eko® certificate HOLDERS (real people) directly into the
// Hall of Fame without requiring them to register an app account.
function ProfessionalsTab() {
  const [items, setItems] = useState([]);
  const [certUsers, setCertUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editUserSkills, setEditUserSkills] = useState(null); // certified-user skills modal
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rPros, rUsers] = await Promise.all([
        api.get("/admin/certified-professionals"),
        api.get("/admin/users"),
      ]);
      setItems(rPros.data?.items || []);
      // Only certified users placed under the "Professional" cohort
      const list = Array.isArray(rUsers.data) ? rUsers.data : (rUsers.data?.items || []);
      setCertUsers(list.filter((u) => u.certified && (u.cert_role || "professional") === "professional"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Naozaj zmazať „${p.name}" zo siene slávy?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/certified-professionals/${p.id}`);
      load();
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-hof-pros-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold text-2xl text-white">Profesionáli — Sieň slávy</h2>
          <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-1">
            Ručne pridaní držitelia certifikátu SauberEko® (nemajú účet v aplikácii)
          </p>
        </div>
        <button
          onClick={() => setEditing("new")}
          className="wb-btn-primary inline-flex items-center gap-2 px-4 py-2 text-xs"
          data-testid="admin-hof-pro-add"
        >
          <Plus className="w-3 h-3" /> Pridať profesionála
        </button>
      </div>

      {/* ─── Certified app users (self-registered welders) ─── */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display font-bold text-sm text-gold-400 uppercase tracking-wider">
              Certifikovaní užívatelia aplikácie
            </h3>
            <p className="text-[11px] text-zinc-500 mt-1">
              Registrovaní welderi s Sa.Eko.Prs certifikátom — klikni „Zručnosti" pre priradenie tagov.
            </p>
          </div>
          <span className="text-[10px] font-mono text-zinc-500">{certUsers.length} užívateľov</span>
        </div>
        {loading ? (
          <div className="text-zinc-500 text-xs">Načítavam…</div>
        ) : certUsers.length === 0 ? (
          <div className="text-center py-6 border border-zinc-800 bg-zinc-950 text-xs text-zinc-500">
            Zatiaľ žiaden certifikovaný užívateľ. Priradiť certifikát vieš v záložke „Certifikáty".
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {certUsers.map((u) => (
              <div key={u.id} className="border border-zinc-800 bg-zinc-950 p-3" data-testid={`admin-hof-user-${u.id}`}>
                <div className="flex items-start gap-2 mb-2">
                  {u.picture ? (
                    <img src={u.picture} alt={u.name} className="w-10 h-10 object-cover border border-zinc-800" />
                  ) : (
                    <div className="w-10 h-10 bg-gold-950/30 border border-gold-700/40 flex items-center justify-center">
                      <span className="font-display font-bold text-gold-400 text-base">{(u.cert_holder_name || u.name)?.[0] || "—"}</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-xs text-white truncate">{u.cert_holder_name || u.name}</div>
                    <div className="text-[10px] font-mono text-zinc-500 truncate">{u.certificate_no || "—"}</div>
                    <div className="text-[10px] text-zinc-600 truncate">{u.email}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-mono text-zinc-500">
                    {(u.skills?.length || 0) + (u.skills_other ? 1 : 0)} zručností
                  </span>
                  <button
                    onClick={() => setEditUserSkills(u)}
                    className="text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 border border-gold-700/50 text-gold-400 hover:border-gold-500 hover:bg-gold-950/30"
                    data-testid={`admin-hof-user-skills-${u.id}`}
                  >
                    Zručnosti
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── Manually-added HoF professionals (no app account) ─── */}
      <div className="mb-3">
        <h3 className="font-display font-bold text-sm text-gold-400 uppercase tracking-wider">
          Manuálne pridaní profesionáli
        </h3>
      </div>
      {loading ? (
        <div className="text-zinc-500 text-sm">Načítavam…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border border-zinc-800 bg-zinc-950">
          <UserCheck className="w-12 h-12 mx-auto text-zinc-700 mb-3" />
          <div className="text-zinc-400 text-sm">Zatiaľ nie sú pridaní žiadni manuálni profesionáli</div>
          <p className="text-xs text-zinc-600 mt-2 max-w-md mx-auto">Tu pridávaš držiteľov SauberEko® certifikátu, ktorí nie sú registrovaní v aplikácii. Objavia sa v sekcii „Profesionáli" v Sieni slávy.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} className="border border-zinc-800 bg-zinc-950 p-4" data-testid={`admin-hof-pro-${p.id}`}>
              <div className="flex items-start gap-3 mb-3">
                {p.picture ? (
                  <img src={p.picture} alt={p.name} className="w-12 h-12 object-cover border border-zinc-800" />
                ) : (
                  <div className="w-12 h-12 bg-gold-950/30 border border-gold-700/40 flex items-center justify-center">
                    <span className="font-display font-bold text-gold-400 text-lg">{p.name?.[0] || "—"}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm text-white truncate">{p.name}</div>
                  <div className="text-[10px] font-mono text-zinc-500">{p.certificate_no || "—"}</div>
                </div>
              </div>
              {(p.city || p.country) && (
                <div className="text-xs text-zinc-400 mb-2">{[p.city, p.country].filter(Boolean).join(", ")}</div>
              )}
              {p.specialization && (
                <div className="text-[11px] text-zinc-500 mb-3 truncate">{p.specialization}</div>
              )}
              <div className="flex gap-2">
                <button onClick={() => setEditing(p)} className="flex-1 text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-gold-500" data-testid={`admin-hof-pro-edit-${p.id}`}>
                  Upraviť
                </button>
                <button onClick={() => remove(p)} disabled={busy} className="text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 border border-red-700/50 text-red-400 hover:bg-red-950/30" data-testid={`admin-hof-pro-delete-${p.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ProEditModal
          pro={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {editUserSkills && (
        <UserSkillsModal
          user={editUserSkills}
          onClose={() => setEditUserSkills(null)}
          onSaved={() => { setEditUserSkills(null); load(); }}
        />
      )}
    </div>
  );
}

// Lightweight modal to assign SkillsPicker output to a certified USER (users collection).
function UserSkillsModal({ user, onClose, onSaved }) {
  const [skills, setSkills] = useState(Array.isArray(user?.skills) ? user.skills : []);
  const [skillsOther, setSkillsOther] = useState(user?.skills_other || "");
  const [mentor, setMentor] = useState(!!user?.mentor);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}`, {
        skills, skills_other: skillsOther || null, mentor,
      });
      onSaved();
    } catch (err) {
      alert("Chyba: " + (err?.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="admin-hof-user-skills-modal">
      <div className="bg-[var(--wb-surface)] border border-gold-700/40 max-w-2xl w-full p-6 relative my-auto max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white" data-testid="admin-hof-user-skills-close">
          <X className="w-4 h-4" />
        </button>
        <h3 className="font-display font-bold text-xl mb-1 inline-flex items-center gap-2">
          <Award className="w-5 h-5 text-gold-400" />
          Zručnosti — {user.cert_holder_name || user.name}
        </h3>
        <p className="text-xs font-mono text-zinc-500 mb-5">
          {user.certificate_no || "—"} · {user.email}
        </p>

        <label className="flex items-center gap-3 mb-5 p-3 border border-zinc-800 bg-zinc-950 cursor-pointer hover:border-gold-700/50">
          <input
            type="checkbox"
            checked={mentor}
            onChange={(e) => setMentor(e.target.checked)}
            className="w-4 h-4 accent-gold-500"
            data-testid="admin-hof-user-mentor-checkbox"
          />
          <div>
            <div className="text-sm text-white font-display font-bold">🎓 Mentor</div>
            <div className="text-[10px] font-mono text-zinc-500">Ochotný školiť / mentorovať mladších welderov</div>
          </div>
        </label>

        <SkillsPicker
          selected={skills}
          onChange={setSkills}
          other={skillsOther}
          onOtherChange={setSkillsOther}
          lang="sk"
        />

        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-zinc-800">
          <button
            onClick={onClose}
            className="text-xs font-mono uppercase tracking-wider px-4 py-2 border border-zinc-700 text-zinc-400 hover:border-zinc-500"
            data-testid="admin-hof-user-skills-cancel"
          >
            Zrušiť
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="wb-btn-primary text-xs uppercase tracking-wider px-4 py-2 disabled:opacity-50"
            data-testid="admin-hof-user-skills-save"
          >
            {busy ? "Ukladám…" : "Uložiť"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProEditModal({ pro, onClose, onSaved }) {
  const [name, setName] = useState(pro?.name || "");
  const [certNo, setCertNo] = useState(pro?.certificate_no || "");
  const [country, setCountry] = useState(pro?.country || "Slovensko");
  const [city, setCity] = useState(pro?.city || "");
  const [specialization, setSpecialization] = useState(pro?.specialization || "");
  const [company, setCompany] = useState(pro?.company || "");
  const [description, setDescription] = useState(pro?.description || "");
  const [picture, setPicture] = useState(pro?.picture || "");
  const [certPhoto, setCertPhoto] = useState(pro?.certificate_photo || "");
  const [certifiedSince, setCertifiedSince] = useState(
    pro?.certified_since ? String(pro.certified_since).split("T")[0] : ""
  );
  const [skills, setSkills] = useState(Array.isArray(pro?.skills) ? pro.skills : []);
  const [skillsOther, setSkillsOther] = useState(pro?.skills_other || "");
  const [mentor, setMentor] = useState(!!pro?.mentor);
  const [busy, setBusy] = useState(false);

  const handleFile = (setter, maxMB = 4.5) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxMB * 1_000_000) { alert(`Príliš veľký súbor (max ${maxMB} MB).`); return; }
    const r = new FileReader();
    r.onload = () => setter(r.result);
    r.readAsDataURL(file);
  };

  const save = async () => {
    if (!name.trim() || name.trim().length < 2) { alert("Meno je povinné."); return; }
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        certificate_no: certNo.trim(),
        country: country.trim(),
        city: city.trim(),
        specialization: specialization.trim(),
        company: company.trim(),
        description: description.trim(),
        picture: picture || null,
        certificate_photo: certPhoto || null,
        certified_since: certifiedSince ? new Date(certifiedSince + "T12:00:00Z").toISOString() : null,
        skills, skills_other: skillsOther || null, mentor,
      };
      if (pro?.id) {
        await api.patch(`/admin/certified-professionals/${pro.id}`, payload);
      } else {
        await api.post("/admin/certified-professionals", payload);
      }
      onSaved();
    } catch (err) {
      alert("Chyba: " + (err?.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="admin-hof-pro-edit-modal">
      <div className="bg-[var(--wb-surface)] border border-gold-700/40 max-w-2xl w-full p-6 relative my-auto max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        <h3 className="font-display font-bold text-xl mb-1 inline-flex items-center gap-2">
          <UserCheck className="w-5 h-5 text-gold-400" />
          {pro ? "Upraviť profesionála" : "Pridať certifikovaného profesionála"}
        </h3>
        <p className="text-xs font-mono text-zinc-500 mb-5">Sieň slávy SauberEko®</p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Meno a priezvisko *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" placeholder="napr. Martin Smrkta" data-testid="admin-hof-pro-name" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Číslo certifikátu</label>
              <input value={certNo} onChange={(e) => setCertNo(e.target.value)} className="wb-input w-full px-3 py-2 text-sm font-mono" placeholder="Sa.Eko.Prs.2026-0001" data-testid="admin-hof-pro-cert-no" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Špecializácia</label>
              <input value={specialization} onChange={(e) => setSpecialization(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" placeholder="napr. Orbitálne / Ručný TIG / Potrubár" data-testid="admin-hof-pro-spec" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Mesto</label>
              <input value={city} onChange={(e) => setCity(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" data-testid="admin-hof-pro-city" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Krajina</label>
              <input value={country} onChange={(e) => setCountry(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" placeholder="Slovensko" data-testid="admin-hof-pro-country" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Firma / zamestnávateľ (voliteľné)</label>
            <input value={company} onChange={(e) => setCompany(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" placeholder="napr. ABC Welding s. r. o." data-testid="admin-hof-pro-company" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">
              Dátum udelenia certifikátu <span className="text-gold-500">(certifikovaný od)</span>
            </label>
            <input
              type="date"
              value={certifiedSince}
              onChange={(e) => setCertifiedSince(e.target.value)}
              className="wb-input w-full px-3 py-2 text-sm font-mono"
              data-testid="admin-hof-pro-certified-since"
            />
            <p className="text-[10px] text-zinc-600 mt-1">
              Ak vynecháš, použije sa dnešný. Zadaj skutočný dátum keď bol certifikát udelený.
            </p>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">
              🎯 Zručnosti <span className="text-gold-500">(admin priraďuje)</span>
            </label>
            <SkillsPicker
              selected={skills}
              onChange={setSkills}
              other={skillsOther}
              onOtherChange={setSkillsOther}
              lang="sk"
            />
          </div>
          <label className="flex items-center gap-3 p-3 border border-zinc-800 bg-zinc-950 cursor-pointer hover:border-gold-700/50">
            <input
              type="checkbox"
              checked={mentor}
              onChange={(e) => setMentor(e.target.checked)}
              className="w-4 h-4 accent-gold-500"
              data-testid="admin-hof-pro-mentor-checkbox"
            />
            <div>
              <div className="text-sm text-white font-display font-bold">🎓 Mentor</div>
              <div className="text-[10px] font-mono text-zinc-500">Ochotný školiť / mentorovať mladších welderov</div>
            </div>
          </label>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Popis (krátky)</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value.slice(0, 500))} rows={2} className="wb-input w-full px-3 py-2 text-sm" placeholder="Krátky popis prác / praxe…" data-testid="admin-hof-pro-desc" />
            <div className="text-[10px] text-zinc-600 text-right mt-1">{description.length}/500</div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Fotka profilu</label>
              {picture ? (
                <div className="space-y-2">
                  <img src={picture} alt="Profil" className="w-full h-24 object-cover border border-zinc-800" />
                  <div className="flex gap-2">
                    <label className="cursor-pointer text-[10px] font-mono uppercase px-2 py-1 border border-zinc-700 hover:border-zinc-500">
                      Vymeniť
                      <input type="file" accept="image/*" onChange={handleFile(setPicture, 2)} className="hidden" />
                    </label>
                    <button onClick={() => setPicture("")} className="text-[10px] font-mono uppercase px-2 py-1 border border-red-700/50 text-red-400">Zmazať</button>
                  </div>
                </div>
              ) : (
                <label className="cursor-pointer inline-block">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase px-3 py-2 border border-gold-700/40 text-gold-400 hover:bg-gold-900/20">
                    Nahrať fotku
                  </span>
                  <input type="file" accept="image/*" onChange={handleFile(setPicture, 2)} className="hidden" data-testid="admin-hof-pro-picture-upload" />
                </label>
              )}
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Foto certifikátu</label>
              {certPhoto ? (
                <div className="space-y-2">
                  <img src={certPhoto} alt="Cert" className="w-full h-24 object-contain bg-white/5 border border-gold-700/30" />
                  <div className="flex gap-2">
                    <label className="cursor-pointer text-[10px] font-mono uppercase px-2 py-1 border border-zinc-700 hover:border-zinc-500">
                      Vymeniť
                      <input type="file" accept="image/*" onChange={handleFile(setCertPhoto, 4.5)} className="hidden" />
                    </label>
                    <button onClick={() => setCertPhoto("")} className="text-[10px] font-mono uppercase px-2 py-1 border border-red-700/50 text-red-400">Zmazať</button>
                  </div>
                </div>
              ) : (
                <label className="cursor-pointer inline-block">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase px-3 py-2 border border-gold-700/40 text-gold-400 hover:bg-gold-900/20">
                    Nahrať foto
                  </span>
                  <input type="file" accept="image/*" onChange={handleFile(setCertPhoto, 4.5)} className="hidden" data-testid="admin-hof-pro-cert-photo-upload" />
                </label>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-xs font-mono uppercase tracking-wider border border-zinc-700 text-zinc-300 hover:border-zinc-500">
            Zrušiť
          </button>
          <button onClick={save} disabled={busy} className="wb-btn-primary px-4 py-2 text-xs inline-flex items-center gap-2" data-testid="admin-hof-pro-save">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            {pro ? "Uložiť zmeny" : "Pridať profesionála"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============= CERT INVOICE — ISSUE INDIVIDUAL CERTIFICATION INVOICE =============
function CertInvoiceTab() {
  const EMPTY_SUPPLIER = {
    legal_name: "", address: "", zip: "", city: "", country: "Slovensko",
    ico: "", dic: "", ic_dph: "",
    email: "", phone: "",
    iban: "", swift: "", bank: "",
  };
  const [supplier, setSupplier] = useState(EMPTY_SUPPLIER);
  const [supplierSavedAt, setSupplierSavedAt] = useState(null);
  const [savingSupplier, setSavingSupplier] = useState(false);

  const [form, setForm] = useState({
    client_legal_name: "",
    client_email: "",
    client_address: "",
    client_zip: "",
    client_city: "",
    client_country: "Slovensko",
    client_ico: "",
    client_dic: "",
    client_ic_dph: "",
    amount: "",
    currency: "EUR",
    invoice_no: "",
    item_description: "",
    quantity: 1,
    unit_price: "",
    variable_symbol: "",
    issue_date_iso: "",
    supply_date_iso: "",
    due_date_iso: "",
    lang: "sk",
    create_payment_link: true,
    send_email_to_client: true,
  });
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const r = await api.get("/admin/cert-invoices?limit=20");
      setHistory(r.data?.items || []);
    } catch { /* ignore */ }
  };
  const loadSupplier = async () => {
    try {
      const r = await api.get("/admin/cert-invoices/supplier-defaults");
      const s = r.data?.supplier || {};
      if (Object.keys(s).length > 0) {
        setSupplier({ ...EMPTY_SUPPLIER, ...s });
        if (s.updated_at) setSupplierSavedAt(s.updated_at);
      }
    } catch { /* ignore */ }
  };
  useEffect(() => { loadHistory(); loadSupplier(); /* eslint-disable-line */ }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setBool = (k) => (e) => setForm({ ...form, [k]: e.target.checked });
  const setSup = (k) => (e) => setSupplier({ ...supplier, [k]: e.target.value });

  const saveSupplier = async () => {
    setSavingSupplier(true);
    try {
      const r = await api.put("/admin/cert-invoices/supplier-defaults", supplier);
      if (r.data?.supplier?.updated_at) setSupplierSavedAt(r.data.supplier.updated_at);
      setResult({ kind: "ok-supplier", text: "Údaje dodávateľa uložené ako predvolené" });
    } catch (e) {
      setResult({ kind: "err", text: e.response?.data?.detail || e.message });
    } finally { setSavingSupplier(false); }
  };

  const buildPayload = () => {
    const supplierClean = Object.fromEntries(
      Object.entries(supplier).filter(([_, v]) => (v ?? "").toString().trim() !== "")
    );
    return {
      ...form,
      amount: Number(form.amount || 0),
      quantity: Number(form.quantity || 1),
      unit_price: form.unit_price === "" ? null : Number(form.unit_price),
      issue_date_iso: form.issue_date_iso || null,
      supply_date_iso: form.supply_date_iso || null,
      due_date_iso: form.due_date_iso || null,
      variable_symbol: form.variable_symbol || null,
      invoice_no: form.invoice_no || null,
      item_description: form.item_description || null,
      supplier: Object.keys(supplierClean).length > 0 ? supplierClean : null,
    };
  };

  const preview = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      alert("Pre náhľad zadajte aspoň sumu > 0.");
      return;
    }
    setPreviewing(true);
    try {
      const payload = buildPayload();
      payload.client_legal_name = form.client_legal_name || "Náhľad — meno klienta";
      const r = await api.post("/admin/cert-invoices/preview", payload, { responseType: "text" });
      const html = typeof r.data === "string" ? r.data : "";
      const w = window.open("", "_blank");
      if (w) {
        w.document.open(); w.document.write(html); w.document.close();
      } else {
        alert("Náhľad neotvorený — povoľ pop-up okná v prehliadači.");
      }
    } catch (e) {
      setResult({ kind: "err", text: e.response?.data?.detail || e.message });
    } finally { setPreviewing(false); }
  };

  const issue = async (e) => {
    e.preventDefault();
    if (!form.client_legal_name.trim() || !form.amount) {
      alert("Meno klienta a suma sú povinné polia.");
      return;
    }
    if (!form.client_email && form.send_email_to_client) {
      alert('Bez emailu klienta nemôžem poslať faktúru. Buď zadaj email, alebo vypni "Poslať email klientovi".');
      return;
    }
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/admin/cert-invoices/issue", buildPayload());
      setResult({ kind: "ok", data: r.data });
      // reset client + invoice-specific fields, keep supplier & settings
      setForm({
        ...form,
        client_legal_name: "", client_email: "", client_address: "",
        client_zip: "", client_city: "", client_ico: "", client_dic: "",
        client_ic_dph: "", amount: "", invoice_no: "", item_description: "",
        quantity: 1, unit_price: "", variable_symbol: "",
        issue_date_iso: "", supply_date_iso: "", due_date_iso: "",
      });
      loadHistory();
      // ─── If email was queued, poll status so admin sees ✅/❌ instead of "preparing…"
      const taskId = r.data?.task_id;
      if (r.data?.email_queued && taskId) {
        let tries = 0;
        const poll = async () => {
          tries += 1;
          try {
            const s = await api.get(`/admin/email-status/${taskId}`);
            const st = s.data?.status;
            if (st === "sent") {
              setResult({ kind: "ok", data: r.data,
                          emailStatus: "sent", emailTo: s.data?.to });
              return;
            }
            if (st === "failed") {
              setResult({ kind: "ok", data: r.data,
                          emailStatus: "failed", emailError: s.data?.error });
              return;
            }
          } catch (_) { /* ignore transient */ }
          if (tries < 40) setTimeout(poll, 3000);
          else setResult((prev) => prev ? { ...prev, emailStatus: "timeout" } : prev);
        };
        setTimeout(poll, 3000);
      }
    } catch (e) {
      setResult({ kind: "err", text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="cert-invoice-tab" className="max-w-4xl space-y-8">
      <div>
        <h2 className="font-display font-black text-2xl text-white">Vystaviť faktúru — SauberEko s.r.o.</h2>
        <p className="text-xs text-zinc-500 mt-1">
          Univerzálna faktúra pre klientov firmy SauberEko® · bielo-zlatý A4 PDF · Stripe link + IBAN bankový prevod · službu doplníš sám
        </p>
      </div>

      <form onSubmit={issue} className="border border-gold-700/30 bg-zinc-950 p-6 space-y-5">
        {/* Supplier (dodávateľ) section — editable, can be saved as default */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-mono uppercase tracking-widest text-gold-400">🏢 Dodávateľ (vystaviteľ)</h3>
            <button type="button" onClick={saveSupplier} disabled={savingSupplier}
              data-testid="cert-inv-supplier-save"
              className="text-[10px] font-mono uppercase tracking-widest text-gold-300 border border-gold-700/40 px-3 py-1 hover:bg-gold-900/20 disabled:opacity-50">
              {savingSupplier ? "Ukladám…" : "💾 Uložiť ako predvolené"}
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={supplier.legal_name} onChange={setSup("legal_name")} placeholder="Názov / Firma" className="wb-input px-3 py-2 text-sm" data-testid="cert-inv-sup-name" />
            <input type="email" value={supplier.email} onChange={setSup("email")} placeholder="E-mail" className="wb-input px-3 py-2 text-sm" />
            <input value={supplier.address} onChange={setSup("address")} placeholder="Adresa (ulica, č.)" className="wb-input px-3 py-2 text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <input value={supplier.zip} onChange={setSup("zip")} placeholder="PSČ" className="wb-input px-3 py-2 text-sm" />
              <input value={supplier.city} onChange={setSup("city")} placeholder="Mesto" className="wb-input px-3 py-2 text-sm" />
            </div>
            <input value={supplier.country} onChange={setSup("country")} placeholder="Krajina" className="wb-input px-3 py-2 text-sm" />
            <input value={supplier.phone} onChange={setSup("phone")} placeholder="Telefón (voliteľné)" className="wb-input px-3 py-2 text-sm" />
            <div className="grid grid-cols-3 gap-2">
              <input value={supplier.ico} onChange={setSup("ico")} placeholder="IČO" className="wb-input px-3 py-2 text-sm" />
              <input value={supplier.dic} onChange={setSup("dic")} placeholder="DIČ" className="wb-input px-3 py-2 text-sm" />
              <input value={supplier.ic_dph} onChange={setSup("ic_dph")} placeholder="IČ DPH" className="wb-input px-3 py-2 text-sm" />
            </div>
            <input value={supplier.bank} onChange={setSup("bank")} placeholder="Banka (voliteľné)" className="wb-input px-3 py-2 text-sm" />
            <input value={supplier.iban} onChange={setSup("iban")} placeholder="IBAN" className="wb-input px-3 py-2 text-sm" data-testid="cert-inv-sup-iban" />
            <input value={supplier.swift} onChange={setSup("swift")} placeholder="BIC / SWIFT" className="wb-input px-3 py-2 text-sm" />
          </div>
          {supplierSavedAt && (
            <div className="text-[10px] text-zinc-500 font-mono mt-2">
              ✓ Posledné uloženie: {supplierSavedAt.slice(0, 19).replace("T", " ")}
            </div>
          )}
        </div>

        <div className="border-t border-zinc-800" />

        {/* Client section */}
        <div>
          <h3 className="text-xs font-mono uppercase tracking-widest text-gold-400 mb-3">👤 Klient (odberateľ)</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <input required value={form.client_legal_name} onChange={set("client_legal_name")} placeholder="Meno / Firma *" className="wb-input px-3 py-2 text-sm" data-testid="cert-inv-name" />
            <input type="email" value={form.client_email} onChange={set("client_email")} placeholder="E-mail" className="wb-input px-3 py-2 text-sm" data-testid="cert-inv-email" />
            <input value={form.client_address} onChange={set("client_address")} placeholder="Adresa (ulica, č.)" className="wb-input px-3 py-2 text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <input value={form.client_zip} onChange={set("client_zip")} placeholder="PSČ" className="wb-input px-3 py-2 text-sm" />
              <input value={form.client_city} onChange={set("client_city")} placeholder="Mesto" className="wb-input px-3 py-2 text-sm" />
            </div>
            <input value={form.client_country} onChange={set("client_country")} placeholder="Krajina" className="wb-input px-3 py-2 text-sm" />
            <div className="grid grid-cols-3 gap-2">
              <input value={form.client_ico} onChange={set("client_ico")} placeholder="IČO" className="wb-input px-3 py-2 text-sm" />
              <input value={form.client_dic} onChange={set("client_dic")} placeholder="DIČ" className="wb-input px-3 py-2 text-sm" />
              <input value={form.client_ic_dph} onChange={set("client_ic_dph")} placeholder="IČ DPH" className="wb-input px-3 py-2 text-sm" />
            </div>
          </div>
        </div>

        {/* Invoice section */}
        <div>
          <h3 className="text-xs font-mono uppercase tracking-widest text-gold-400 mb-3">💶 Údaje faktúry</h3>
          <InvoiceNumbering refresh={result} />
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            <input required type="number" step="0.01" min="0" value={form.amount} onChange={set("amount")} placeholder="Suma celkom *" className="wb-input px-3 py-2 text-sm" data-testid="cert-inv-amount" />
            <select value={form.currency} onChange={set("currency")} className="wb-input px-3 py-2 text-sm">
              <option value="EUR">EUR (€)</option>
              <option value="CZK">CZK (Kč)</option>
              <option value="PLN">PLN (zł)</option>
              <option value="USD">USD ($)</option>
            </select>
            <input value={form.invoice_no} onChange={set("invoice_no")} placeholder="Číslo faktúry (auto, iba 1-RRRR-NNNN)" className="wb-input px-3 py-2 text-sm" data-testid="cert-inv-no" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            <input type="number" step="0.01" min="0" value={form.quantity} onChange={set("quantity")} placeholder="Množstvo" className="wb-input px-3 py-2 text-sm" />
            <input type="number" step="0.01" min="0" value={form.unit_price} onChange={set("unit_price")} placeholder="Cena za jednotku (voliteľné)" className="wb-input px-3 py-2 text-sm" />
            <input value={form.variable_symbol} onChange={set("variable_symbol")} placeholder="Variabilný symbol (auto)" className="wb-input px-3 py-2 text-sm" />
          </div>
          <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Popis služby <span className="text-amber-400">*</span> (objaví sa na faktúre)</label>
          <textarea value={form.item_description} onChange={set("item_description")} rows={3} placeholder="Napíš sem ČO presne fakturuješ — napr.: Konzultácia v oblasti orbital welding pre projekt potrubných systémov, 4 hodiny." className="wb-input w-full px-3 py-2 text-sm" data-testid="cert-inv-desc" />
          <div className="grid sm:grid-cols-3 gap-3 mt-3">
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Dátum vystavenia</label>
              <input type="date" value={form.issue_date_iso} onChange={set("issue_date_iso")} className="wb-input w-full px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Dátum dodania</label>
              <input type="date" value={form.supply_date_iso} onChange={set("supply_date_iso")} className="wb-input w-full px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Dátum splatnosti</label>
              <input type="date" value={form.due_date_iso} onChange={set("due_date_iso")} className="wb-input w-full px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="mt-3 grid sm:grid-cols-2 gap-3 items-center">
            <select value={form.lang} onChange={set("lang")} className="wb-input px-3 py-2 text-sm" data-testid="cert-inv-lang">
              <option value="sk">🇸🇰 Slovenčina</option>
              <option value="en">🇬🇧 English</option>
              <option value="cs">🇨🇿 Čeština</option>
              <option value="de">🇩🇪 Deutsch</option>
              <option value="pl">🇵🇱 Polski</option>
            </select>
            <div className="text-[10px] text-zinc-500 font-mono">
              Faktúra bude v zvolenom jazyku klienta
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="border-t border-zinc-800 pt-4 space-y-2">
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <input type="checkbox" checked={form.create_payment_link} onChange={setBool("create_payment_link")} className="w-4 h-4" data-testid="cert-inv-stripe" />
            <span>💳 Vytvoriť <strong className="text-gold-300">Stripe Payment Link</strong> — klient bude môcť zaplatiť kartou priamo z emailu</span>
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <input type="checkbox" checked={form.send_email_to_client} onChange={setBool("send_email_to_client")} className="w-4 h-4" data-testid="cert-inv-send" />
            <span>✉️ Poslať faktúru (PDF + odkaz) priamo na email klienta</span>
          </label>
        </div>

        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={preview} disabled={previewing || busy}
            data-testid="cert-inv-preview"
            className="border border-gold-700/40 text-gold-300 px-5 py-3 text-sm inline-flex items-center gap-2 hover:bg-gold-900/20 disabled:opacity-50 font-mono uppercase tracking-widest">
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : "👁️"}
            {previewing ? "Generujem…" : "Náhľad"}
          </button>
          <button type="submit" disabled={busy} data-testid="cert-inv-submit"
            className="wb-btn-primary px-6 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            {busy ? "Vystavujem…" : "Vystaviť faktúru"}
          </button>
        </div>

        {result?.kind === "ok-supplier" && (
          <div className="border border-green-700/50 bg-green-950/30 p-3 text-xs text-green-300">
            ✅ {result.text}
          </div>
        )}

        {result?.kind === "ok" && (
          <div className="border border-green-700/50 bg-green-950/30 p-4 text-sm" data-testid="cert-inv-result">
            <div className="text-green-300 font-bold mb-1">✅ Faktúra {result.data.invoice_no} vystavená</div>
            <div className="text-zinc-300">Poslaná klientovi: <strong>{result.data.sent_to_client ? "ÁNO" : "NIE"}</strong></div>
            {result.data.payment_link_url && (
              <div className="mt-2">
                <span className="text-zinc-400">💳 Stripe Payment Link:</span>{" "}
                <a href={result.data.payment_link_url} target="_blank" rel="noreferrer" className="text-gold-300 underline break-all">
                  {result.data.payment_link_url}
                </a>
              </div>
            )}
            {/* Live email-delivery status (only shown when email was queued) */}
            {result.data.email_queued && (
              <div className="mt-3 pt-3 border-t border-green-700/30 text-xs">
                {!result.emailStatus && (
                  <div className="text-zinc-300 inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Email sa pripravuje a bude odoslaný v priebehu 30-90 sekúnd…</div>
                )}
                {result.emailStatus === "sent" && (
                  <div data-testid="cert-inv-email-sent" className="text-emerald-300 font-medium inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> ✅ Email úspešne odoslaný na <span className="font-mono">{result.emailTo}</span></div>
                )}
                {result.emailStatus === "failed" && (
                  <div data-testid="cert-inv-email-failed" className="text-red-300 font-medium">❌ Email zlyhal — {result.emailError || "neznáma chyba"}</div>
                )}
                {result.emailStatus === "timeout" && (
                  <div className="text-amber-300">⏱️ Odosielanie trvá dlhšie ako 2 min. Skontroluj históriu faktúr pre finálny stav.</div>
                )}
              </div>
            )}
          </div>
        )}
        {result?.kind === "err" && (
          <div className="border border-red-700/50 bg-red-950/30 p-4 text-sm text-red-300">
            ❌ Chyba: {result.text}
          </div>
        )}
      </form>

      {/* History */}
      {history.length > 0 && (
        <div data-testid="cert-inv-history">
          <h3 className="font-display font-bold text-lg text-white mb-3">📋 História vystavených faktúr</h3>
          <div className="border border-zinc-800 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-zinc-900 text-zinc-500 uppercase tracking-wider">
                <tr><th className="p-2 text-left">Č. faktúry</th><th className="p-2 text-left">Klient</th><th className="p-2 text-right">Suma</th><th className="p-2 text-left">Stav</th><th className="p-2 text-left">Stripe</th><th className="p-2 text-left">Dátum</th></tr>
              </thead>
              <tbody>
                {history.map((inv) => (
                  <tr key={inv.id} className="border-t border-zinc-800 text-zinc-300 hover:bg-zinc-950">
                    <td className="p-2 font-mono text-gold-400">{inv.invoice_no}</td>
                    <td className="p-2">{inv.client?.legal_name}</td>
                    <td className="p-2 text-right font-mono">{inv.amount?.toFixed(2)} {inv.currency}</td>
                    <td className="p-2">
                      {inv.payment_status === "paid" ? <span className="text-green-400">✓ Zaplatené</span> : <span className="text-zinc-500">Čaká</span>}
                    </td>
                    <td className="p-2">
                      {inv.payment_link_url ? (
                        <a href={inv.payment_link_url} target="_blank" rel="noreferrer" className="text-gold-400 underline" title="Otvoriť Stripe Payment Link">link ↗</a>
                      ) : "—"}
                    </td>
                    <td className="p-2 text-zinc-500">{inv.issued_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============= RECOGNITIONS — HALL OF FAME (Iteration 77) =============
// SauberEko® brand awards / honors. NOT a certificate — a marketing recognition.
// Public (no paywall), no cert_no, admin adds directly + saubereko-intro can also
// POST via /api/integrations/hof/recognition.
function RecognitionsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | rec object
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/certified-recognitions");
      setItems(r.data?.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    if (!window.confirm(`Naozaj zmazať uznanie „${r.recognition_title}" pre ${r.name}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/certified-recognitions/${r.id}`);
      load();
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-hof-recognitions-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold text-2xl text-white inline-flex items-center gap-2">
            <Trophy className="w-6 h-6 text-gold-400" /> Uznania — SauberEko®
          </h2>
          <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-1">
            Vyznamenania a ocenenia udelené značkou (verejné, bez paywallu)
          </p>
        </div>
        <button
          onClick={() => setEditing("new")}
          className="wb-btn-primary inline-flex items-center gap-2 px-4 py-2 text-xs"
          data-testid="admin-hof-recognition-add"
        >
          <Plus className="w-3 h-3" /> Pridať uznanie
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Načítavam…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border border-zinc-800 bg-zinc-950">
          <Trophy className="w-12 h-12 mx-auto text-zinc-700 mb-3" />
          <div className="text-zinc-400 text-sm">Zatiaľ žiadne uznania</div>
          <p className="text-xs text-zinc-600 mt-2 max-w-md mx-auto">
            Klikni „Pridať uznanie" a vlož prvé vyznamenanie. Objaví sa vo verejnej Sieni slávy v záložke „Uznania".
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((r) => (
            <div key={r.id} className="border border-zinc-800 bg-zinc-950 overflow-hidden" data-testid={`admin-recognition-${r.id}`}>
              {r.award_image && (
                <div className="aspect-video bg-black overflow-hidden">
                  <img src={r.award_image} alt={r.recognition_title} className="w-full h-full object-contain" />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start gap-2 mb-2">
                  {r.picture ? (
                    <img src={r.picture} alt={r.name} className="w-10 h-10 object-cover border border-zinc-800" />
                  ) : (
                    <div className="w-10 h-10 bg-gold-950/30 border border-gold-700/40 flex items-center justify-center">
                      <Trophy className="w-5 h-5 text-gold-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm text-white truncate">{r.recognition_title}</div>
                    <div className="text-[11px] text-gold-300 truncate">{r.name}</div>
                    <div className="text-[10px] font-mono text-zinc-500 truncate">
                      {r.category || "—"}{r.city ? ` · ${r.city}` : ""}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 pt-2 border-t border-zinc-800/60">
                  <button
                    onClick={() => setEditing(r)}
                    className="flex-1 text-[10px] font-mono uppercase tracking-wider px-2 py-1.5 border border-gold-700/50 text-gold-400 hover:border-gold-500 hover:bg-gold-950/30"
                    data-testid={`admin-recognition-edit-${r.id}`}
                  >
                    Upraviť
                  </button>
                  <button
                    onClick={() => remove(r)}
                    disabled={busy}
                    className="text-[10px] font-mono uppercase tracking-wider px-2 py-1.5 border border-red-700/50 text-red-400 hover:border-red-500 hover:bg-red-950/30 disabled:opacity-50"
                    data-testid={`admin-recognition-delete-${r.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <RecognitionEditModal
          rec={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function RecognitionEditModal({ rec, onClose, onSaved }) {
  const [name, setName] = useState(rec?.name || "");
  const [recognitionTitle, setRecognitionTitle] = useState(rec?.recognition_title || "");
  const [category, setCategory] = useState(rec?.category || "");
  const [description, setDescription] = useState(rec?.description || "");
  const [awardedDate, setAwardedDate] = useState(
    rec?.awarded_date ? String(rec.awarded_date).slice(0, 10) : new Date().toISOString().slice(0, 10)
  );
  const [country, setCountry] = useState(rec?.country || "SK");
  const [city, setCity] = useState(rec?.city || "");
  const [picture, setPicture] = useState(rec?.picture || "");
  const [awardImage, setAwardImage] = useState(rec?.award_image || "");
  const [website, setWebsite] = useState(rec?.website || "");
  const [social, setSocial] = useState(rec?.social || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const handleFile = (setter, mbMax) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > mbMax * 1024 * 1024) {
      alert(`Súbor je príliš veľký (max ${mbMax} MB)`);
      return;
    }
    const r = new FileReader();
    r.onload = (ev) => setter(ev.target.result);
    r.readAsDataURL(f);
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {
        name: name.trim(),
        recognition_title: recognitionTitle.trim(),
        category: category.trim() || null,
        description: description.trim() || null,
        awarded_date: awardedDate ? new Date(awardedDate + "T12:00:00Z").toISOString() : null,
        country: country.trim() || null,
        city: city.trim() || null,
        picture: picture || null,
        award_image: awardImage || null,
        website: website.trim() || null,
        social: social.trim() || null,
      };
      if (rec?.id) {
        await api.patch(`/admin/certified-recognitions/${rec.id}`, payload);
      } else {
        await api.post("/admin/certified-recognitions", payload);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="admin-recognition-modal">
      <div className="bg-[var(--wb-surface)] border border-gold-700/40 max-w-2xl w-full p-6 relative my-auto max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white">
          <X className="w-4 h-4" />
        </button>
        <h3 className="font-display font-bold text-xl mb-1 inline-flex items-center gap-2">
          <Trophy className="w-5 h-5 text-gold-400" />
          {rec?.id ? "Upraviť uznanie" : "Nové uznanie"}
        </h3>
        <p className="text-xs font-mono text-zinc-500 mb-5">Vyznamenanie SauberEko® — objaví sa vo verejnej Sieni slávy</p>

        {err && <div className="mb-4 p-3 bg-red-950/40 border border-red-700 text-red-300 text-xs font-mono">{err}</div>}

        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Meno príjemcu / firma *</label>
              <input value={name} onChange={(e) => setName(e.target.value.slice(0, 120))}
                     className="wb-input w-full px-3 py-2 text-sm" placeholder="Ján Novák" data-testid="admin-recognition-name" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Názov vyznamenania *</label>
              <input value={recognitionTitle} onChange={(e) => setRecognitionTitle(e.target.value.slice(0, 160))}
                     className="wb-input w-full px-3 py-2 text-sm" placeholder="Zvárač roka 2026" data-testid="admin-recognition-title" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Kategória</label>
              <input value={category} onChange={(e) => setCategory(e.target.value.slice(0, 80))}
                     className="wb-input w-full px-3 py-2 text-sm" placeholder="Osobnosť roka / Firma / Životné dielo" data-testid="admin-recognition-category" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Dátum udelenia</label>
              <input type="date" value={awardedDate} onChange={(e) => setAwardedDate(e.target.value)}
                     className="wb-input w-full px-3 py-2 text-sm" data-testid="admin-recognition-date" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Krajina</label>
              <input value={country} onChange={(e) => setCountry(e.target.value.slice(0, 60))}
                     className="wb-input w-full px-3 py-2 text-sm" placeholder="SK" data-testid="admin-recognition-country" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Mesto</label>
              <input value={city} onChange={(e) => setCity(e.target.value.slice(0, 80))}
                     className="wb-input w-full px-3 py-2 text-sm" placeholder="Bratislava" data-testid="admin-recognition-city" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Popis / dôvod</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value.slice(0, 500))}
                      rows={3} className="wb-input w-full px-3 py-2 text-sm"
                      placeholder="Za výnimočný prínos v obore…" data-testid="admin-recognition-desc" />
            <div className="text-[10px] text-zinc-600 text-right mt-1">{description.length}/500</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Fotka príjemcu</label>
              {picture ? (
                <div className="space-y-2">
                  <img src={picture} alt="Príjemca" className="w-full h-24 object-cover border border-zinc-800" />
                  <div className="flex gap-2">
                    <label className="cursor-pointer text-[10px] font-mono uppercase px-2 py-1 border border-zinc-700 hover:border-zinc-500">
                      Vymeniť
                      <input type="file" accept="image/*" onChange={handleFile(setPicture, 5)} className="hidden" />
                    </label>
                    <button onClick={() => setPicture("")} className="text-[10px] font-mono uppercase px-2 py-1 border border-red-700/50 text-red-400">Zmazať</button>
                  </div>
                </div>
              ) : (
                <label className="cursor-pointer block border-2 border-dashed border-zinc-700 hover:border-gold-600 px-3 py-4 text-center text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-gold-400">
                  Nahrať fotku (max 5 MB)
                  <input type="file" accept="image/*" onChange={handleFile(setPicture, 5)} className="hidden" />
                </label>
              )}
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Foto vyznamenania</label>
              {awardImage ? (
                <div className="space-y-2">
                  <img src={awardImage} alt="Vyznamenanie" className="w-full h-24 object-contain border border-zinc-800 bg-black" />
                  <div className="flex gap-2">
                    <label className="cursor-pointer text-[10px] font-mono uppercase px-2 py-1 border border-zinc-700 hover:border-zinc-500">
                      Vymeniť
                      <input type="file" accept="image/*" onChange={handleFile(setAwardImage, 5)} className="hidden" />
                    </label>
                    <button onClick={() => setAwardImage("")} className="text-[10px] font-mono uppercase px-2 py-1 border border-red-700/50 text-red-400">Zmazať</button>
                  </div>
                </div>
              ) : (
                <label className="cursor-pointer block border-2 border-dashed border-zinc-700 hover:border-gold-600 px-3 py-4 text-center text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-gold-400">
                  Nahrať plaketu / diplom (max 5 MB)
                  <input type="file" accept="image/*" onChange={handleFile(setAwardImage, 5)} className="hidden" />
                </label>
              )}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Web</label>
              <input value={website} onChange={(e) => setWebsite(e.target.value.slice(0, 200))}
                     className="wb-input w-full px-3 py-2 text-sm" placeholder="https://…" data-testid="admin-recognition-web" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Social</label>
              <input value={social} onChange={(e) => setSocial(e.target.value.slice(0, 200))}
                     className="wb-input w-full px-3 py-2 text-sm" placeholder="https://linkedin.com/…" data-testid="admin-recognition-social" />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-zinc-800">
          <button
            onClick={onClose}
            className="text-xs font-mono uppercase tracking-wider px-4 py-2 border border-zinc-700 text-zinc-400 hover:border-zinc-500"
          >
            Zrušiť
          </button>
          <button
            onClick={save}
            disabled={busy || !name.trim() || !recognitionTitle.trim()}
            className="wb-btn-primary text-xs uppercase tracking-wider px-4 py-2 disabled:opacity-50"
            data-testid="admin-recognition-save"
          >
            {busy ? "Ukladám…" : (rec?.id ? "Uložiť" : "Vytvoriť")}
          </button>
        </div>
      </div>
    </div>
  );
}




function RecruitersTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/certified-recruiters");
      setItems(r.data?.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Naozaj zmazať „${p.name}" zo siene slávy?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/certified-recruiters/${p.id}`);
      load();
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-hof-rec-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold text-2xl text-white">Nábor & HR — Sieň slávy</h2>
          <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-1">
            Certifikovaní personálni manažéri a náborári SauberEko®
          </p>
        </div>
        <button
          onClick={() => setEditing("new")}
          className="wb-btn-primary inline-flex items-center gap-2 px-4 py-2 text-xs"
          data-testid="admin-hof-rec-add"
        >
          <Plus className="w-3 h-3" /> Pridať personalistu
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Načítavam…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border border-zinc-800 bg-zinc-950">
          <UserSearch className="w-12 h-12 mx-auto text-zinc-700 mb-3" />
          <div className="text-zinc-400 text-sm">Zatiaľ nie sú pridaní žiadni personalisti</div>
          <p className="text-xs text-zinc-600 mt-2 max-w-md mx-auto">Tu pridávaš certifikovaných personálnych manažérov a náborárov SauberEko®. Objavia sa v sekcii „Nábor & HR" v Sieni slávy.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} className="border border-zinc-800 bg-zinc-950 p-4" data-testid={`admin-hof-rec-${p.id}`}>
              <div className="flex items-start gap-3 mb-3">
                {p.picture ? (
                  <img src={p.picture} alt={p.name} className="w-12 h-12 object-cover border border-zinc-800" />
                ) : (
                  <div className="w-12 h-12 bg-gold-950/30 border border-gold-700/40 flex items-center justify-center">
                    <span className="font-display font-bold text-gold-400 text-lg">{p.name?.[0] || "—"}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm text-white truncate">{p.name}</div>
                  <div className="text-[10px] font-mono text-zinc-500">{p.certificate_no || "—"}</div>
                </div>
              </div>
              {(p.city || p.country) && (
                <div className="text-xs text-zinc-400 mb-2">{[p.city, p.country].filter(Boolean).join(", ")}</div>
              )}
              {p.specialization && (
                <div className="text-[11px] text-zinc-500 mb-3 truncate">{p.specialization}</div>
              )}
              <div className="flex gap-2">
                <button onClick={() => setEditing(p)} className="flex-1 text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-gold-500" data-testid={`admin-hof-rec-edit-${p.id}`}>
                  Upraviť
                </button>
                <button onClick={() => remove(p)} disabled={busy} className="text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 border border-red-700/50 text-red-400 hover:bg-red-950/30" data-testid={`admin-hof-rec-delete-${p.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <RecruiterEditModal
          rec={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function RecruiterEditModal({ rec, onClose, onSaved }) {
  const [name, setName] = useState(rec?.name || "");
  const [certNo, setCertNo] = useState(rec?.certificate_no || "");
  const [country, setCountry] = useState(rec?.country || "Slovensko");
  const [city, setCity] = useState(rec?.city || "");
  const [specialization, setSpecialization] = useState(rec?.specialization || "");
  const [company, setCompany] = useState(rec?.company || "");
  const [description, setDescription] = useState(rec?.description || "");
  const [linkedin, setLinkedin] = useState(rec?.linkedin || "");
  const [website, setWebsite] = useState(rec?.website || "");
  const [emailContact, setEmailContact] = useState(rec?.email || "");
  const [picture, setPicture] = useState(rec?.picture || "");
  const [certPhoto, setCertPhoto] = useState(rec?.certificate_photo || "");
  const [certifiedSince, setCertifiedSince] = useState(
    rec?.certified_since ? String(rec.certified_since).split("T")[0] : ""
  );
  const [skills, setSkills] = useState(Array.isArray(rec?.skills) ? rec.skills : []);
  const [skillsOther, setSkillsOther] = useState(rec?.skills_other || "");
  const [busy, setBusy] = useState(false);

  const handleFile = (setter, maxMB = 4.5) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxMB * 1_000_000) { alert(`Príliš veľký súbor (max ${maxMB} MB).`); return; }
    const r = new FileReader();
    r.onload = () => setter(r.result);
    r.readAsDataURL(file);
  };

  const save = async () => {
    if (!name.trim() || name.trim().length < 2) { alert("Meno je povinné."); return; }
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        certificate_no: certNo.trim(),
        country: country.trim(),
        city: city.trim(),
        specialization: specialization.trim(),
        company: company.trim(),
        description: description.trim(),
        linkedin: linkedin.trim(),
        website: website.trim(),
        email: emailContact.trim(),
        picture: picture || null,
        certificate_photo: certPhoto || null,
        certified_since: certifiedSince ? new Date(certifiedSince + "T12:00:00Z").toISOString() : null,
        skills, skills_other: skillsOther || null,
      };
      if (rec?.id) {
        await api.patch(`/admin/certified-recruiters/${rec.id}`, payload);
      } else {
        await api.post("/admin/certified-recruiters", payload);
      }
      onSaved();
    } catch (err) {
      alert("Chyba: " + (err?.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="admin-hof-rec-edit-modal">
      <div className="bg-[var(--wb-surface)] border border-gold-700/40 max-w-2xl w-full p-6 relative my-auto max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        <h3 className="font-display font-bold text-xl mb-1 inline-flex items-center gap-2">
          <UserSearch className="w-5 h-5 text-gold-400" />
          {rec ? "Upraviť personalistu" : "Pridať certifikovaného personalistu"}
        </h3>
        <p className="text-xs font-mono text-zinc-500 mb-5">Sieň slávy SauberEko® — Nábor & HR</p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Meno a priezvisko *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" placeholder="napr. Jana Náborová" data-testid="admin-hof-rec-name" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Číslo certifikátu</label>
              <input value={certNo} onChange={(e) => setCertNo(e.target.value)} className="wb-input w-full px-3 py-2 text-sm font-mono" placeholder="Sa.Eko.HR.2026-0001" data-testid="admin-hof-rec-cert-no" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Špecializácia</label>
              <input value={specialization} onChange={(e) => setSpecialization(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" placeholder="napr. Senior HR — strojárstvo, zváračstvo" data-testid="admin-hof-rec-spec" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Mesto</label>
              <input value={city} onChange={(e) => setCity(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" data-testid="admin-hof-rec-city" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Krajina</label>
              <input value={country} onChange={(e) => setCountry(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" placeholder="Slovensko" data-testid="admin-hof-rec-country" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Firma / agentúra (voliteľné)</label>
            <input value={company} onChange={(e) => setCompany(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" placeholder="napr. ABC Recruiting s. r. o." data-testid="admin-hof-rec-company" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">LinkedIn (URL)</label>
              <input value={linkedin} onChange={(e) => setLinkedin(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" placeholder="https://linkedin.com/in/…" data-testid="admin-hof-rec-linkedin" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Web (voliteľné)</label>
              <input value={website} onChange={(e) => setWebsite(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" placeholder="https://…" data-testid="admin-hof-rec-website" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Kontakt e-mail (voliteľné)</label>
            <input type="email" value={emailContact} onChange={(e) => setEmailContact(e.target.value)} className="wb-input w-full px-3 py-2 text-sm" placeholder="napr. nabor@firma.sk" data-testid="admin-hof-rec-email" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">
              Dátum udelenia certifikátu <span className="text-gold-500">(certifikovaný od)</span>
            </label>
            <input
              type="date"
              value={certifiedSince}
              onChange={(e) => setCertifiedSince(e.target.value)}
              className="wb-input w-full px-3 py-2 text-sm font-mono"
              data-testid="admin-hof-rec-certified-since"
            />
            <p className="text-[10px] text-zinc-600 mt-1">
              Ak vynecháš, použije sa dnešný. Zadaj skutočný dátum keď bol certifikát udelený.
            </p>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">
              🎯 Zručnosti <span className="text-gold-500">(admin priraďuje)</span>
            </label>
            <SkillsPicker
              selected={skills}
              onChange={setSkills}
              other={skillsOther}
              onOtherChange={setSkillsOther}
              lang="sk"
            />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Popis (krátky)</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value.slice(0, 500))} rows={2} className="wb-input w-full px-3 py-2 text-sm" placeholder="Krátky popis činnosti / odbornosti…" data-testid="admin-hof-rec-desc" />
            <div className="text-[10px] text-zinc-600 text-right mt-1">{description.length}/500</div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Fotka profilu</label>
              {picture ? (
                <div className="space-y-2">
                  <img src={picture} alt="Profil" className="w-full h-24 object-cover border border-zinc-800" />
                  <div className="flex gap-2">
                    <label className="cursor-pointer text-[10px] font-mono uppercase px-2 py-1 border border-zinc-700 hover:border-zinc-500">
                      Vymeniť
                      <input type="file" accept="image/*" onChange={handleFile(setPicture, 2)} className="hidden" />
                    </label>
                    <button onClick={() => setPicture("")} className="text-[10px] font-mono uppercase px-2 py-1 border border-red-700/50 text-red-400">Zmazať</button>
                  </div>
                </div>
              ) : (
                <label className="cursor-pointer inline-block">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase px-3 py-2 border border-gold-700/40 text-gold-400 hover:bg-gold-900/20">
                    Nahrať fotku
                  </span>
                  <input type="file" accept="image/*" onChange={handleFile(setPicture, 2)} className="hidden" data-testid="admin-hof-rec-picture-upload" />
                </label>
              )}
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Foto certifikátu</label>
              {certPhoto ? (
                <div className="space-y-2">
                  <img src={certPhoto} alt="Certifikát" className="w-full h-24 object-cover border border-zinc-800" />
                  <div className="flex gap-2">
                    <label className="cursor-pointer text-[10px] font-mono uppercase px-2 py-1 border border-zinc-700 hover:border-zinc-500">
                      Vymeniť
                      <input type="file" accept="image/*" onChange={handleFile(setCertPhoto, 4)} className="hidden" />
                    </label>
                    <button onClick={() => setCertPhoto("")} className="text-[10px] font-mono uppercase px-2 py-1 border border-red-700/50 text-red-400">Zmazať</button>
                  </div>
                </div>
              ) : (
                <label className="cursor-pointer inline-block">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase px-3 py-2 border border-gold-700/40 text-gold-400 hover:bg-gold-900/20">
                    Nahrať foto
                  </span>
                  <input type="file" accept="image/*" onChange={handleFile(setCertPhoto, 4)} className="hidden" data-testid="admin-hof-rec-cert-photo-upload" />
                </label>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button onClick={onClose} className="flex-1 text-xs font-mono uppercase tracking-wider px-4 py-2 border border-zinc-700 text-zinc-300">Zrušiť</button>
          <button onClick={save} disabled={busy} className="flex-1 wb-btn-primary text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="admin-hof-rec-save">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            {rec ? "Uložiť zmeny" : "Pridať personalistu"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ─────────── DiscountVoucherTab ───────────
 * White-gold SauberEko® discount voucher composer.
 * Admin fills recipient + voucher details, previews live, then sends as email.
 */
function DiscountVoucherTab() {
  const [form, setForm] = useState({
    to: "",
    name: "",
    voucher_no: "",
    certificate_no: "",
    service_label: "",
    amount: "",
    currency: "EUR",
    valid_until_str: "",
    personal_note: "",
    lang: "sk",
  });
  const [previewHtml, setPreviewHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [err, setErr] = useState("");

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reqPreview = async () => {
    setErr(""); setBusy(true);
    try {
      const r = await api.post("/admin/voucher/preview", form);
      setPreviewHtml(r.data?.html || "");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Nepodarilo sa pripraviť náhľad");
    } finally { setBusy(false); }
  };

  // Auto-preview when key fields change (debounced)
  useEffect(() => {
    if (!form.name && !form.amount) { setPreviewHtml(""); return; }
    const t = setTimeout(() => { reqPreview(); }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name, form.voucher_no, form.certificate_no, form.service_label,
      form.amount, form.currency, form.valid_until_str, form.personal_note, form.lang]);

  const send = async () => {
    setErr(""); setSent(null);
    // Klientská validácia — pomôže ušetriť round-trip.
    const emailRe = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!form.to || !emailRe.test(form.to.trim())) {
      setErr("Zadaj platný email príjemcu vo formáte meno@doména.sk");
      return;
    }
    if (!form.name || !form.name.trim()) { setErr("Zadaj meno príjemcu"); return; }
    if (!window.confirm(`Naozaj odoslať zľavový kupón na ${form.to}?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/voucher/send", form);
      const taskId = r.data?.task_id;
      setSent({
        to: r.data?.sent_to,
        subject: r.data?.subject,
        queued: r.data?.queued === true,
        message: r.data?.message,
        task_id: taskId,
        status: "processing",
      });
      // ─── Poll delivery status every 3s (max 40 tries = ~2min) ───
      if (taskId) {
        let tries = 0;
        const poll = async () => {
          tries += 1;
          try {
            const s = await api.get(`/admin/email-status/${taskId}`);
            const st = s.data?.status;
            if (st === "sent" || st === "failed") {
              setSent((prev) => prev ? {
                ...prev,
                status: st,
                error: s.data?.error || null,
                sent_at: s.data?.sent_at,
              } : prev);
              return; // stop polling
            }
          } catch (_) { /* ignore transient poll errors */ }
          if (tries < 40) setTimeout(poll, 3000);
          else setSent((prev) => prev ? { ...prev, status: "timeout" } : prev);
        };
        setTimeout(poll, 3000);
      }
    } catch (ex) {
      // Zobrazíme user-friendly hlášku + pod ňou technický detail pre debug.
      const status = ex?.response?.status;
      const detail = ex?.response?.data?.detail;
      let friendly = "Nepodarilo sa odoslať kupón.";
      if (status === 400) friendly = `Neplatné údaje: ${detail || "skontroluj email a meno."}`;
      else if (status === 401 || status === 403) friendly = "Nemáš oprávnenie — prihlás sa znova ako admin.";
      else if (status === 413) friendly = "Voucher je príliš veľký (obrázok?). Skús skrátiť personálnu poznámku.";
      else if (status === 422) friendly = "Chýba povinné pole (email, meno, alebo suma).";
      else if (status === 429) friendly = "Príliš veľa požiadaviek — počkaj minútu.";
      else if (status >= 500) friendly = `Server vrátil chybu ${status}. Skús to znova o pár sekúnd; ak to opakovane padá, napíš mi presnú hlášku.`;
      else if (!status) friendly = "Nepodarilo sa spojiť so serverom (skontroluj internet / VPN).";
      const debug = detail && detail !== friendly ? ` [Detail: ${typeof detail === "string" ? detail : JSON.stringify(detail)}]` : "";
      setErr(friendly + debug);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="discount-voucher-tab">
      <div>
        <h2 className="font-display text-2xl text-gold-400 flex items-center gap-2">
          <Ticket className="w-6 h-6" /> Zľavový kupón SauberEko®
        </h2>
        <p className="text-xs text-zinc-500 mt-1 font-mono">
          Krásny zlato-biely kupón s tvojou pečiatkou a podpisom — odošle sa emailom príjemcovi.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* ─── Form ─── */}
        <div className="border border-zinc-800 bg-[var(--wb-surface)] p-5 space-y-3">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Email príjemcu *</label>
            <input type="email" data-testid="voucher-to"
              value={form.to} onChange={upd("to")}
              placeholder="prijemca@example.com"
              className="wb-input w-full px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Meno príjemcu (zobrazí sa veľkým písmom) *</label>
            <input type="text" data-testid="voucher-name"
              value={form.name} onChange={upd("name")}
              placeholder="Jozef Martinek"
              className="wb-input w-full px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Číslo kupónu</label>
              <input type="text" data-testid="voucher-no"
                value={form.voucher_no} onChange={upd("voucher_no")}
                placeholder="VCH-2026-0001"
                className="wb-input w-full px-3 py-2 text-sm font-mono" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Číslo certifikátu</label>
              <input type="text" data-testid="voucher-cert"
                value={form.certificate_no} onChange={upd("certificate_no")}
                placeholder="Sa.Eko.Prs.2026-0002"
                className="wb-input w-full px-3 py-2 text-sm font-mono" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Služba</label>
            <input type="text" data-testid="voucher-service"
              value={form.service_label} onChange={upd("service_label")}
              placeholder="Re-certifikácia 2027 / Audit prevádzky / …"
              className="wb-input w-full px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Hodnota</label>
              <input type="text" data-testid="voucher-amount"
                value={form.amount} onChange={upd("amount")}
                placeholder="150 alebo 15"
                className="wb-input w-full px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Mena</label>
              <select data-testid="voucher-currency"
                value={form.currency} onChange={upd("currency")}
                className="wb-input w-full px-3 py-2 text-sm">
                <option value="EUR">EUR</option>
                <option value="%">% (percenta)</option>
                <option value="USD">USD</option>
                <option value="CZK">CZK</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Platnosť do</label>
            <input type="text" data-testid="voucher-valid"
              value={form.valid_until_str} onChange={upd("valid_until_str")}
              placeholder="31.12.2026"
              className="wb-input w-full px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Osobný odkaz (voliteľný)</label>
            <textarea data-testid="voucher-note"
              value={form.personal_note} onChange={upd("personal_note")}
              rows={3}
              placeholder="Ďakujem za Vašu profesionalitu — venujem Vám zľavu…"
              className="wb-input w-full px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Jazyk emailu</label>
            <select data-testid="voucher-lang"
              value={form.lang} onChange={upd("lang")}
              className="wb-input w-full px-3 py-2 text-sm">
              <option value="sk">Slovenčina</option>
              <option value="en">English</option>
              <option value="cs">Čeština</option>
              <option value="de">Deutsch</option>
              <option value="pl">Polski</option>
            </select>
          </div>

          {err && <div className="p-2 border border-red-900 bg-red-950/30 text-red-400 text-xs flex items-center gap-2"><AlertCircle className="w-4 h-4" />{err}</div>}
          {sent && sent.status === "sent" && (
            <div data-testid="voucher-status-sent" className="p-3 border-2 border-emerald-500 bg-emerald-950/40 text-emerald-300 text-sm flex items-center gap-2 font-medium">
              <CheckCircle2 className="w-5 h-5" />
              ✅ Kupón úspešne odoslaný na <span className="font-mono">{sent.to}</span>
            </div>
          )}
          {sent && sent.status === "failed" && (
            <div data-testid="voucher-status-failed" className="p-3 border-2 border-red-500 bg-red-950/40 text-red-300 text-sm flex flex-col gap-1">
              <div className="flex items-center gap-2 font-medium">
                <AlertCircle className="w-5 h-5" />
                ❌ Odoslanie zlyhalo — {sent.to}
              </div>
              {sent.error && <div className="text-xs font-mono text-red-400 pl-7">{sent.error}</div>}
            </div>
          )}
          {sent && sent.status === "timeout" && (
            <div className="p-2 border border-amber-700 bg-amber-950/30 text-amber-300 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              ⏱️ Odosielanie trvá dlhšie ako 2 min. Skontroluj kartu "Manuálne emaily" alebo skús znova.
            </div>
          )}
          {sent && (sent.status === "processing" || !sent.status) && (
            <div data-testid="voucher-status-processing" className="p-2 border border-emerald-900 bg-emerald-950/30 text-emerald-400 text-xs flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {sent.queued
                ? (sent.message || `Kupón sa pripravuje a bude odoslaný na ${sent.to} v priebehu 30-90 sekúnd.`)
                : `Odosielam na ${sent.to}…`}
            </div>
          )}

          <button data-testid="voucher-send"
            onClick={send} disabled={busy || !form.to || !form.name}
            className="wb-btn-primary w-full py-3 text-sm inline-flex items-center justify-center gap-2 mt-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Odoslať zľavový kupón
          </button>
        </div>

        {/* ─── Live preview ─── */}
        <div className="border border-zinc-800 bg-[var(--wb-surface)] overflow-hidden">
          <div className="px-3 py-2 bg-zinc-900 text-xs font-mono uppercase text-zinc-400 flex items-center justify-between">
            <span><Eye className="w-3.5 h-3.5 inline mr-1" /> Náhľad</span>
            {busy && <Loader2 className="w-3 h-3 animate-spin" />}
          </div>
          {previewHtml ? (
            <iframe
              title="voucher-preview"
              srcDoc={previewHtml}
              className="w-full bg-[#EFE9DC]"
              style={{ height: 900, border: 0 }}
              data-testid="voucher-preview"
            />
          ) : (
            <div className="p-10 text-center text-xs text-zinc-500">
              Vyplň polia naľavo — náhľad sa zobrazí automaticky.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
